// The base-building controller. One instance per team: team 0 (blue) = PLAYER-built (HUD + click-to-
// place); team 1 (red) = AI-built. Two deterministic item flows share the belts:
//   ORE  line: Drill → Conveyors → Upgraders → Receptacle → orbital MATERIALS (buys buildings).
//   PART line: Fabricator (materials → part) → Assembler STATIONS (each bumps the part's tier) →
//              Deployer (part → a UNIT whose kind scales with tier). Longer line = better units.
// P2 POWER+CLIMATE: on hot/cold worlds, production buildings choke unless inside a POWERED Cooler.

import * as THREE from "three";
import { BLOOM_LAYER, FACTORY_BAND, type Scene } from "../world/scene";
import type { Input } from "../engine/input";
import type { NavGraph } from "../systems/pathfind";
import { FACTORY, PHYS, BUILDINGS, PALETTE, FOOTPRINT, FOOTPRINT_OUTBOARD, DIR_VEC, DIR_YAW, needsClimate, isConduit, isUpgrader, isUpgraderLike, unlockAt, WAVERIDER_TABLE, WAVERIDER_TABLE_TOTAL, USE_JOLT_ORE, type Dir, type BuildingKind } from "./defs";
import { JoltWorld } from "./jolt";
import { FactoryHud, BULLDOZE, type Sel } from "./hud";
import { ClusterFlares } from "../campaign/lensflare";
import { InventoryPreview } from "./invpreview";
import { ORECANNON, CONVEYOR, TRIPLEXER, ENRICHER, RECEPTACLE, OBLITERATOR, TELEPORTER_A, TELEPORTER_B, DROPPER_SINGLE, DROPPER_DOUBLE, RAMP, SPLITTER, WALL, BLASTER, FREEZER, PRESSURIZER, MAGNETRON, INFERNACE, LEECH, ECOBOX, SCANNER, WAVERIDER, DIVERSIFIER, MUTATOR, LASER, PACKER, VAT, type FactoryModel } from "./models";
import { ScanLabels } from "./scanlabels";
import { ModelInstancer, type ExtraPart, type NeonSpec } from "./instances";

export interface FactoryOpts {
  team: 0 | 1;
  mode: "player" | "ai";
  deploy: (team: 0 | 1, kind: "little" | "giant") => void;
}

interface Placed {
  kind: BuildingKind;
  gx: number;
  gz: number;
  floor: number;
  dir: Dir;
  mesh?: THREE.Object3D;                                         // Group path (procedural placeholders + blaster); undefined when instanced
  inst?: { key: string; slot: number } | null;                  // instanced path (conveyor/drill/wall/ramp/splitter/cryo) — pool key + slot
  mat?: THREE.Matrix4;                                           // cached tile transform (T·Ry) for the instanced path — drop points + wheel-spin base
  rotTop?: number;                                               // cryo: accumulated top-wheel angle (rad)
  rotBot?: number;                                               // cryo: accumulated bottom-wheel angle (rad)
  cooldown: number;
  climateOK: boolean;
  chokeMark: THREE.Mesh | null;
  toggle?: boolean;                                             // splitter: alternates its two outputs
  splitIx?: number;                                             // splitter (physics ore): which of its 3 outputs fired last (cycles)
  flash?: number;                                               // blaster/cryo: 0..1 pop, set to 1 on blast/cool pulse, decays (drives the neon pop)
  through?: number;                                             // RECEPTACLE: ore this one has swallowed since it was placed. Its own lifetime count (not value — that is the Scanner), shown on the screen part. Lives on the Placed, so it dies with the building.
  dropIx?: number;                                              // drill/dual-drill: which spout dropped last (dual alternates)
  spinTop?: number;                                             // cryo: top-flywheel angular speed (rad/s) — fast idle, drained by throughput
  spinBot?: number;                                             // cryo: blue-neon bottom-wheel angular speed (rad/s) — spins up while cooling
  busy?: boolean;                                               // cryo: any ore over the tile this frame (bottom wheel + neon on/off)
  load?: number;                                                // cryo: smoothed ore-on-tile count (throughput proxy → how hard the flywheel is drained)
  phase?: "load" | "cook" | "release";                          // pressurizer: oven cycle stage
  phaseT?: number;                                              // pressurizer: time left in the current stage
  heldCount?: number;                                           // pressurizer: ore captured in the current batch (0..pressurizerSlots)
  doorEnt?: number;                                             // pressurizer: entrance door open-fraction (0 shut … 1 fully retracted), animated
  doorExit?: number;                                            // pressurizer: exit door open-fraction
  bobT?: number;                                                // magnetron: disk bob-phase accumulator (rad)
  rotDisk?: number;                                             // magnetron: accumulated disk spin angle (rad) — the instanced path has no Object3D to hold it
  flowT?: number;                                               // leech: neon flow-phase accumulator (rad)
  neonA?: number;                                               // leech: neon active fade 0..1 (ore-on-belt-and-powered) · triplexer: same, for its four violet strips
  ringSpin?: number;                                            // scanner: continuous ring spin angle (rad)
  ringSnap?: number;                                            // scanner: current eased snap offset (rad)
  ringSnapTgt?: number;                                         // scanner: snap target (+45° per ore pass; ring eases onto it)
  // CANNON. `yaw`/`pitch` are where the gun ACTUALLY points and are simulation state — they decide where a shot
  // goes, so they slew inside the fixed step, never in an animate* pass. `tgt*` is the commanded aim point; the
  // gun chases it and fires along its real heading meanwhile, which is why re-aiming under load sprays ore.
  turYaw?: number;                                              // cannon: barrel's true WORLD bearing (rad)
  turPitch?: number;                                            // cannon: barrel's true elevation (rad, + = up)
  tgtX?: number;                                                // cannon: aim point, world X
  tgtZ?: number;                                                // cannon: aim point, world Z
  tgtY?: number;                                                // cannon: aim point, world Y (deck height of the landing tile)
  tgtManual?: boolean;                                          // cannon: sliders own the aim; tgt* is then DERIVED for display, not authoritative
  manYaw?: number;                                              // cannon: slider-commanded bearing (rad) — its own field, never overloaded onto tgtX
  manPitch?: number;                                            // cannon: slider-commanded elevation (rad)
  manV?: number;                                                // cannon: slider-commanded launch speed
  muzzleV?: number;                                             // cannon: the speed THIS shot leaves at (chosen per target)
  fired?: number;                                               // cannon: muzzle-flash fade 0..1
  pool?: number;                                                // vat: pooled worth of ore eaten since the last Amalgam
  eaten?: number;                                               // vat: how many eaten since the last Amalgam
  charge?: number;                                              // laser: 1 while the emitter holds an absorbed burn, spent on the next ore through
  waveT?: number;                                               // waverider: neon wave scroll phase (rad)
  waveA?: number;                                               // waverider: neon active fade 0..1 (ore currently on the belt)
}

// one struct for both flows: part=false → ORE, part=true → unit-part (tier).
// PARTS still ride the tile rails (gx,gz,dir,t). ORE is now a PHYSICS body (px..pz position, vx..vz velocity);
// gx,gz track the tile it's currently over (for belt lookup + clog bucketing).
interface Item { active: boolean; part: boolean; gx: number; gz: number; floor: number; dir: Dir; t: number; value: number; elem: number; elemMask: number; tier: number; applied: boolean; px: number; py: number; pz: number; qx: number; qy: number; qz: number; qw: number; /* physics ORIENTATION, read back from Jolt each frame — ore are full 6-DOF when PHYS.tumble is on */ vx: number; vy: number; vz: number; capKey: number; /* splitter: tile-key of the splitter currently holding this ore, else -1 */ bodyH?: number; /* Jolt body handle when USE_JOLT_ORE (movement owned by JoltWorld); undefined in the legacy integrator */ flung?: boolean; /* Jolt: airborne from a fling (DOF unlocked to tumble) — re-locked to no-tumble on landing */ bombT?: number; /* Bomb Packer: seconds ARMED. Value climbs with it, and so does the chance to blow. */ caff?: number; /* Triplexer (MMF Caffeinator): upgrader applications still to be buffed. Set to FACTORY.triplexerCharges on a pass through a Triplexer; one is spent each time an upgrader's effect is amplified. MUST be reset on spawn — this pool recycles slots. */ uid?: number; /* unique per SPAWN. Both this pool and Jolt's body free-list recycle slots, so an Item reference (or a bodyH) identifies a SLOT, not an ore — anything that follows one ore over time must compare this. */ }
const popcount = (n: number): number => { let c = 0; while (n) { c += n & 1; n >>= 1; } return c; };
// element accent colors (fire/ice/electric/plant/magnetized), for tinting ore by what it carries
const ELEM_COL = [new THREE.Color(0xff6a2c), new THREE.Color(0x59d5ff), new THREE.Color(0xffe14a), new THREE.Color(0x6cff8a), new THREE.Color(0xc060e0)];
interface Launch { active: boolean; pos: THREE.Vector3; vel: THREE.Vector3; life: number; mesh: THREE.Mesh; }

// grid cells are keyed by (gx, gz, FLOOR) — floors stack independently at the same (x,z) column
const key = (gx: number, gz: number, floor: number): string => `${gx},${gz},${floor}`;
const RED_TINT = new THREE.Color(0xc0392b);
const ORE_COL = new THREE.Color(0xffb060);
const PART_COL = new THREE.Color(0x9b6bff);

// The HIGH-COUNT model buildings are INSTANCED (one InstancedMesh per part — see instances.ts), so a dense factory
// costs a handful of draw calls instead of hundreds of meshes. Everything else (procedural placeholders + the rare
// blaster) stays on the per-Group path (makeMesh). The cryo's blue coil (Union2) carries a per-instance neon factor.
const INST: Partial<Record<BuildingKind, { key: string; model: FactoryModel; neon?: Record<string, number>; arrow?: boolean }>> = {
  conveyor: { key: "conveyor", model: CONVEYOR, arrow: true },
  drill:    { key: "drill",    model: DROPPER_SINGLE },
  drill2:   { key: "drill2",   model: DROPPER_DOUBLE },
  wall:     { key: "wall",     model: WALL },
  ramp:     { key: "ramp",     model: RAMP },
  splitter: { key: "splitter", model: SPLITTER },
  cryo:     { key: "cryo",     model: FREEZER, neon: { Union2: 0x59d5ff } },
};

// Model-backed MACHINES that are also safe to instance. Measured: an instancer-backed kind adds ZERO scene
// objects, while a machine on the Group path adds 3-5 meshes, each submitted in all four render passes — and
// the build plate holds 6x6x10 = 360 cells, so this is the one cost in the game that grows with how much the
// player builds. Their per-building visual state is emissive COLOR (a vec3 attribute) + animated part matrices,
// both of which the instancer carries per instance (setEmissive/setEmissiveColor/setMatrix, as the cryo does).
// DELIBERATELY EXCLUDED: `vat` and `diversifier` have transparent parts (d 0.5 in their .mtl) which would lose
// per-building depth sorting once they collapse into one InstancedMesh anchored at the world origin; `scanner`
// animates a ring about a pivot derived from a bounding box the shared model does not carry.
/** The magnetron is TWO models on the Group path: the bundled CONVEYOR deck ore ride across, plus a child Group
 *  ("magDisk") holding the hovering disk — which is the entire magnetron.obj. A pool takes ONE model, so this
 *  merges them, renaming the base's parts out of the way of the disk's own Union1/Union2. Geometry is shared by
 *  reference (Pool clones per pool), and the disk parts stay at their re-origined local height: the hover/spin is
 *  a per-instance MATRIX written each frame by animateMagnetrons, exactly as the cryo's wheels are. */
const MAGNETRON_RIG: FactoryModel = {
  parts: [...CONVEYOR.parts.map((p) => ({ ...p, name: `base${p.name}` })), ...MAGNETRON.parts],
  collider: CONVEYOR.collider,
  dropPoints: [],
};
/** The cryo's ice blue, as LINEAR components — what a per-instance emissive vec3 has to carry to equal the Group
 *  path's `emissive.setHex(0x59d5ff)` (three converts sRGB→linear on the way into a material). */
const CRYO_BLUE = new THREE.Color(0x59d5ff);

/** Cannon parts that SWIVEL with the turret (yaw only) — the GEAR. */
const CANNON_TURRET = ["Union4"];
/** The part that ELEVATES: the barrel, whose group is named for its own pivot offset (`0,-20,0` → `02001`). */
const CANNON_BARREL: string | null = "02001";

const MACHINE_INST: Partial<Record<BuildingKind, {
  model: FactoryModel; deck?: string | string[]; support?: string | string[];
  neon?: string[]; rest?: Record<string, number>; arrow?: boolean;
  /** Parts whose emissive HUE varies per building: the material bakes WHITE emissive + keeps the authored surface
   *  color, and the per-instance vec3 carries the color. Bloom comes from a masked twin so an instance at
   *  emissive 0 bleeds nothing (the Group path toggled the bloom LAYER per mesh, which instancing can't). */
  hueParts?: string[];
  /** Don't switch the selective-bloom pass on just because this kind was placed — nothing glows until something
   *  makes it (a cryo capping a magnetron), and that path turns the pass on itself. */
  lazyBloom?: boolean;
}>> = {
  // MMF Sparkler: bundled conveyor deck + the hovering disk (baseUnion1/baseUnion2 = the deck, Union1/Union2 = the
  // disk's ring + brown-copper inner). The inner is normally UNLIT and goes ice-blue under a cryo — the one part in
  // the game whose neon hue is per building, which is why the pool's emissive attribute is a vec3.
  magnetron: { model: MAGNETRON_RIG, neon: ["Union2"], hueParts: ["Union2"], rest: { Union2: 0 }, lazyBloom: true, arrow: true },
  ecobox:    { model: ECOBOX,    deck: "Union1", neon: ["Union3"], rest: { Union3: 0.25 }, arrow: true },
  packer:    { model: PACKER,    deck: "Union4", neon: ["Union1"], rest: { Union1: 0.2 },  arrow: true },
  mutator:   { model: MUTATOR,   deck: "Union4", neon: ["Union3"], rest: { Union3: 0.14 }, arrow: true },
  laser:     { model: LASER,     deck: "Union3", neon: ["Union2"], rest: { Union2: 0.12 }, arrow: true },
  infernace: { model: INFERNACE, deck: "Union3" },
  enricher:  { model: ENRICHER,  deck: "Union1", neon: ["Union3"], rest: { Union3: 0.10 }, arrow: true },
  // The spike ring is driven entirely at runtime — hue AND brightness — so it is a hueParts neon.
  obliterator: { model: OBLITERATOR, neon: ["Union2"], hueParts: ["Union2"], rest: { Union2: 0 } },
  // ORE CANNON. Bundled conveyor deck (Union1 gray support / Union2 dark belt — identified by SHAPE, they match
  // the real Conveyor's two parts exactly) under a turret whose Union5 gear and Union4 barrel each get their own
  // per-instance matrix every frame. No arrow: the deck's facing says nothing about where the gun is aimed.
  cannon:      { model: ORECANNON, deck: "Union2", support: "Union1" },
  // L-shaped, six deck segments, four neon strips that fade with ore presence — all per-INSTANCE emissive,
  // which the pool carries natively (animateTriplexers already writes through setPartGlow).
  triplexer: { model: TRIPLEXER, deck: ["Union6", "Union7", "Union8", "Union9", "Union10", "Union11"],
               neon: ["Union1", "Union2", "Union3", "Union4"],
               rest: { Union1: 0.05, Union2: 0.05, Union3: 0.05, Union4: 0.05 } },
  // Union1 is the belt (100x20x100), Union3 the support (95x15x102) authored CYAN — shape, not color, says which.
  teleportera: { model: TELEPORTER_A, deck: "Union1", support: "Union3" },
  teleporterb: { model: TELEPORTER_B, deck: "Union1", support: "Union3" },
};

/** How many slots a kind's instance pool has to hold. The plate is `footprint² × maxFloors` = 360 tiles and a
 *  building takes one each, so 360 is the true ceiling — except WALLS, which hug tile EDGES and stack four to a
 *  tile. The pool cap used to be a flat guess of 320, and going one over does NOT degrade gracefully: the
 *  InstancedMesh draws past its own instanceMatrix, and (measured, 321 conveyors) every frame from then on is a
 *  GL INVALID_OPERATION. Cheap to size correctly — a slot is 16 floats per part, so 360 conveyor decks is 23 KB. */
const poolCap = (kind: BuildingKind): number =>
  FACTORY.footprint * FACTORY.footprint * FACTORY.maxFloors * (kind === "wall" ? 4 : 1);

/** A placement config for the instanced path, resolved per Factory (team tint + deck repaint are per-team). */
interface InstConf {
  key: string; model: FactoryModel; neon?: Record<string, NeonSpec>; arrow?: boolean; rest?: Record<string, number>;
  lazyBloom?: boolean;
}

export class Factory {
  private root = new THREE.Group();
  /** Everything this factory draws — buildings, instancer pools, ore. Used to skip it in the water reflection. */
  get renderRoot(): THREE.Object3D { return this.root; }
  private instancer!: ModelInstancer;        // instanced pools for the high-count model buildings (built in ctor)
  private instConfs = new Map<BuildingKind, InstConf>();
  /** ORE-ONLY pause, per factory. Deliberately NOT the DEV `simPause` in main.ts, which freezes the whole
   *  world: the player asked for a way to build without ore flying around, and the enemy factory, the battle
   *  and the units must all keep running. This flag gates `simulate()` for THIS factory alone. */
  orePaused = false;
  private oreStep = 0;          // frames of ore sim to run while paused (the STEP button)
  private oblFlash = 0;         // 0..1 ability flash on the spike ring, decays back to the idle red
  private oblHue = new THREE.Color(0xff1919);
  private oblCol = new THREE.Color(0xff1919);  // the hue currently on the ring — fed to the star-flare pass
  private oblSpin = 0;
  private tpCursor = new Map<string, number>();   // per-ENTRANCE round-robin cursor over the exits
  private tpExits: Placed[] | null = null;
  private recList: Placed[] | null = null;      // receptacles, for the out-through-the-tube sink test        // cached teleporter-B list, invalidated on any build change // per-Factory tinted pool configs (see machineConf)
  private arrowExtra: ExtraPart[] | null = null; // shared conveyor direction arrow, as an instanced extra part (lazy)
  private scanLabels!: ScanLabels;              // scanner: floating value readouts, spawned as ore is scanned
  private scanBuf = new Float32Array(FACTORY.scannerWindow); // rolling window of recent scanned worths (→ median)
  private scanBufN = 0;                         // # of scanBuf slots filled (0…scannerWindow)
  private scanBufPos = 0;                        // next ring-buffer write index
  private scanMax = 0;                           // highest worth scanned this battle
  private scanCount = 0;                         // total ore scanned (for the readout)
  private world: Scene;         // the Scene wrapper (renderer owner) — used to flag selective bloom on
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private input: Input;
  private hud: FactoryHud | null = null;
  readonly team: 0 | 1;   // exposed so the caller can hand each factory its own pad rect
  private mode: "player" | "ai";
  private deploy: (team: 0 | 1, kind: "little" | "giant") => void;

  private buildings = new Map<string, Placed>();
  private rampCells = new Map<string, Placed>(); // every cell a ramp overhangs (incl. its anchor) → the ramp building
  private reserved = new Map<string, Placed>();  // extra footprint cells (the scanner's housing juts one tile to the side) → the owning building
  private walls = new Map<string, Placed[]>();   // wall-only tiles → up to 4 edge walls (one per dir); block ore + units
  private mods = new Map<string, Placed>();      // overhead modifiers (cryo Cooler) that mount ON TOP of a `buildings` tile — one per tile
  private wallBoxes: { x0: number; z0: number; x1: number; z1: number }[] = []; // floor-0 wall strips in WORLD coords (for the battle nav)
  private wallBounds = { x0: 0, z0: 0, x1: 0, z1: 0, has: false };               // union AABB → O(1) reject in blocksAt
  private items: Item[] = [];
  // P6 INVENTORY: how many of each building the player OWNS and has not placed. Buildings are no longer bought
  // with materials — materials buy PACKS, and a pack is uniformly random draws (see FACTORY.packDraws). Bulldozing
  // returns the item, so a count limits what is placed AT ONCE. AI factories ignore all of this and build freely.
  private inv = new Map<BuildingKind, number>();
  private packsBought = 0;
  private invPreview: InventoryPreview | null = null;
  private heroPreview: InventoryPreview | null = null;   // the detail pane's larger, single-model view
  private oreUid = 0;                       // monotonic spawn counter — see Item.uid
  private zone: THREE.Group | null = null;            // buildable-zone overlay (build mode only)
  private zoneCells: THREE.LineSegments | null = null;
  private zoneEdge: THREE.LineSegments | null = null;
  private oreAttr!: THREE.InstancedBufferAttribute;   // per-ore (glow, seed) — value written in light, not size
  private sparkAttr!: THREE.InstancedBufferAttribute; // per-ore sparkle color = the elements it carries
  private oreTime = { value: 0 };                     // shader clock for the glints
  private oreFlares = new ClusterFlares();            // the campaign's star flares, reused for the richest ore
  private _ndc = new THREE.Vector3();
  private instItem: (Item | null)[] = [];   // last frame's InstancedMesh slot → Item, so a click can name the ore it hit
  private itemMesh: THREE.InstancedMesh;
  private launches: Launch[] = [];

  private originX = 0;
  private originZ = 0;
  private cols = 0;
  private rows = 0;
  private baseY = 0;
  private ready = false;

  // Phase 1 Jolt pivot: a lazy per-factory rigid-body world for ore movement (behind USE_JOLT_ORE). Boots on
  // the first flagged frame; null until the WASM resolves so ore falls back to the legacy integrator meanwhile.
  jolt: JoltWorld | null = null;
  private joltBooting = false;
  private joltGen = 0;          // bumped on every rebuild so a stale in-flight create() can't overwrite the new world

  materials = FACTORY.startMaterials;
  launched = 0;
  // planet flavor: a freshly-mined ore's starting stats depend on the world (set per level in onLevel)
  private oreStartValue = 1;
  private oreStartMask = 0;

  private climate = 0;
  private climateKind: "none" | "hot" | "cold" = "none";
  private climateDirty = false;
  private powerSupply = 0;
  private powerDemand = 0;
  private discs: THREE.Mesh[] = [];
  private wires: THREE.Line[] = [];

  private buildMode = false;
  private sel: Sel = "conveyor";
  private ghostDir: Dir = 0;
  private ghost: THREE.Group;
  private ghostKind: BuildingKind | null = null;
  private hover: { gx: number; gz: number } | null = null;
  private activeFloor = 0;        // the floor you're currently building on (player, build mode)

  private starter = { cx: 0, cz: 0 };
  private aiQueue: { kind: BuildingKind; gx: number; gz: number; dir: Dir }[] = [];
  private aiStep = 0;
  private aiEscalate = 0;

  // "parts wasted" feedback: a throttled HUD warning when a fabricator keeps draining materials but the
  // parts never reach a deployer (unfinished belt end, wrong-type routing, a bulldozed belt). Counts parts
  // actually DESTROYED (not merely emitted), so parts in flight on a long WORKING belt never false-trigger.
  private partsWasted = 0;   // parts lost with no deploy in between (any deploy resets it)
  private sinceWaste = 99;   // seconds since the last waste event (large = none recently)
  private wasteWarnCd = 0;   // re-warn throttle

  private _ray = new THREE.Raycaster();
  private _plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private _hit = new THREE.Vector3();
  private _dummy = new THREE.Object3D();
  private _c = new THREE.Vector3();
  private _v = new THREE.Vector3();
  private _q = new THREE.Quaternion();
  private _yAxis = new THREE.Vector3(0, 1, 0);
  private _one = new THREE.Vector3(1, 1, 1);
  private _animMat = new THREE.Matrix4();
  private _cmA = new THREE.Matrix4();   // cannon: scratch for composing the barrel's pivot/elevation
  private _cmB = new THREE.Matrix4();   // cannon: the barrel's world matrix (render AND muzzle spawn read it)
  private _cv = new THREE.Vector3();    // cannon: scratch point (muzzle tip, pivot)
  private aimed: Placed | null = null;  // cannon whose aiming menu is open (null = none)
  private _partMat = new THREE.Matrix4();   // instanced per-part offset: buildingMatrix · localOffset
  private _partOff = new THREE.Matrix4();
  private _oreHash = new Map<string, Item[]>();
  private _col = new THREE.Color();
  private _sparkC = new THREE.Color();  // scratch for the per-ore sparkle color (element mix)

  constructor(scene: Scene, input: Input, container: HTMLElement, opts: FactoryOpts) {
    this.world = scene;
    this.scene = scene.scene;
    this.camera = scene.camera;
    this.input = input;
    this.team = opts.team;
    this.mode = opts.mode;
    this.deploy = opts.deploy;
    this.scene.add(this.root);
    this.instancer = new ModelInstancer(this.root, FACTORY_BAND, BLOOM_LAYER);
    this.scanLabels = new ScanLabels(this.scene);

    if (this.mode === "player") {
      this.hud = new FactoryHud(container);
      this.hud.onSelect = (s) => { this.sel = s; this.hud!.setSelected(s); this.rebuildGhost(); };
      this.hud.onBuild = () => { if (!this.buildMode) this.setBuildMode(true); }; // click the "[B] BUILD" prompt
      this.hud.onPack = () => this.buyPack();   // P6: materials buy PACKS, packs grant the buildings
      // DEV: stock everything, so a new building can be tested without grinding the whole tycoon to unlock it.
      // Flag read at point of use, like the `?jolt=` toggle in update().
      this.hud.onStockAll = () => this.stockAll();
      this.hud.setDev(new URLSearchParams(location.search).get("dev") === "1");
      // OBLITERATOR abilities. Every one acts on THIS Factory instance only — the AI factory owns a separate
      // items pool, a separate orePaused flag and no HUD, so none of this can reach the enemy team.
      this.hud.onObliterate = () => { if (this.mode === "player") this.obliterate(); };
      this.hud.onAimCannon = (tx, tz, h) => this.aimCannonAtTile(tx, tz, h);
      this.hud.onAimCannonManual = (y, p, pw) => this.aimCannonManual(y, p, pw);
      this.hud.onCloseCannon = () => this.closeCannonMenu();
      this.hud.onTogglePause = () => { if (this.mode === "player") this.setOrePaused(!this.orePaused); };
      this.hud.onStepOre = () => { if (this.mode === "player") this.stepOre(1); };
      // QUICK PLACE — `[code][x][z][rot]` from the search box. Floor comes from the active floor ([ and ]),
      // so the command only carries what those controls do not already own.
      this.hud.onProbe = (sel, gx, gz, dir) => this.probePlacement(sel, gx, gz, dir);
      this.hud.onPlaceAt = (sel, gx, gz, dir) => {
        if (this.mode !== "player") return;
        if (sel === BULLDOZE) { this.bulldoze(gx, gz, this.activeFloor); return; }
        this.place(sel as BuildingKind, gx, gz, this.activeFloor, (dir % 4) as Dir);
      };
      // Spinning previews: built from makeMesh, so the thing turning in a slot IS the building you will place.
      // Detail pane: the SELECTED building, rendered large in its own viewport. Same machinery as the grid —
      // one canvas, one scissored viewport — just with a single slot in it.
      this.hud.onDetail = (kind) => {
        if (!this.hud) return;
        if (!this.heroPreview) this.heroPreview = new InventoryPreview(this.hud.detailWell);
        this.heroPreview.setSlots(kind ? [{ key: kind, el: this.hud.detailWell }] : [],
          (k) => { try { return this.makeMesh(k as BuildingKind, 0, false); } catch { return null; } });
      };
      this.hud.onOwnedChanged = (slots) => {
        if (!this.invPreview) this.invPreview = new InventoryPreview(this.hud!.barEl);
        this.invPreview.setSlots(slots, (kind) => {
          try { return this.makeMesh(kind as BuildingKind, 0, false); } catch { return null; }
        });
      };
      this.hud.onExport = () => {                                     // copy a shareable code of the whole layout
        const code = this.exportCode();
        console.log("[factory export]\n" + code);                    // always retrievable from the console as a fallback
        const parts = this.buildings.size + this.mods.size + [...this.walls.values()].reduce((n, a) => n + a.length, 0);
        navigator.clipboard?.writeText(code).then(
          () => this.hud?.flash(`⎘ factory copied · ${parts} parts`),
          () => this.hud?.flash("⎘ copy blocked — code in console"),
        );
      };
      this.hud.onImport = () => {                                     // paste a code → rebuild the layout
        const code = window.prompt("Paste a factory code (LF1…):");
        if (code == null || !code.trim()) return;
        const r = this.importCode(code);
        this.hud?.flash(r.ok ? `⎀ imported · ${r.placed} placed${r.failed ? `, ${r.failed} skipped` : ""}` : `⎀ ${r.error}`);
      };
    }

    const itemGeo = new THREE.BoxGeometry(20, 20, 20);
    const itemMat = new THREE.MeshStandardMaterial({ emissive: 0xffffff, emissiveIntensity: 0.5, roughness: 0.5, vertexColors: false });
    // ORE VALUE IS WRITTEN IN LIGHT, not size. An ore climbs the color wheel as it gains worth — dim red at the
    // bottom, once round the wheel, back to a blazing red at the top — and starts to glow, then sparkle. Value
    // owns the body; ELEMENTS own the sparkle color, so a fire+magnetized ore throws orange and purple glints
    // off a value-colored body and both readouts survive.
    // Cost is two floats per ore and a handful of shader lines: `aOre` = (glow, seed), the bloom pass is already
    // additive so a dim ore bleeds nothing and a blazing one bleeds hard for free, and the sparkle is analytic
    // (no particles, no extra draws) for up to oreCap ore.
    this.oreAttr = new THREE.InstancedBufferAttribute(new Float32Array(FACTORY.oreCap * 2), 2);   // (glow, seed)
    this.sparkAttr = new THREE.InstancedBufferAttribute(new Float32Array(FACTORY.oreCap * 3), 3); // element color
    this.oreAttr.setUsage(THREE.DynamicDrawUsage);
    this.sparkAttr.setUsage(THREE.DynamicDrawUsage);
    itemGeo.setAttribute("aOre", this.oreAttr);
    itemGeo.setAttribute("aSpark", this.sparkAttr);
    itemMat.onBeforeCompile = (sh) => {
      sh.uniforms.uOreTime = this.oreTime;
      sh.vertexShader = sh.vertexShader
        .replace("#include <common>", "#include <common>\nattribute vec2 aOre;\nattribute vec3 aSpark;\nvarying vec2 vOre;\nvarying vec3 vSpark;\nvarying vec3 vOreN;")
        // `transformedNormal`, NOT normalMatrix*objectNormal: only the former has the INSTANCE matrix folded in
        // (three.js does that in <defaultnormal_vertex>). Without it every ore shares one set of face normals, so
        // the facet term is identical across the field and never changes as an ore tumbles — no twinkle at all.
        .replace("#include <begin_vertex>", "#include <begin_vertex>\n vOre = aOre;\n vSpark = aSpark;\n vOreN = transformedNormal;");
      sh.fragmentShader = sh.fragmentShader
        .replace("#include <common>", "#include <common>\nvarying vec2 vOre;\nvarying vec3 vSpark;\nvarying vec3 vOreN;\nuniform float uOreTime;")
        .replace("#include <emissivemap_fragment>", `#include <emissivemap_fragment>
        {
          float glow = vOre.x, seed = vOre.y;
          // GLINTS: facets catch the light and wink, keyed to the ore's own seed so no two twinkle together, and
          // colored by the ELEMENTS it carries — the one readout the value hue had to take over.
          // A glint is a TIGHT lobe swept across the faces, not a slow fade: a highlight direction orbits each
          // ore on its own phase and flashes hard the moment it lines up with a face. A wide lobe against a
          // FIXED direction averaged out to a ~15% constant lift instead — measurable (peak luminance moved by
          // 5 of 188) but invisible, which is exactly why it read as "not rendering".
          vec3 n = normalize(vOreN);
          float a = uOreTime * 3.6 + seed * 6.2831853;
          vec3 dir = normalize(vec3(cos(a), 0.5, sin(a)));
          float g1 = pow(max(0.0, dot(n, dir)), 12.0);
          float a2 = uOreTime * -5.2 + seed * 12.566;              // a second, counter-rotating glint
          float g2 = pow(max(0.0, dot(n, normalize(vec3(cos(a2), -0.35, sin(a2))))), 12.0);
          float spark = (g1 + g2 * 0.7) * smoothstep(0.02, 0.30, glow); // faintest ore stay dull
          totalEmissiveRadiance = vColor * glow * 1.5 + vSpark * spark * (3.0 + 14.0 * glow);
        }`);
    };
    this.itemMesh = new THREE.InstancedMesh(itemGeo, itemMat, FACTORY.oreCap);
    this.itemMesh.layers.enable(BLOOM_LAYER); // per-INSTANCE glow for free: the bloom add is proportional to brightness
    this.world.overlayPasses.push((r) => this.oreFlares.render(r)); // ore flares, additive over the composited frame
    this.itemMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.itemMesh.frustumCulled = false;
    this.itemMesh.count = FACTORY.oreCap;
    this.scene.add(this.itemMesh);
    for (let i = 0; i < FACTORY.oreCap; i++) {
      this.items.push({ active: false, part: false, gx: 0, gz: 0, floor: 0, dir: 0, t: 0, value: 0, elem: 1, elemMask: 0, tier: 0, applied: false, px: 0, py: 0, pz: 0, qx: 0, qy: 0, qz: 0, qw: 1, vx: 0, vy: 0, vz: 0, capKey: -1 });
      this.itemMesh.setColorAt(i, ORE_COL); // allocates instanceColor
    }

    const lGeo = new THREE.SphereGeometry(16, 8, 8);
    for (let i = 0; i < 24; i++) {
      const m = new THREE.Mesh(lGeo, new THREE.MeshBasicMaterial({ color: 0xffce8a, transparent: true, opacity: 0 }));
      m.visible = false;
      this.scene.add(m);
      this.launches.push({ active: false, pos: new THREE.Vector3(), vel: new THREE.Vector3(), life: 0, mesh: m });
    }

    const ringGeo = new THREE.RingGeometry(FACTORY.coolerRadius * 0.94, FACTORY.coolerRadius, 48);
    const ringMat = new THREE.MeshBasicMaterial({ color: 0x5ad0ff, transparent: true, opacity: 0.32, side: THREE.DoubleSide });
    for (let i = 0; i < 8; i++) {
      const d = new THREE.Mesh(ringGeo, ringMat);
      d.rotation.x = -Math.PI / 2; d.visible = false;
      this.scene.add(d); this.discs.push(d);
    }
    for (let i = 0; i < 8; i++) {
      const g = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]);
      const w = new THREE.Line(g, new THREE.LineBasicMaterial({ color: 0xffe14a, transparent: true, opacity: 0.7 }));
      w.visible = false; w.frustumCulled = false;
      this.scene.add(w); this.wires.push(w);
    }

    this.ghost = new THREE.Group();
    this.ghost.visible = false;
    this.scene.add(this.ghost);
    this.hud?.setSelected(this.sel);
    this.rebuildGhost();
  }

  // ---- level setup ------------------------------------------------------------------------------
  /** The instanced ORE/PART mesh — exposed so the Scene can register it as its own GPU cost channel. */
  get oreMesh(): THREE.InstancedMesh { return this.itemMesh; }

  onLevel(nav: NavGraph, climate: number, climateKind: "none" | "hot" | "cold",
          padRect?: { x0: number; x1: number; z0: number; z1: number }): void {
    this.clear();
    this.climate = climate;
    this.climateKind = climateKind;
    // planet-flavored ore: hot worlds mine fire-touched ore, cold worlds ice-touched (a free head-start
    // element + a little extra base) — so each world's factory plays a bit differently from tile one.
    this.oreStartMask = climateKind === "hot" ? 1 : climateKind === "cold" ? 2 : 0;
    this.oreStartValue = climateKind === "none" ? 1 : 1.2;
    this.hud?.setClimateMode(climate > 0);
    const pad = this.team === 0 ? nav.startKeys : nav.coreKeys;
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity, y = 0, n = 0;
    for (const k of pad) {
      const p = nav.point.get(k);
      if (!p) continue;
      minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
      minZ = Math.min(minZ, p.z); maxZ = Math.max(maxZ, p.z);
      y += p.y; n++;
    }
    if (!n || !isFinite(minX)) { this.ready = false; return; }
    const half = 100;
    // The plaza is large, but the buildable grid is CLAMPED to a tight `footprint` (P3: forces building
    // UP). Center the small grid inside the plaza so the pad sits in the middle, not a corner.
    const fullCols = Math.max(6, Math.round((maxX - minX + 2 * half) / FACTORY.tile));
    const fullRows = Math.max(6, Math.round((maxZ - minZ + 2 * half) / FACTORY.tile));
    this.cols = Math.min(FACTORY.footprint, fullCols);
    this.rows = Math.min(FACTORY.footprint, fullRows);
    // CENTER THE GRID ON THE PAD RECT when the terrain gave us one. Deriving it from nav-cell bounds put the
    // grid up to half a CELL (100) off the cement the terrain had actually stamped, in each axis — so the
    // paved border looked lopsided and, once the pad was trimmed to the grid size, the far column of tiles
    // ended up on sloped ground. Concentric by construction is the only way these two stay in step.
    if (padRect) {
      this.originX = (padRect.x0 + padRect.x1) / 2 - (this.cols * FACTORY.tile) / 2;
      this.originZ = (padRect.z0 + padRect.z1) / 2 - (this.rows * FACTORY.tile) / 2;
    } else {
      this.originX = minX - half + ((fullCols - this.cols) * FACTORY.tile) / 2;
      this.originZ = minZ - half + ((fullRows - this.rows) * FACTORY.tile) / 2;
    }
    this.baseY = y / n;
    this._plane.set(new THREE.Vector3(0, 1, 0), -this.baseY);
    this.ready = true;

    this.materials = FACTORY.startMaterials;
    this.launched = 0;
    this.inv.clear(); this.packsBought = 0;
    for (const [k, n] of Object.entries(FACTORY.startKit)) this.inv.set(k as BuildingKind, n);
    this.seedStarter();
    if (this.mode === "ai") this.aiPlan();
    this.climateDirty = true; this.tpExits = null; this.recList = null; this.hud?.setHasObliterator(this.hasObliterator());
    this.hud?.setMaterials(this.materials, this.launched);
    this.hud?.setInventory(this.inv, this.packPrice(), this.materials);
    this.hud?.refreshLocks(this.launched); // P5: gray out the buildings not yet unlocked for this fresh run
  }

  private seedStarter(): void {
    const cx = Math.floor(this.cols / 2), cz = Math.floor(this.rows / 2);
    this.starter = { cx, cz };
    this.place("drill", cx - 3, cz, 0, 0, true);
    this.place("receptacle", cx + 2, cz, 0, 0, true);
  }

  // ---- grid math --------------------------------------------------------------------------------
  private inZone(gx: number, gz: number): boolean { return gx >= 0 && gz >= 0 && gx < this.cols && gz < this.rows; }
  private floorY(floor: number): number { return this.baseY + floor * FACTORY.floorHeight; }
  private tileCenter(gx: number, gz: number, floor: number, out: THREE.Vector3): THREE.Vector3 {
    return out.set(this.originX + (gx + 0.5) * FACTORY.tile, this.floorY(floor), this.originZ + (gz + 0.5) * FACTORY.tile);
  }
  private worldX(gx: number): number { return this.originX + (gx + 0.5) * FACTORY.tile; }
  private worldZ(gz: number): number { return this.originZ + (gz + 0.5) * FACTORY.tile; }
  private tileAtWorld(x: number, z: number): { gx: number; gz: number } | null {
    const gx = Math.floor((x - this.originX) / FACTORY.tile);
    const gz = Math.floor((z - this.originZ) / FACTORY.tile);
    return this.inZone(gx, gz) ? { gx, gz } : null;
  }

  // ---- placement --------------------------------------------------------------------------------

  /** A 2-tile building's SECOND footprint cell. `scanner` juts one tile to the LEFT of its belt; `waverider` is
   *  2-tiles-LONG so its far cell is one tile FORWARD along the belt. Returns null for normal 1-tile buildings. */
  /** The cells a multi-tile building occupies BESIDES its anchor (gx,gz). Offsets are in TRAVEL space —
   *  `f` = forward along `dir`, `l` = left-perpendicular — so a shape is written once and rotates with the
   *  building for free. Empty for ordinary 1-tile kinds.
   *
   *  Returns a LIST because footprints are no longer all one cell: the scanner juts one tile to the side and
   *  the waverider one tile forward, but an L-shaped 3-tile building needs two. */
  private extraCells(kind: BuildingKind, gx: number, gz: number, floor: number, dir: Dir): { gx: number; gz: number; k: string }[] {
    const shape = FOOTPRINT[kind];
    if (!shape) return [];
    const [dx, dz] = DIR_VEC[dir];
    // forward = (dx,dz); left-perp of travel = (-dz,dx) — the basis the scanner's housing offset was written in
    return shape.map(([f, l]: readonly [number, number]) => {
      const ex = gx + f * dx - l * dz, ez = gz + f * dz + l * dx;
      return { gx: ex, gz: ez, k: key(ex, ez, floor) };
    });
  }

  private place(kind: BuildingKind, gx: number, gz: number, floor: number, dir: Dir, free = false): boolean {
    if (!this.inZone(gx, gz)) return false;
    // The FLOOR was never bounds-checked — only the horizontal grid was. It did not show while floors barely
    // stacked, but a share code (or a typo) could put a building on a floor that does not exist, where nothing
    // renders its deck and ore fall straight through.
    if (floor < 0 || floor >= FACTORY.maxFloors) return false;
    if (kind === "leech" && floor < 1) { this.hud?.flash("leech needs floor 2+ (an upgrader powers it from directly below)"); return false; }
    const tk = key(gx, gz, floor), wa = this.walls.get(tk);
    // WALL: hugs a tile edge, up to 4 per tile (one per edge). It may share a tile with ANY building EXCEPT a BELT
    // (conveyor / ramp — a wall would block the ore it carries); you just can't re-wall an edge that's already walled.
    if (kind === "wall") {
      if (this.buildings.get(tk)?.kind === "conveyor" || this.rampCells.has(tk) || this.reserved.has(tk)) { this.hud?.flash("can't wall a belt"); return false; }
      if (wa && (wa.length >= 4 || wa.some((w) => w.dir === dir))) return false;           // ≤4, and one wall per edge
    } else if (kind === "cryo") {
      // CRYO is an OVERHEAD modifier: it mounts on TOP of an existing ore-line building (conveyor / any upgrader /
      // blaster), sharing the tile — it never takes an empty tile. One cooler per tile.
      const under = this.buildings.get(tk);
      if (!under || !(under.kind === "conveyor" || under.kind === "magnetron" || isUpgrader(under.kind))) { this.hud?.flash("cryo mounts on a conveyor, magnetron, upgrader or blaster"); return false; }
      if (this.mods.has(tk)) return false;
    } else if (this.buildings.has(tk) || this.rampCells.has(tk) || this.reserved.has(tk) || (wa && wa.length && kind === "conveyor")) {
      return false; // one building per tile (+ ramp overhang + a 2-tile building's reserved cell); a conveyor can't share a wall tile
    }
    // 2-tile buildings (scanner): its housing juts one tile to the side — that cell must be in-zone and free too.
    const extra = this.extraCells(kind, gx, gz, floor, dir);
    // An OUTBOARD kind (the receptacle) is BUILT to hang off the plate, so its extra cell skips the in-zone
    // test — but it must still be unoccupied, or two of them could overlap in mid-air off the edge.
    const outboard = FOOTPRINT_OUTBOARD.has(kind);
    if (extra.some((e) => (!outboard && !this.inZone(e.gx, e.gz)) || this.buildings.has(e.k) || this.reserved.has(e.k) || this.rampCells.has(e.k) || this.mods.has(e.k))) {
      this.hud?.flash(`${BUILDINGS[kind].name} needs ${extra.length + 1} free tiles`);
      return false;
    }
    const def = BUILDINGS[kind];
    const need = unlockAt(kind); // P5: player-gated behind a launch milestone (AI + starters build freely)
    if (!free && this.mode === "player" && this.launched < need) { this.hud?.flash(`locked · launch ${need} to orbit`); return false; }
    // INVENTORY, not currency: you must own one. (AI builds freely; starters are placed `free`.)
    if (!free && this.mode === "player") {
      if ((this.inv.get(kind) ?? 0) <= 0) { this.hud?.flash(`no ${def.name} left — open a pack`); return false; }
      this.inv.set(kind, (this.inv.get(kind) ?? 0) - 1);
      this.hud?.setInventory(this.inv, this.packPrice(), this.materials);
    }
    // (removed the red choke-warning sphere — the distinct models don't need it; the HUD still counts chokers,
    //  and climateOK still drives the choke penalty. chokeMark kept null so the update path stays a no-op.)
    const b: Placed = { kind, gx, gz, floor, dir, cooldown: kind === "fabricator" ? FACTORY.fabTime : kind === "blaster" ? FACTORY.blasterCharge : 0, climateOK: true, chokeMark: null };
    const conf: InstConf | undefined = INST[kind] ?? this.machineConf(kind);
    if (conf) {
      // INSTANCED path: the building is just a tile transform (T·Ry) + a slot in the shared per-part InstancedMeshes.
      const mat = new THREE.Matrix4().compose(this.tileCenter(gx, gz, floor, this._c), this._q.setFromAxisAngle(this._yAxis, DIR_YAW[dir]), this._one);
      b.mat = mat;
      b.inst = { key: conf.key, slot: -1 };
      b.inst.slot = this.instancer.add(conf.key, conf.model, mat, b, { neon: conf.neon, extra: conf.arrow ? this.arrowParts() : undefined, cap: poolCap(kind) });
      // Pool FULL (poolCap is sized to the whole plate, so this is belt-and-braces): drop this ONE building onto
      // the Group path instead of overflowing the pool, which would break the whole factory's rendering.
      if (b.inst.slot < 0) { b.inst = undefined; b.mat = undefined; }
      else {
        if (conf.neon && !conf.lazyBloom) this.world.bloomActive = true; // a neon instance exists → enable the selective-bloom pass
        // Pool.add seeds instEmissive = 1, but these parts IDLE far dimmer (makeMesh set emissiveIntensity to a
        // rest value of 0.12–0.25). Without this seed a freshly placed machine glows 4–8× too bright until its
        // animate pass happens to run — and the packer, which has no animate pass, would stay that way forever.
        if (conf.rest) for (const [part, v] of Object.entries(conf.rest)) this.instancer.setEmissive(conf.key, b.inst.slot, part, v);
        // The magnetron's disk is a per-instance MATRIX (hover + spin), and importCode places outside the animate
        // pass — seed it now or the disk sits inside its own deck for a frame.
        if (kind === "magnetron") this.setDiskMatrix(b, FACTORY.magnetronHoverY, 0);
        if (kind === "cannon") this.seedCannon(b);   // same reason: aim the turret before the first frame draws it
      }
    }
    if (!b.inst) {                                                  // no instanced config, or its pool was full
      const mesh = this.makeMesh(kind, dir);
      mesh.traverse((o) => { if ((o as THREE.Mesh).isMesh) o.renderOrder = FACTORY_BAND; }); // perf: banded so all building draws time as one "factory" channel
      mesh.position.copy(this.tileCenter(gx, gz, floor, this._c));
      if (kind === "receptacle") this.fitReceptacleTube(mesh);
      b.mesh = mesh;
      this.root.add(mesh);
    }
    if (kind === "wall") {
      if (wa) wa.push(b); else this.walls.set(tk, [b]);
      this.rebuildWallBoxes(); // refresh the battle-nav collision strips
    } else if (kind === "cryo") {
      this.mods.set(tk, b); // stacks on top of the host building; leaves the host's `buildings` entry untouched
    } else {
      this.buildings.set(tk, b);
      if (kind === "ramp") { const [dx, dz] = DIR_VEC[dir]; for (let s = 0; s <= 2; s++) this.rampCells.set(key(gx + s * dx, gz + s * dz, floor), b); } // reserve the 3 overhung cells
      for (const e of extra) this.reserved.set(e.k, b); // mark every non-anchor footprint cell occupied
      this.climateDirty = true; this.tpExits = null; this.recList = null; this.hud?.setHasObliterator(this.hasObliterator());
    }
    return true;
  }

  /** Is the `dir` edge of tile (gx,gz,floor) walled? True if a wall hugs that edge from EITHER adjacent tile. */
  private edgeWalled(gx: number, gz: number, floor: number, dir: Dir): boolean {
    const a = this.walls.get(key(gx, gz, floor));
    if (a && a.some((w) => w.dir === dir)) return true;
    const [dx, dz] = DIR_VEC[dir], b = this.walls.get(key(gx + dx, gz + dz, floor));
    return !!b && b.some((w) => w.dir === (((dir + 2) % 4) as Dir));
  }

  /** Recompute the world-space collision strips for FLOOR-0 walls (fed to the battle nav so units/tanks/the player
   *  can't cross or shoot through them). Only floor 0 blocks ground units; upper-floor walls are above their heads. */
  private rebuildWallBoxes(): void {
    this.wallBoxes.length = 0;
    const T = FACTORY.tile, H = 12; // strip half-depth across the boundary (wide enough to catch a step/LOS sample)
    let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
    for (const arr of this.walls.values()) for (const w of arr) {
      if (w.floor !== 0) continue;
      const [dx, dz] = DIR_VEC[w.dir], cx = this.worldX(w.gx), cz = this.worldZ(w.gz);
      const box = dx !== 0
        ? { x0: cx + dx * T * 0.5 - H, x1: cx + dx * T * 0.5 + H, z0: cz - T * 0.5, z1: cz + T * 0.5 }   // ±X edge → spans Z
        : { x0: cx - T * 0.5, x1: cx + T * 0.5, z0: cz + dz * T * 0.5 - H, z1: cz + dz * T * 0.5 + H };  // ±Z edge → spans X
      this.wallBoxes.push(box);
      x0 = Math.min(x0, box.x0); x1 = Math.max(x1, box.x1); z0 = Math.min(z0, box.z0); z1 = Math.max(z1, box.z1);
    }
    this.wallBounds = this.wallBoxes.length ? { x0, z0, x1, z1, has: true } : { x0: 0, z0: 0, x1: 0, z1: 0, has: false };
  }

  /** Battle-nav hook: is world (x,z) inside one of this factory's floor-0 wall strips? Units/tanks/the player treat
   *  it as non-walkable — blocking movement AND targeting line-of-sight. Cheap: union-bbox reject, then a short list. */
  blocksAt(x: number, z: number): boolean {
    const b = this.wallBounds;
    if (!b.has || x < b.x0 || x > b.x1 || z < b.z0 || z > b.z1) return false;
    for (const w of this.wallBoxes) if (x >= w.x0 && x <= w.x1 && z >= w.z0 && z <= w.z1) return true;
    return false;
  }

  /** Drop a placed building's render resource — a shared instance (swap-removed from its pool) or a Group mesh. */
  private removePlaced(b: Placed): void {
    if (b.inst) this.instancer.remove(b.inst.key, b.inst.slot);
    else if (b.mesh) { this.root.remove(b.mesh); disposeObj(b.mesh); }
  }

  private bulldoze(gx: number, gz: number, floor: number): void {
    const tk = key(gx, gz, floor);
    const owner = this.reserved.get(tk);
    if (owner) { this.bulldoze(owner.gx, owner.gz, owner.floor); return; } // clicked a scanner's housing tile → remove the scanner itself
    const wa = this.walls.get(tk);
    const mod = this.mods.get(tk);
    if (mod) { // an overhead cooler is the TOP layer — clear it first, leaving the host building beneath intact
      this.removePlaced(mod); this.mods.delete(tk);
      this.giveBack(mod.kind);
      return;
    }
    if (wa && wa.length) { // a walled tile is wall-only — one bulldoze clears the whole stack (walls are cheap)
      for (const w of wa) { this.removePlaced(w); this.giveBack("wall"); }
      this.walls.delete(tk);
      this.rebuildWallBoxes();
      return;
    }
    const b = this.buildings.get(key(gx, gz, floor));
    if (!b) return; // the receptacle used to be exempt here: it was pre-placed and unique, now it is player-built
    this.removePlaced(b);
    this.buildings.delete(key(gx, gz, floor));
    if (b.kind === "ramp") { const [dx, dz] = DIR_VEC[b.dir]; for (let s = 0; s <= 2; s++) this.rampCells.delete(key(b.gx + s * dx, b.gz + s * dz, b.floor)); }
    for (const e of this.extraCells(b.kind, b.gx, b.gz, b.floor, b.dir)) this.reserved.delete(e.k); // release every footprint cell
    this.giveBack(b.kind);
    this.climateDirty = true; this.tpExits = null; this.recList = null; this.hud?.setHasObliterator(this.hasObliterator());
  }

  // ---- share codes: serialize the WHOLE layout to a copy-pasteable string + rebuild from one ------
  // Format: "LF1|kind,gx,gz,floor,dir|kind,gx,gz,floor,dir|…". Kind is the literal BuildingKind string (stable +
  // human-readable). Every occupant is captured — buildings (incl. the pre-placed drill/receptacle + ramp anchors),
  // stacked edge walls, and overhead cryo mods — so a round-trip is a faithful full snapshot.

  /** Serialize every placed part to a shareable code (see format above). */
  exportCode(): string {
    const out: string[] = [];
    // Optional 6th field: per-building state. Only the cannon has any today — its aim, stored as an OFFSET from
    // its own tile center (`Tdx:dz:dy`) rather than as world coordinates, so a build stays aimed correctly when
    // it is pasted onto a different pad or loaded by the other factory. Older codes simply have no 6th field.
    const push = (b: Placed): void => {
      let extra = "";
      if (b.kind === "cannon" && b.tgtX != null && b.tgtY != null && b.tgtZ != null) {
        const c = this.tileCenter(b.gx, b.gz, b.floor, this._cv);
        extra = `,T${Math.round(b.tgtX - c.x)}:${Math.round(b.tgtZ - c.z)}:${Math.round(b.tgtY - c.y)}`;
      }
      out.push(`${b.kind},${b.gx},${b.gz},${b.floor},${b.dir}${extra}`);
    };
    for (const b of this.buildings.values()) push(b);              // one entry per tile (ramp = its anchor; cells re-derived on import)
    for (const arr of this.walls.values()) for (const w of arr) push(w);
    for (const m of this.mods.values()) push(m);
    return "LF1|" + out.join("|");
  }

  /** Rebuild the layout from a code (replaces the current one; economy untouched). Parses fully BEFORE clearing so a
   *  malformed code is a no-op. Places hosts before their overhead mods, and reports how many landed vs. were skipped
   *  (e.g. a coordinate outside this map's smaller grid). */
  importCode(code: string): { ok: boolean; placed: number; failed: number; error?: string } {
    const trimmed = code.trim();
    if (!trimmed.startsWith("LF1|")) return { ok: false, placed: 0, failed: 0, error: "not a factory code (needs LF1…)" };
    const rows: { kind: BuildingKind; gx: number; gz: number; floor: number; dir: Dir; aim?: [number, number, number] }[] = [];
    for (const e of trimmed.slice(4).split("|")) {
      if (!e) continue;
      const p = e.split(",");
      // 6th field is OPTIONAL per-building state (cannon aim). Anything unrecognized is skipped rather than
      // rejected, so a code written by a newer build still loads everything it can here.
      let aim: [number, number, number] | undefined;
      if (p.length === 6) {
        const t = p[5].startsWith("T") ? p[5].slice(1).split(":").map(Number) : [];
        if (t.length === 3 && t.every(Number.isFinite)) aim = [t[0], t[1], t[2]];
        p.length = 5;
      }
      if (p.length !== 5) return { ok: false, placed: 0, failed: 0, error: `bad entry "${e}"` };
      // "slingatron" was renamed to "receptacle" — keep reading old share codes rather than failing them.
      const kind = (p[0] === "slingatron" ? "receptacle" : p[0]) as BuildingKind;
      if (!(kind in BUILDINGS)) return { ok: false, placed: 0, failed: 0, error: `unknown part "${p[0]}"` };
      const nums = p.slice(1).map(Number);
      if (nums.some((n) => !Number.isFinite(n))) return { ok: false, placed: 0, failed: 0, error: `bad numbers in "${e}"` };
      rows.push({ kind, gx: nums[0], gz: nums[1], floor: nums[2], dir: (((nums[3] % 4) + 4) % 4) as Dir, aim });
    }
    this.clearLayout();
    // hosts (rank 0) before edge walls (1) before overhead cryo mods (2) — a mod needs its host present to place.
    const rank = (k: BuildingKind): number => (k === "cryo" ? 2 : k === "wall" ? 1 : 0);
    rows.sort((a, b) => rank(a.kind) - rank(b.kind));
    let placed = 0, failed = 0;
    for (const r of rows) {
      if (!this.place(r.kind, r.gx, r.gz, r.floor, r.dir, true)) { failed++; continue; }
      placed++;
      if (r.aim) this.aimCannon(this.buildings.get(key(r.gx, r.gz, r.floor)), r.aim[0], r.aim[1], r.aim[2]);
    }
    this.climateDirty = true; this.tpExits = null; this.recList = null; this.hud?.setHasObliterator(this.hasObliterator());
    return { ok: true, placed, failed };
  }

  /** Tear down every placed part (buildings/walls/mods/ramp cells) + any in-flight items, WITHOUT touching the
   *  economy or build-mode UI — the reset an import needs (vs. full `clear()` which also resets materials/AI/etc.). */
  private clearLayout(): void {
    for (const b of this.buildings.values()) if (b.mesh) { this.root.remove(b.mesh); disposeObj(b.mesh); } // Group-path only
    this.buildings.clear();
    this.walls.clear();  // walls + mods (cryo) are all instanced → their meshes are wiped by instancer.clear()
    this.mods.clear();
    this.instancer.clear();
    this.rampCells.clear();
    this.reserved.clear();
    this.rebuildWallBoxes();
    if (this.jolt) for (const o of this.items) if (o.bodyH != null) { this.jolt.despawnOre(o.bodyH); o.bodyH = undefined; } // release Jolt bodies too, or they orphan (leak the pool)
    for (const o of this.items) o.active = false; // in-flight ore/parts reference now-gone tiles → drop them
  }

  // ---- per-frame --------------------------------------------------------------------------------
  update(dt: number, commandMode: boolean): void {
    if (!this.ready) return;
    // Ore physics world: `?jolt=0/1` wins, else `window.__jolt`, else the build default. Boot is lazy +
    // fire-and-forget (ore ride the legacy integrator until the WASM resolves); flipping it off mid-session
    // tears the world down and hands movement straight back, so the two can be A/B'd live.
    const q = new URLSearchParams(location.search).get("jolt");
    const wantJolt = q != null ? q !== "0" : ((globalThis as { __jolt?: boolean }).__jolt ?? USE_JOLT_ORE);
    if (wantJolt && !this.jolt && !this.joltBooting) {
      this.joltBooting = true;
      const gen = this.joltGen;
      JoltWorld.create()
        .then((w) => { if (gen === this.joltGen) { this.jolt = w; w.setProbe(this.inspectOn); } else w.teardown(); }) // rebuilt while booting → discard
        .catch((e) => { this.joltBooting = false; console.error("[jolt] boot failed", e); });
    } else if (!wantJolt && this.jolt) {
      this.jolt.teardown(); this.jolt = null; this.joltBooting = false;
      for (const o of this.items) { o.bodyH = undefined; o.flung = false; } // stale handles → the legacy integrator takes over from the last positions
    }
    if (this.mode === "player") {
      if (commandMode && this.input.consumeKeyPress("KeyB")) this.setBuildMode(!this.buildMode);
      if (!commandMode && this.buildMode) this.setBuildMode(false);
      if (this.buildMode) this.handleBuildInput();
      if (this.invPreview) this.invPreview.render(this.buildMode ? dt : 0);
      if (this.heroPreview) this.heroPreview.render(this.buildMode ? dt : 0);
      this.updateTileTip(commandMode);
      // Self-heal the contact census. A console bench that borrows the probe and switches it off on the way out
      // used to leave the inspector reporting "touching nothing" for an ore plainly sitting on a belt — which
      // reads as the click having failed. The inspector owns the probe whenever it is on.
      if (this.inspectOn && this.jolt && !this.jolt.isProbing()) this.jolt.setProbe(true);
      if (this.latched && dt > 0) this.sampleInspect(); // dt 0 = sim paused; a stopped clock is not a stuck ore
      this.hud?.setIdlePrompt(commandMode && !this.buildMode); // "[B] BUILD" prompt when idle in the free camera
    } else {
      this.aiTick(dt);
    }
    if (this.climateDirty) this.recomputeClimate();
    // ORE PAUSE (this factory only — the enemy Factory has its own flag and is untouched).
    let sdt = dt;
    // STEP button: hand over exactly one fixed step's worth, so one press advances the sim by one step rather
    // than by a frame that may contain none.
    if (this.orePaused) { sdt = this.oreStep > 0 ? (this.jolt?.fixedStep ?? 1 / 60) : 0; if (this.oreStep > 0) this.oreStep--; }
    if (sdt > 0) {
      const w = this.jolt;
      if (w) {
        // THE FRAME/STEP SEAM. A frame is a variable slice of wall time; the step is constant. So the frame
        // does only what is safe to do at a variable rate — track the layout, reconcile bodies to ore — and
        // every scripted effect (drill cadence, cooldowns, the capKey economy, part movement) runs inside
        // onStep at a fixed 1/30. Sim behavior is then identical at 20fps and 144fps.
        // The order around the Step is the one this always had, just per step instead of per frame:
        // buildings tick → reconcile → Step → read back → item/economy pass. Reordering it (buildings after
        // the Step) was measured to change ore behavior outright — 0 → 13 of 28 ore lost — so it is load-bearing.
        this.syncBelts();          // static belt bodies track the layout (surface velocity drives the ore)
        this.reconcileOreBodies(w);
        w.step(sdt,
          (h) => { this.tickBuildings(h); this.reconcileOreBodies(w); },   // before the Step
          (h) => {                                                        // after it, on fresh positions
            for (const o of this.items) if (o.active && !o.part && o.bodyH != null) w.readInto(o.bodyH, o);
            this.tickItems(h);
          });
      } else this.simulate(sdt);   // legacy fused integrator (?jolt=0) — per frame, byte-for-byte as before
    } else this.syncBelts();       // still track the layout so building while paused registers its decks
    this.animateObliterators(dt);
    this.animateBlasters(dt);
    this.animateCryos(dt);
    this.animatePressurizers(dt);
    this.animateMagnetrons(dt);
    this.animateInfernaces(dt);
    this.animateLeeches(dt);
    this.animateEcoboxes(dt);
    this.animateDiversifiers(dt);
    this.animateBeams(dt);
    this.animateCannons(dt);
    this.animateScanners(dt);
    this.animateWaveriders(dt);
    this.animateTriplexers(dt);
    this.animateReceptacles();
    this.scanLabels.update(dt);
    this.updateScanHud(dt);
    this.oreTime.value = (this.oreTime.value + dt) % 600; // wrapped: an ever-growing clock loses sine precision
    this.renderItems();
    this.updateLaunches(dt);
    if (this.hud) {
      let ore = 0; for (const o of this.items) if (o.active && !o.part) ore++;
      this.hud.setMaterials(this.materials, this.launched); this.hud.setOreCount(ore); this.hud.update(dt); this.updateWasteWarning(dt);
    }
  }

  private setBuildMode(on: boolean): void {
    this.buildMode = on;
    this.input.buildMode = on;
    this.ghost.visible = false;
    this.hud?.setBuildMode(on);
    if (on) { this.hud?.setFloor(this.activeFloor, FACTORY.maxFloors); this.buildZone(); }
    if (this.zone) this.zone.visible = on;
  }

  /** The BUILDABLE ZONE, drawn on the active floor: a cell grid over exactly cols×rows plus a bright border.
   *  The world grid in the scene is a 40 000-stud debug helper with 100-stud cells drawn EVERYWHERE, so with a
   *  plate smaller than the pad there was nothing on screen saying where building stops — tiles kept going past
   *  the last buildable one and looked identical to it. This is the only thing that marks the edge. */
  private buildZone(): void {
    const T = FACTORY.tile, y = this.floorY(this.activeFloor) + 1.5;
    const x0 = this.originX, z0 = this.originZ, x1 = x0 + this.cols * T, z1 = z0 + this.rows * T;
    const pts: number[] = [];
    for (let i = 1; i < this.cols; i++) { const x = x0 + i * T; pts.push(x, y, z0, x, y, z1); } // interior cells
    for (let i = 1; i < this.rows; i++) { const z = z0 + i * T; pts.push(x0, y, z, x1, y, z); }
    const border: number[] = [x0, y, z0, x1, y, z0, x1, y, z0, x1, y, z1, x1, y, z1, x0, y, z1, x0, y, z1, x0, y, z0];
    if (!this.zone) {
      this.zone = new THREE.Group();
      const mk = (arr: number[], color: number, opacity: number): THREE.LineSegments => {
        const g = new THREE.BufferGeometry();
        g.setAttribute("position", new THREE.Float32BufferAttribute(arr, 3));
        const m = new THREE.LineBasicMaterial({ color, transparent: true, opacity, depthWrite: false });
        return new THREE.LineSegments(g, m);
      };
      this.zoneCells = mk(pts, 0x5ad0ff, 0.28);
      this.zoneEdge = mk(border, 0x3fd0e6, 0.95);
      this.zone.add(this.zoneCells, this.zoneEdge);
      this.zone.renderOrder = FACTORY_BAND;
      this.root.add(this.zone);
    } else {
      const set = (m: THREE.LineSegments, arr: number[]): void => {
        m.geometry.setAttribute("position", new THREE.Float32BufferAttribute(arr, 3));
        m.geometry.attributes.position.needsUpdate = true;
      };
      set(this.zoneCells!, pts); set(this.zoneEdge!, border);
    }
  }

  // (Floor DIMMING was removed by design — every floor always renders solid. A dense multi-floor factory being
  //  visually hard to read is an intended mechanic, so upper floors are no longer hidden while you build below.)

  /** Return a bulldozed building to the inventory (player only — the AI never had one). */
  private giveBack(kind: BuildingKind): void {
    if (this.mode !== "player") return;
    this.inv.set(kind, (this.inv.get(kind) ?? 0) + 1);
    this.hud?.setInventory(this.inv, this.packPrice(), this.materials);
  }

  /** What the NEXT pack costs — it climbs with every pack already opened, so buying is a real decision. */
  packPrice(): number { return Math.round(FACTORY.packCost * Math.pow(FACTORY.packCostGrow, this.packsBought)); }

  /** Open a pack: FACTORY.packDraws units, each an independent UNIFORM draw over the types unlocked so far.
   *  No rarity weighting — a Conveyor is exactly as likely as a Vat. That is the point: belts are precious and
   *  a pile of duplicate specials is a joke you cannot build with. */
  /** DEV: 99 of every placeable building, ignoring launch unlocks. Testing a new machine otherwise means
   *  playing the whole tycoon up to its unlock and then hoping the pack RNG hands it over. */
  stockAll(): void {
    if (this.mode !== "player") return;
    const n = 99;
    let kinds = 0;
    for (const k of PALETTE) {
      if (!BUILDINGS[k].placeable) continue;   // BULLDOZE and any non-placeable palette entry aren't stock
      this.inv.set(k, n);
      kinds++;
    }
    this.hud?.setInventory(this.inv, this.packPrice(), this.materials);
    this.hud?.flash(`DEV · ${n}× ${kinds} buildings`);
  }

  /** Every teleporter B, in a STABLE order (floor, then z, then x) so the round-robin is predictable rather
   *  than dependent on Map insertion — a player rebuilding one exit shouldn't reshuffle the whole rotation.
   *  Cached; invalidated by any placement or bulldoze. */
  private teleportExits(): Placed[] {
    if (!this.tpExits) {
      this.tpExits = [...this.buildings.values()].filter((b) => b.kind === "teleporterb")
        .sort((a, b) => (a.floor - b.floor) || (a.gz - b.gz) || (a.gx - b.gx));
    }
    return this.tpExits;
  }

  private receptacles(): Placed[] {
    if (!this.recList) this.recList = [...this.buildings.values()].filter((b) => b.kind === "receptacle");
    return this.recList;
  }

  /** Has this ore actually gone OUT through a receptacle — ridden across the deck, off the far edge and dropped
   *  past the lip — rather than merely touched it? Banking on contact meant a clog resting on the deck was eaten
   *  where it sat; the ore has to physically leave the plate through the tube. Returns true if it was consumed. */
  private sinkThroughReceptacle(o: Item): boolean {
    for (const r of this.receptacles()) {
      const ax = this.worldX(r.gx), az = this.worldZ(r.gz);
      const [dx, dz] = DIR_VEC[r.dir];
      const along = (o.px - ax) * dx + (o.pz - az) * dz;          // distance along travel from the anchor center
      if (along <= FACTORY.tile * 0.5) continue;                   // still on or behind the deck — not through yet
      const side = Math.abs((o.px - ax) * -dz + (o.pz - az) * dx); // lateral offset from the lane
      if (side > FACTORY.tile * 0.9) continue;                     // went off the SIDE, not out through the tube
      if (o.py > this.floorY(r.floor) + FACTORY.oreDeck - FACTORY.receptacleLip) continue; // hasn't dropped yet
      r.through = (r.through ?? 0) + 1;
      this.launch(this.oreValue(o.value, o.elem, o.elemMask), r.gx, r.gz);
      o.active = false;
      return true;
    }
    return false;
  }

  /** Stretch the tube (`Part3`) so it runs THROUGH the ground from whatever floor the receptacle sits on.
   *  Geometry spans y[-100,0], so scaling about y=0 grows it downward and leaves the mouth where it is. */
  private fitReceptacleTube(mesh: THREE.Object3D): void {
    const tube = mesh.getObjectByName("Part3") as THREE.Mesh | undefined;
    if (!tube) return;
    const bottom = this.baseY - FACTORY.receptacleTubeDepth;
    tube.scale.y = Math.max(1, (mesh.position.y - bottom) / 100);
  }

  /** Read a quick-place command back in words, and say whether it would actually commit. Every rule the echo
   *  reports lives here rather than in the HUD, so the panel never has to know the placement rules. */
  probePlacement(sel: Sel, gx: number, gz: number, dir: number): { ok: boolean; text: string } {
    const FACE = ["▶ +X", "▼ +Z", "◀ −X", "▲ −Z"];
    const at = `(${gx},${gz}) f${this.activeFloor}`;
    if (!this.inZone(gx, gz)) return { ok: false, text: `${at} — <b>outside the build zone</b>` };
    if (sel === BULLDOZE) {
      const k = key(gx, gz, this.activeFloor);
      const b = this.buildings.get(k) ?? this.mods.get(k) ?? this.reserved.get(k);
      const wa = this.walls.get(k);
      const what = b ? BUILDINGS[b.kind].name : (wa && wa.length ? "wall" : null);
      return what
        ? { ok: true, text: `<b>Bulldoze</b> ${what} · ${at}` }
        : { ok: false, text: `${at} — <b>nothing to bulldoze</b>` };
    }
    const kind = sel as BuildingKind;
    const name = BUILDINGS[kind].name;
    if ((this.inv.get(kind) ?? 0) <= 0) return { ok: false, text: `<b>${name}</b> — none owned` };
    const need = unlockAt(kind);
    if (this.launched < need) return { ok: false, text: `<b>${name}</b> — locked, launch ${need}` };
    if (this.buildings.has(key(gx, gz, this.activeFloor)) && kind !== "cryo")
      return { ok: false, text: `${at} — <b>occupied</b>` };
    return { ok: true, text: `<b>${name}</b> · ${at} · ${FACE[dir % 4]}` };
  }

  buyPack(): void {
    if (this.mode !== "player") return;
    const price = this.packPrice();
    if (this.materials < price) { this.hud?.flash(`pack costs ◆ ${price}`); return; }
    const pool = PALETTE.filter((k) => this.launched >= unlockAt(k) && BUILDINGS[k].placeable);
    if (!pool.length) return;
    this.materials -= price;
    this.packsBought++;
    const got: BuildingKind[] = [];
    for (let i = 0; i < FACTORY.packDraws; i++) {
      const k = pool[(Math.random() * pool.length) | 0];
      this.inv.set(k, (this.inv.get(k) ?? 0) + 1);
      got.push(k);
    }
    this.hud?.setMaterials(this.materials, this.launched);
    this.hud?.setInventory(this.inv, this.packPrice(), this.materials);
    this.hud?.pack(got);
  }

  /** Active ore riding this factory's belts (parts excluded) — for the DEV readout. */
  oreCount(): number {
    let n = 0;
    for (const o of this.items) if (o.active && !o.part) n++;
    return n;
  }

  /** Rebuild the physics world so baked-in PHYS knobs (ore shape/size, friction, tumble, gravity, belt speed) take
   *  effect. Ore keep their positions and are re-bodied next frame by the normal reconcile; belts/walls re-register
   *  from the layout. Drives the DEV "Physics" bank, where these are FEEL calls best judged in motion. */
  rebuildPhysics(): void {
    this.joltGen++;                       // invalidate any create() still in flight
    if (this.jolt) this.jolt.teardown();
    this.jolt = null;
    this.joltBooting = false;
    for (const o of this.items) { o.bodyH = undefined; o.flung = false; }
  }

  /** Comms aid: name the tile under the cursor and everything stacked on it — coordinates, each floor's building
   *  and which way it FACES, walls/mods/footprints, plus how many ore are sitting on that column right now. A dense
   *  base is impossible to eyeball, so this is how a trouble spot gets reported precisely. Works in build mode
   *  (probes the active floor) and in the free camera (probes the ground plane). */
  private updateTileTip(commandMode: boolean): void {
    if (!this.hud) return;
    if (this.mode !== "player" || !(commandMode || this.buildMode)) { this.hud.setTileTip(0, 0, null); return; }
    const nd = this.input.pointerNDC();
    this._ray.setFromCamera(new THREE.Vector2(nd.x, nd.y), this.camera);
    this._plane.constant = -this.floorY(this.buildMode ? this.activeFloor : 0);
    const hit = this._ray.ray.intersectPlane(this._plane, this._hit);
    const t = hit ? this.tileAtWorld(hit.x, hit.z) : null;
    if (!t || !this.inZone(t.gx, t.gz)) { this.hud.setTileTip(0, 0, null); return; }

    const FACE = ["▶ +X", "▼ +Z", "◀ −X", "▲ −Z"];
    let ore = 0;
    for (const o of this.items) if (o.active && !o.part && o.gx === t.gx && o.gz === t.gz) ore++;
    let html = `<span class="xy">⌗ ${t.gx},${t.gz}</span>` + (ore ? `  <span class="em">${ore} ore</span>` : "");
    for (let fl = FACTORY.maxFloors - 1; fl >= 0; fl--) {
      const k = key(t.gx, t.gz, fl), bits: string[] = [];
      const b = this.buildings.get(k);
      if (b) bits.push(`<span class="on">${BUILDINGS[b.kind].name}</span> ${FACE[b.dir]}`);
      const r = this.reserved.get(k);
      if (!b && r) bits.push(`<span class="fl">${BUILDINGS[r.kind].name} footprint</span>`);
      if (!b && this.rampCells.get(k)) bits.push(`<span class="fl">ramp slope</span>`);
      const m = this.mods.get(k);
      if (m) bits.push(`<span class="em">${BUILDINGS[m.kind].name}</span>`);
      const wa = this.walls.get(k);
      if (wa?.length) bits.push(`<span class="em">wall</span> ${wa.map((x) => FACE[x.dir].slice(2)).join(" ")}`);
      if (!bits.length) continue;
      const mark = this.buildMode && fl === this.activeFloor ? "▸" : " ";
      html += `\n<span class="fl">${mark}f${fl}</span> ${bits.join("  ")}`;
    }
    const px = this.input.pointerPx();
    this.hud.setTileTip(px.x, px.y, html);
  }

  // ── DEV ore inspector ─────────────────────────────────────────────────────────────────────────────────
  //  The developer clicks an ore that LOOKS stuck; from then on this follows THAT ORE and records what is
  //  physically holding it. Identity is the whole difficulty: `items` is a pool and Jolt's bodies come off a
  //  free list, so an Item reference (or a bodyH) names a SLOT that a later, unrelated ore will inherit.
  //  Only Item.uid names an ore, so every sample re-checks it rather than trusting the reference.
  private inspectOn = false;
  private latched: {
    uid: number; item: Item; ticks: number; stalledTicks: number; ended: string | null;
    px: number; py: number; pz: number;
    trace: { t: number; x: number; y: number; z: number; v: number; cell: string | null; probed: boolean; belt: number; wall: number; curb: number; ore: number; ny: number }[];
  } | null = null;

  setInspect(on: boolean): void {
    this.inspectOn = on;
    this.jolt?.setProbe(on);
    if (!on) { this.latched = null; this.hud?.setOrePin(null); }
    else this.hud?.setOrePin(`<span class="xy">ORE INSPECTOR</span>\nclick an ore to follow it`);
  }

  /** Raycast the ore InstancedMesh under the cursor and latch onto the ore that was hit. Returns false when the
   *  click missed every ore, so the caller can let the click fall through to the camera. */
  pickOreUnderPointer(): boolean {
    if (!this.inspectOn) return false;
    const nd = this.input.pointerNDC();
    this._ray.setFromCamera(new THREE.Vector2(nd.x, nd.y), this.camera);
    this.itemMesh.computeBoundingSphere(); // ore move every frame — a cached sphere silently misses the hit
    for (const h of this._ray.intersectObject(this.itemMesh, false)) {
      const it = h.instanceId != null ? this.instItem[h.instanceId] : null;
      if (!it || !it.active || it.part || it.uid == null) continue;
      this.latched = { uid: it.uid, item: it, ticks: 0, stalledTicks: 0, ended: null, px: it.px, py: it.py, pz: it.pz, trace: [] };
      this.sampleInspect(true); // snapshot immediately — clicking usually happens with the sim paused
      return true;
    }
    return false;
  }

  /** One trace sample of the latched ore (20 Hz, 60 s ring). `force` takes a snapshot right now — used on the
   *  click itself, and it is why the sim being PAUSED does not fabricate stall time: a paused ore is not moving
   *  because time is stopped, which is not the same as being stuck, so update() stops sampling while dt is 0. */
  private sampleInspect(force = false): void {
    const L = this.latched;
    if (!L || L.ended) return;
    const o = L.item;
    if (o.uid !== L.uid || !o.active) {                        // the slot moved on — do NOT keep reporting on it
      L.ended = o.uid !== L.uid ? "gone — its pool slot was reused by a new ore" : "gone — sunk, destroyed, or fell out of the world";
      this.renderOrePin();
      return;
    }
    L.ticks++;
    if (!force && L.ticks % 3) return;                         // 20 Hz
    const v = Math.hypot(o.px - L.px, o.py - L.py, o.pz - L.pz) * 20; // units/second between samples
    L.px = o.px; L.py = o.py; L.pz = o.pz;
    if (v < 4) L.stalledTicks++; else L.stalledTicks = 0;
    const cell = this.jolt && o.bodyH != null ? this.jolt.contactCell(o.bodyH) : null;
    const pr = this.jolt && o.bodyH != null ? this.jolt.probeOf(o.bodyH) : null;
    L.trace.push({ t: L.ticks, x: Math.round(o.px), y: Math.round(o.py), z: Math.round(o.pz), v: Math.round(v),
                   cell, probed: pr != null, belt: pr?.belt ?? 0, wall: pr?.wall ?? 0, curb: pr?.curb ?? 0, ore: pr?.ore ?? 0, ny: pr ? +pr.nyMin.toFixed(2) : 1 });
    if (L.trace.length > 1200) L.trace.shift();
    this.renderOrePin();
  }

  private renderOrePin(): void {
    const L = this.latched;
    if (!L || !this.hud) return;
    const s = L.trace[L.trace.length - 1];
    if (L.ended) { this.hud.setOrePin(`<span class="xy">ore #${L.uid}</span>\n<span class="bad">${L.ended}</span>`); return; }
    if (!s) return;
    // name what is holding it, using the same reading as the console tools: ore behind it = a queue (often
    // designed), a wall with nothing behind it = the interesting kind of stall.
    const solid = s.wall + s.curb, name = s.wall > 0 ? "WALL" : "CURB (auto lane edge)";
    const held = !s.probed ? `<span class="bad">no contact data</span> — probe is off, not a physics reading`
      : s.belt === 0 && solid === 0 && s.ore === 0 ? `<span class="fl">airborne</span> — touching nothing`
      : s.v >= 4 ? `<span class="on">moving</span>`
      : s.belt === 0 && s.ore > 0 ? `<span class="bad">riding on other ore</span> (no belt under it)`
      : s.belt === 0 && solid > 0 ? `<span class="bad">hanging on a ${name}</span>, off the deck`
      : solid > 0 && s.ore === 0 ? `<span class="bad">held by a ${name}</span>, nothing behind it`
      : s.ore > 0 ? `queued behind other ore`
      : `on the deck, undriven`;
    const b = s.cell ? (this.buildings.get(s.cell) ?? this.reserved.get(s.cell)) : null;
    this.hud.setOrePin(
      `<span class="xy">ore #${L.uid}</span>  ${Math.round(s.v)} u/s\n` +
      `<span class="fl">on</span> ${s.cell ?? "(airborne)"} ${b ? BUILDINGS[b.kind].name : ""}\n` +
      `${held}\n` +
      `<span class="fl">contacts</span> belt ${s.belt} · curb ${s.curb} · wall ${s.wall} · ore ${s.ore}\n` +
      `<span class="fl">stalled</span> ${(L.stalledTicks / 20).toFixed(1)}s   <span class="fl">trace</span> ${L.trace.length}`);
  }

  /** DEV: the whole trace, for console analysis. */
  inspectTrace(): unknown {
    const L = this.latched;
    if (!L) return { latched: false, hint: "turn on DEV → Ore inspector, then click an ore" };
    return { uid: L.uid, ended: L.ended, stalledSeconds: +(L.stalledTicks / 20).toFixed(1), samples: L.trace.length, trace: L.trace };
  }

  private handleBuildInput(): void {
    // NO per-building hotkeys. They were removed outright: with ~30 buildings every letter was taken, which
    // (a) made them unmemorable anyway — clicking the inventory is faster — and (b) meant each new building
    // silently stole a key from something else. Teleporter B took KeyR and broke ghost ROTATION below, because
    // this loop ran first and consumed the press. Selection is click-only; the build-mode keys are now just
    // rotate/floor/bulldoze, which cannot collide with content.
    if (this.input.consumeKeyPress("KeyX")) { this.sel = BULLDOZE; this.hud?.setSelected(BULLDOZE); this.rebuildGhost(); }
    if (this.input.consumeKeyPress("KeyR")) { this.ghostDir = ((this.ghostDir + 1) % 4) as Dir; this.rebuildGhost(); }
    // floor selector: [ down a floor, ] up a floor
    if (this.input.consumeKeyPress("BracketRight") && this.activeFloor < FACTORY.maxFloors - 1) { this.activeFloor++; this.hud?.setFloor(this.activeFloor, FACTORY.maxFloors);  this.buildZone(); }
    if (this.input.consumeKeyPress("BracketLeft") && this.activeFloor > 0) { this.activeFloor--; this.hud?.setFloor(this.activeFloor, FACTORY.maxFloors);  this.buildZone(); }

    const nd = this.input.pointerNDC();
    this._ray.setFromCamera(new THREE.Vector2(nd.x, nd.y), this.camera);
    this._plane.constant = -this.floorY(this.activeFloor); // pick the tile on the ACTIVE floor's plane (no parallax)
    const hit = this._ray.ray.intersectPlane(this._plane, this._hit);
    this.hover = hit ? this.tileAtWorld(hit.x, hit.z) : null;
    this.updateGhost();

    const pressed = this.input.consumePlacePressed();
    if (this.hover && this.input.placeHeld) {
      const hit = this.buildings.get(key(this.hover.gx, this.hover.gz, this.activeFloor));
      // Clicking a cannon that is ALREADY there opens its aiming menu. No new input binding: placing onto an
      // occupied tile is a guaranteed no-op, so this costs nothing, and right-click is spoken for (it pans the
      // camera in build mode). Bulldoze still wins — you must be able to remove one without a menu in the way.
      if (this.sel !== BULLDOZE && hit?.kind === "cannon") { if (pressed) this.openCannonMenu(hit); }
      else if (this.sel === BULLDOZE) this.bulldoze(this.hover.gx, this.hover.gz, this.activeFloor);
      else this.place(this.sel, this.hover.gx, this.hover.gz, this.activeFloor, this.ghostDir);
    }
  }

  // ---- power + climate --------------------------------------------------------------------------
  private recomputeClimate(): void {
    this.climateDirty = false;
    const gens: Placed[] = [], coolers: Placed[] = [];
    for (const b of this.buildings.values()) {
      if (b.kind === "generator") gens.push(b);
      else if (b.kind === "cooler") coolers.push(b);
    }
    this.powerSupply = gens.length * FACTORY.genPower;
    this.powerDemand = coolers.length * FACTORY.coolerDraw;
    const poweredN = Math.min(coolers.length, Math.floor(this.powerSupply / FACTORY.coolerDraw));
    const powered = coolers.slice(0, poweredN);
    const r2 = FACTORY.coolerRadius * FACTORY.coolerRadius;

    for (const b of this.buildings.values()) {
      if (this.climate === 0 || !needsClimate(b.kind)) b.climateOK = true;
      else {
        const bx = this.worldX(b.gx), bz = this.worldZ(b.gz);
        let ok = false;
        for (const c of powered) {
          const dx = bx - this.worldX(c.gx), dz = bz - this.worldZ(c.gz);
          if (dx * dx + dz * dz <= r2) { ok = true; break; }
        }
        b.climateOK = ok;
      }
      if (b.chokeMark) b.chokeMark.visible = !b.climateOK;
    }

    let di = 0, wi = 0;
    for (const c of powered) {
      if (di < this.discs.length) {
        const d = this.discs[di++];
        d.visible = true;
        d.position.set(this.worldX(c.gx), this.baseY + 4, this.worldZ(c.gz));
      }
      const g = this.nearest(c, gens);
      if (g && wi < this.wires.length) {
        const w = this.wires[wi++];
        w.visible = true;
        const pos = w.geometry.attributes.position as THREE.BufferAttribute;
        pos.setXYZ(0, this.worldX(g.gx), this.baseY + 58, this.worldZ(g.gz));
        pos.setXYZ(1, this.worldX(c.gx), this.baseY + 58, this.worldZ(c.gz));
        pos.needsUpdate = true;
      }
    }
    for (; di < this.discs.length; di++) this.discs[di].visible = false;
    for (; wi < this.wires.length; wi++) this.wires[wi].visible = false;

    if (this.hud) {
      const choking = this.climate > 0 ? [...this.buildings.values()].filter((b) => needsClimate(b.kind) && !b.climateOK).length : 0;
      this.hud.setPower(this.climate > 0, this.powerSupply, this.powerDemand, choking, this.climateKind);
    }
  }

  private nearest(b: Placed, from: Placed[]): Placed | null {
    let best: Placed | null = null, bd = Infinity;
    for (const g of from) {
      const dx = b.gx - g.gx, dz = b.gz - g.gz, d = dx * dx + dz * dz;
      if (d < bd) { bd = d; best = g; }
    }
    return best;
  }

  // ---- AI procedural builder --------------------------------------------------------------------
  private aiPlan(): void {
    const { cx, cz } = this.starter;
    const climate = this.climate > 0
      ? [{ kind: "generator" as BuildingKind, gx: cx, gz: cz + 3, dir: 0 as Dir }, { kind: "cooler" as BuildingKind, gx: cx - 1, gz: cz + 1, dir: 0 as Dir }]
      : [];
    this.aiQueue = [
      // ore line: drill(cx-3) → ENRICH(+) → belt → receptacle(cx+2). (Was enrich → multiply → imbue; the
      // multiplier + inline element imbuers were archived 2026-07-22, so the AI just belts the ore onward now.)
      { kind: "enricher", gx: cx - 2, gz: cz, dir: 0 },
      { kind: "conveyor", gx: cx - 1, gz: cz, dir: 0 },
      { kind: "conveyor", gx: cx, gz: cz, dir: 0 },
      { kind: "conveyor", gx: cx + 1, gz: cz, dir: 0 },
      ...climate,
      // assembly line (fabricator → assembler station → deployer). Build the SINK first and the
      // material-draining SOURCE last, so the fabricator never spills parts (wasting materials) before
      // the deployer exists.
      { kind: "deployer", gx: cx - 1, gz: cz + 2, dir: 0 },
      { kind: "assembler", gx: cx - 2, gz: cz + 2, dir: 0 },
      { kind: "fabricator", gx: cx - 3, gz: cz + 2, dir: 0 },
    ];
    this.aiStep = 1.0;
    this.aiEscalate = 20;
  }

  private aiTick(dt: number): void {
    if (this.aiQueue.length) {
      this.aiStep -= dt;
      if (this.aiStep <= 0) {
        const s = this.aiQueue[0];
        if (this.place(s.kind, s.gx, s.gz, 0, s.dir)) { this.aiQueue.shift(); this.aiStep = 1.3; }
        else this.aiStep = 0.8; // can't afford yet — wait for income
      }
      return;
    }
    this.aiEscalate -= dt;
    if (this.aiEscalate <= 0) {
      this.aiEscalate = 18;
      if (this.materials > 100) { // reinforce: another fabricator feeding the assembly line
        const spot = this.freeSpotNear(this.starter.cx - 3, this.starter.cz + 2);
        if (spot) this.place("fabricator", spot.gx, spot.gz, 0, 0);
      }
    }
  }

  private freeSpotNear(cx: number, cz: number): { gx: number; gz: number } | null {
    for (let r = 1; r <= 5; r++)
      for (let dz = -r; dz <= r; dz++) for (let dx = -r; dx <= r; dx++) {
        const gx = cx + dx, gz = cz + dz;
        if (this.inZone(gx, gz) && !this.buildings.has(key(gx, gz, 0))) return { gx, gz };
      }
    return null;
  }

  // ---- ore + part sim ---------------------------------------------------------------------------
  /** Give every active ore a physics body and retire the bodies of dead ore. Safe at a variable rate — it is a
   *  diff, not a simulation step — so it runs once per frame AND after each fixed step (ore spawned by a tick
   *  need a body before the next Step, or they hang in the air for the rest of the frame). */
  private reconcileOreBodies(w: JoltWorld): void {
    for (const o of this.items) {
      if (o.part) continue;
      if (o.active && o.bodyH == null) { const h = w.spawnOre(o.px, o.py, o.pz); if (h != null) o.bodyH = h; } // null = pool full → retry next tick
      else if (!o.active && o.bodyH != null) { w.despawnOre(o.bodyH); o.bodyH = undefined; }
    }
  }

  /** ONE simulation tick — machine timers, then the item pass. Under Jolt `update` drives these two halves
   *  separately so they keep their original order AROUND the physics Step (buildings before, items after);
   *  the legacy integrator calls both together per frame. */
  private simulate(dt: number): void { this.tickBuildings(dt); this.tickItems(dt); }

  /** Machine timers: drill cadence, fabricator/blaster charge, the pressurizer chamber. Runs BEFORE the Step,
   *  so ore it drops get a body from the same tick's reconcile — exactly as when this ran ahead of the
   *  per-frame step. Under Jolt `dt` is the fixed 1/30, so cadence no longer rides on frame rate. */
  private tickBuildings(dt: number): void {
    for (const b of this.buildings.values()) {
      if (b.kind === "drill" || b.kind === "drill2") {
        b.cooldown -= dt;
        if (b.cooldown <= 0) {
          const rate = b.kind === "drill2" ? 2 : 1; // dual drill drops twice as fast (its two spouts alternate)
          b.cooldown = FACTORY.dropInterval / (rate * (b.climateOK ? 1 : FACTORY.chokeEff));
          this.dropOre(b); // spawn a PHYSICS ore in the spout; it falls onto whatever's below
        }
      } else if (b.kind === "fabricator") {
        b.cooldown -= dt;
        if (b.cooldown <= 0) {
          const out = this.neighbor(b);
          if (out && (out.kind === "conveyor" || out.kind === "assembler" || isConduit(out.kind)) && this.materials >= FACTORY.fabCost) {
            this.materials -= FACTORY.fabCost;
            b.cooldown = FACTORY.fabTime / (b.climateOK ? 1 : FACTORY.chokeEff);
            this.spawn(true, out.gx, out.gz, out.floor, out.dir, 0, 0);
            this.fling(this.worldX(b.gx), this.baseY + 70, this.worldZ(b.gz), 180, this.team === 0 ? 0x9b6bff : 0xff8ad0);
          } else b.cooldown = 0.3;
        }
      } else if (b.kind === "blaster") {
        b.cooldown -= dt;
        if (b.cooldown <= 0) { b.cooldown = FACTORY.blasterCharge / (b.climateOK ? 1 : FACTORY.chokeEff); this.blasterBlast(b); } // charge → blast the fire beam
      } else if (b.kind === "cannon") {
        this.stepCannon(b, dt); // turret slew is SIM, not animation — it decides where the next shot goes
      } else if (b.kind === "pressurizer") {
        this.stepPressurizer(b, dt);
      } else if (b.kind === "leech" || b.kind === "waverider" || b.kind === "triplexer") {
        b.busy = false; // cleared here; the ore pass sets it true when ore is on the belt (drives the neon)
      }
    }
  }

  /** The ore/part pass — reads post-Step positions and fires the capKey economy. Under Jolt this runs AFTER
   *  each fixed Step (positions must be fresh); the legacy fused integrator runs it per frame as before. */
  private tickItems(dt: number): void {
    const step = (FACTORY.oreSpeed * dt) / FACTORY.tile;
    // Phase 1: when the Jolt world is up it OWNS ore movement and the capKey economy runs as a position pass
    // (oreEconomyPass); otherwise the legacy fused integrator (updateOre) runs, byte-for-byte as before.
    const w = this.jolt;
    for (const o of this.items) {
      if (!o.active) continue;
      // ORE: under Jolt the world owns movement entirely (ramps included — they're real sloped conveyors), and
      // this pass only reads positions to fire the economy. Otherwise the legacy fused integrator.
      if (!o.part) { if (w) this.oreEconomyPass(o); else this.updateOre(o, dt); continue; }
      const here = this.buildings.get(key(o.gx, o.gz, o.floor));
      if (!here) { if (o.part) this.noteWaste(); o.active = false; continue; } // belt under it was bulldozed
      o.t += step;
      if (!o.applied && o.t >= 0.5) {
        const def = BUILDINGS[here.kind];
        if (!o.part && def.proc) {
          const eff = here.climateOK ? 1 : FACTORY.chokeEff; // out of climate band ⇒ the effect is weaker
          // (the generic proc path — Triplexer caffeine applies here too, for parity with the special-cased
          //  machines. NB ore never reach this block today: the `!o.part` items are routed to oreEconomyPass /
          //  updateOre above and `continue`, so this is the PART path with a dead `!o.part` guard. Kept in sync
          //  anyway so the rule is "every upgrader honors caffeine", with no exceptions to remember.)
          if (def.proc === "mult") o.value *= this.caffMult(o, 1 + (def.mult! - 1) * eff);            // × multiplier
          else if (def.proc === "add") o.value += this.caffAdd(o, def.add! * eff);                    // + flat value (add early!)
          else if (def.proc === "elem") { o.elem *= this.caffMult(o, 1 + (FACTORY.elemMul - 1) * eff); o.elemMask |= def.elemBit!; } // imbue element
        } else if (o.part && here.kind === "assembler" && here.climateOK) {
          o.tier += 1; // an Assembler station only advances a part when it's in climate band
        }
        o.applied = true;
      }
      if (o.t < 1) continue;
      // parts stay on the same floor now (lift/chute — the vertical PART transports — were archived 2026-07-22).
      const exitFloor = o.floor;
      // the splitter can route the item out a DIFFERENT face: it alternates forward/right (load-balance / feed two lines).
      let exitDir = o.dir;
      if (here.kind === "splitter") { here.toggle = !here.toggle; exitDir = (here.toggle ? o.dir : (o.dir + 1) % 4) as Dir; }
      const next = this.buildings.get(key(o.gx + DIR_VEC[exitDir][0], o.gz + DIR_VEC[exitDir][1], exitFloor));
      if (!next) { if (o.part) this.noteWaste(); o.active = false; continue; } // spilled off the belt / lift-to-nowhere
      if (!o.part) { // ORE
        // A sink consumes whatever travels INTO it — the item only ever tests the single tile ahead in
        // its own direction, so reaching the furnace means it was aimed at it. No facing match required
        // (that silently ate ore whenever the receptacle faced a different way than the feeding belt).
        if (next.kind === "receptacle") { next.through = (next.through ?? 0) + 1; this.launch(this.oreValue(o.value, o.elem, o.elemMask), next.gx, next.gz); o.active = false; continue; }
        if (!isUpgrader(next.kind) && !isConduit(next.kind)) { o.active = false; continue; }
      } else { // PART
        // Any part that reaches a deployer becomes a unit (tier decides little vs giant). Previously this
        // required o.dir === deployer.dir, so 3 of 4 deployer rotations silently consumed the part and
        // its materials with no unit — and the deployer has no arrow to reveal its facing. A sink accepts.
        if (next.kind === "deployer") { this.deployUnit(o.tier, next); o.active = false; continue; }
        if (next.kind !== "assembler" && !isConduit(next.kind)) { this.noteWaste(); o.active = false; continue; } // part hit a wrong-type building
      }
      o.gx = next.gx; o.gz = next.gz; o.floor = exitFloor; o.dir = next.dir; o.t -= 1; o.applied = false;
    }
    // Armed ore accrue value and roll for detonation on SIM time, not frame time — this ran per frame, where a
    // larger dt inflated each roll's probability (p = risk·bombT·dt) and the survival curve bent with frame
    // rate. At the fixed step every ore sees the same dt, so the gamble is identical at 20fps and 144fps.
    // It runs before the reap below so an ore that detonates this tick gives its body straight back.
    this.tickBombs(dt);
    if (w) for (const o of this.items) if (!o.active && !o.part && o.bodyH != null) { w.despawnOre(o.bodyH); o.bodyH = undefined; } // reap ore the economy just sank (receptacle/destroy) so no body lingers a frame
    this.updateMods(dt); // overhead coolers imbue ice into ore riding the tile beneath them (uses fresh ore positions)
    if (!w) this.separateOres(); // flag-OFF only; under Jolt ore-ore is solver contacts (belts land at Increment 3)
  }

  /** Overhead coolers (cryo, in `mods`): every `cryoPulse` seconds, weakly ICE-imbue any ore riding the tile UNDER
   *  the cooler (bit 2 + a small factor), and flag `busy` so the flywheel drains. A cryo capping a BLASTER has no ore
   *  on its own tile — its work (retool the beam fire→ice) happens in blasterBlast — so it reads idle here, spinning fast. */
  private updateMods(dt: number): void {
    for (const m of this.mods.values()) {
      if (m.kind !== "cryo") continue;
      m.cooldown -= dt;
      const pulse = m.cooldown <= 0;
      let count = 0; // how many ore are over the tile RIGHT NOW (density = the throughput proxy that drains the flywheel)
      for (const o of this.items) {
        if (!o.active || o.part || o.floor !== m.floor || o.gx !== m.gx || o.gz !== m.gz) continue;
        count++;
        if (pulse) { o.elemMask |= 2; o.elem *= FACTORY.cryoIce; } // ICE = bit 2, weak factor (steam-pairs with a blaster's fire)
      }
      if (pulse) { m.cooldown = count > 0 ? FACTORY.cryoPulse : 0; if (count > 0) m.flash = 1; } // no ore ⇒ stay armed to fire the instant it arrives
      // smoothed ore density → drives the flywheel drain; decays back toward 0 when the stream stops (spins the top wheel up)
      m.load = (m.load ?? 0) + (count - (m.load ?? 0)) * Math.min(1, dt / FACTORY.cryoLoadTau);
      m.busy = count > 0;
    }
  }

  // ---- physics ore (P1): works with the IMPORTED assets only — drops from the dropper's spout, falls, rides
  // conveyors by belt surface-velocity, clogs via separation, and is swallowed at the receptacle. Pass-through
  // placeholders (upgraders/splitters/…) are solid blocks with no channel yet, so ore just piles against them
  // until those assets are remade with holes. ----------------------------------------------------------------

  /** Spawn a physics ore inside the drill's glass spout (alternating spouts on the dual drill). It free-falls. */
  private dropOre(b: Placed): void {
    // drop points + the building's world transform come from the instanced path (b.mat + model) or the Group path.
    const conf = INST[b.kind];
    const drops = conf ? conf.model.dropPoints : (b.mesh?.userData.dropPoints as [number, number, number][] | undefined);
    let xf: THREE.Matrix4 | undefined = b.mat;
    if (!xf && b.mesh) { b.mesh.updateMatrixWorld(); xf = b.mesh.matrixWorld; }
    if (drops && drops.length && xf) {
      const ix = b.dropIx = ((b.dropIx ?? -1) + 1) % drops.length;
      this._v.set(drops[ix][0], drops[ix][1], drops[ix][2]).applyMatrix4(xf);
    } else {
      const out = this.neighbor(b);
      this._v.set(this.worldX(out?.gx ?? b.gx), this.floorY(b.floor) + 70, this.worldZ(out?.gz ?? b.gz));
    }
    for (const o of this.items) {
      if (o.active) continue;
      o.active = true; o.part = false; o.floor = b.floor; o.applied = false; o.tier = 0; o.t = 0; o.uid = ++this.oreUid;
      o.qx = 0; o.qy = 0; o.qz = 0; o.qw = 1; // upright: this slot may still hold the last ore's tumble
      o.bombT = undefined;                   // ...and its BOMB. A pooled slot that once held an armed ore
      // handed its accumulated fuse to every ore born there afterwards — new ore arrived already ticking,
      // deep into the risk curve, and blew up at the drill. It cascades: one armed ore poisons its slot
      // for the rest of the session, and the pool survives importCode/rebuildPhysics, so it outlives the
      // layout that caused it. EVERY per-ore field has to be reset here; the pool is not a fresh object.
      o.caff = 0;                            // ...and its TRIPLEXER CHARGES, for exactly the same reason.
      // (Measured while wiring this up: with the reset missing, 3 of 28 ore on a rig containing NO Triplexer at
      //  all still arrived caffeinated and took a boosted upgrade. Same class of bug as the bomb fuse above.)
      if (this.jolt && o.bodyH != null) this.jolt.despawnOre(o.bodyH); // recycling this slot → free any stale body first
      o.bodyH = undefined;
      o.px = this._v.x; o.py = this._v.y; o.pz = this._v.z; o.vx = 0; o.vy = -10; o.vz = 0; o.capKey = -1;
      o.gx = Math.floor((o.px - this.originX) / FACTORY.tile);
      o.gz = Math.floor((o.pz - this.originZ) / FACTORY.tile);
      o.value = this.oreStartValue; o.elemMask = this.oreStartMask; o.elem = this.oreStartMask ? 1.15 : 1;
      return;
    }
  }

  /** One ore's physics step: ride a RAMP up-slope (→ next floor at the top), else gravity + conveyor ride / clog. */
  private updateOre(o: Item, dt: number): void {
    o.px += o.vx * dt; o.pz += o.vz * dt;
    const gx = Math.floor((o.px - this.originX) / FACTORY.tile);
    const gz = Math.floor((o.pz - this.originZ) / FACTORY.tile);
    o.gx = gx; o.gz = gz;
    const inZ = this.inZone(gx, gz);
    // ON A RAMP → ride UP the incline; at the top, step onto the upper floor and carry on there.
    const ramp = inZ ? this.rampCells.get(key(gx, gz, o.floor)) : undefined;
    if (ramp) {
      const [rdx, rdz] = DIR_VEC[ramp.dir];
      // prog 0 at the ramp FOOT (anchor's back edge, +½ tile behind its center), 1 at the top of the 3rd cell
      const prog = (((o.px - this.worldX(ramp.gx)) * rdx + (o.pz - this.worldZ(ramp.gz)) * rdz) + FACTORY.tile * 0.5) / (3 * FACTORY.tile);
      if (prog >= 0.98) {
        o.floor = Math.min(ramp.floor + 1, FACTORY.maxFloors - 1);
        o.py = this.floorY(o.floor) + FACTORY.oreDeck; o.vy = 0;
      } else {
        const restY = this.floorY(ramp.floor) + FACTORY.oreDeck + Math.max(0, prog) * FACTORY.floorHeight;
        o.py += (restY - o.py) * Math.min(1, 12 * dt); o.vy = 0;
        const k = Math.min(1, 7 * dt);
        o.vx += (FACTORY.oreSpeed * rdx - o.vx) * k;
        o.vz += (FACTORY.oreSpeed * rdz - o.vz) * k;
      }
      return;
    }
    // FLAT belt / air: gravity, then CATCH on whatever conveyor deck the ore's HEIGHT puts it at — on ANY floor,
    // not a tracked one. So ore off a belt END or a ramp TOP just falls: it lands on a lower belt if one is under
    // it (respecting the belt geometry however it arrives), and spills off if nothing catches it (mis-wired setup).
    const CATCH = 8;
    o.vy = Math.max(o.vy - FACTORY.oreGrav * dt, -450); // gravity, capped terminal speed so a fast fall can't skip a deck
    o.py += o.vy * dt;
    if (o.py < this.floorY(0) - 250) { o.active = false; return; } // fell off the world
    const F = Math.max(0, Math.min(FACTORY.maxFloors - 1, Math.floor((o.py - this.floorY(0) - FACTORY.oreDeck + CATCH) / FACTORY.floorHeight)));
    const deckY = this.floorY(F) + FACTORY.oreDeck;
    let here = inZ ? this.buildings.get(key(gx, gz, F)) : undefined;
    if (!here && inZ) { const r = this.reserved.get(key(gx, gz, F)); if (r && (r.kind === "waverider" || r.kind === "triplexer")) here = r; } // a multi-tile building's OTHER tiles ride its own belt too (waverider = 2 long, triplexer = 3 in an L)
    if (this.sinkThroughReceptacle(o)) return; // banked only once it has ridden out over the lip, not on contact
    if (here && here.kind === "conveyor" && o.py <= deckY + CATCH) {
      o.py = deckY; if (o.vy < 0) o.vy = 0; o.floor = F; o.capKey = -1; // land on / ride this belt (clear any splitter hold)
      const [dx, dz] = DIR_VEC[here.dir];
      const k = Math.min(1, 7 * dt);
      o.vx += (FACTORY.oreSpeed * dx - o.vx) * k;
      o.vz += (FACTORY.oreSpeed * dz - o.vz) * k;
    } else if (here && here.kind === "splitter" && o.py <= deckY + CATCH) {
      // SPLITTER: ride into the black center box, then the splitter takes control and flings the ore out one of the
      // 3 non-intake sides, cycling. Intake = the −dir (back) side; outputs cycle {dir, dir+1(right), dir+3(left)}.
      o.py = deckY; if (o.vy < 0) o.vy = 0; o.floor = F;
      const tk = gx + gz * 100 + F * 10000, cx = this.worldX(gx), cz = this.worldZ(gz), k = Math.min(1, 10 * dt);
      if (o.capKey === tk) {                                          // already captured here → drive out the chosen output
        const [ex, ez] = DIR_VEC[o.dir];
        o.vx += (FACTORY.oreSpeed * ex - o.vx) * k;
        o.vz += (FACTORY.oreSpeed * ez - o.vz) * k;
      } else {
        const dcx = o.px - cx, dcz = o.pz - cz, d = Math.hypot(dcx, dcz);
        if (d < 16) {                                                 // fully engulfed in the center → take control + eject
          const outs: Dir[] = [here.dir, ((here.dir + 1) % 4) as Dir, ((here.dir + 3) % 4) as Dir];
          here.splitIx = ((here.splitIx ?? -1) + 1) % 3;
          o.dir = outs[here.splitIx]; o.capKey = tk;
          o.px = cx; o.pz = cz;                                       // snap to center (masked by the black box), then fling
          const [ex, ez] = DIR_VEC[o.dir];
          o.vx = FACTORY.oreSpeed * ex; o.vz = FACTORY.oreSpeed * ez;
        } else {                                                      // still approaching → ride toward the center
          o.vx += ((d > 0 ? -dcx / d : 0) * FACTORY.oreSpeed - o.vx) * k;
          o.vz += ((d > 0 ? -dcz / d : 0) * FACTORY.oreSpeed - o.vz) * k;
        }
      }
    } else if (here && here.kind === "pressurizer" && o.py <= deckY + CATCH) {
      // PRESSURIZER (oven): the tile is a deck. LOAD → capture ore into the sealed chamber; COOK → frozen inside;
      // RELEASE → ride out the exit (forward). When sealed/full, incoming ore is shoved back out the entrance to wait.
      o.py = deckY; if (o.vy < 0) o.vy = 0; o.floor = F;
      const nk = gx + gz * 100 + F * 10000, cx = this.worldX(gx), cz = this.worldZ(gz);
      const [fx, fz] = DIR_VEC[here.dir], k = Math.min(1, 8 * dt), lim = FACTORY.tile * 0.5 - 12;
      if (o.capKey === nk) {                                          // HELD in this batch
        if (here.phase === "release") {                              // exit open → ride out the front, but can't slip back
          o.vx += (FACTORY.oreSpeed * fx - o.vx) * k; o.vz += (FACTORY.oreSpeed * fz - o.vz) * k;
          const along = (o.px - cx) * fx + (o.pz - cz) * fz;
          if (along < -lim) { const d = -lim - along; o.px += d * fx; o.pz += d * fz; }
        } else {                                                      // load/cook → settle toward center + stay boxed inside
          o.vx += (cx - o.px) * 0.05 - o.vx * k; o.vz += (cz - o.pz) * 0.05 - o.vz * k;
          o.px = Math.max(cx - lim, Math.min(cx + lim, o.px)); o.pz = Math.max(cz - lim, Math.min(cz + lim, o.pz));
        }
      } else if (here.phase === "load" && (here.heldCount ?? 0) < FACTORY.pressurizerSlots) {
        if ((here.heldCount ?? 0) === 0) here.phaseT = FACTORY.pressurizerLoad; // start the load window on the first ore
        here.heldCount = (here.heldCount ?? 0) + 1;
        o.capKey = nk;                                                // captured → next frames it settles inside (branch above)
        o.vx += (FACTORY.oreSpeed * fx * 0.3 - o.vx) * k; o.vz += (FACTORY.oreSpeed * fz * 0.3 - o.vz) * k; // drift inward
      } else {                                                        // doors shut / oven full → shove back out the entrance
        const along = (o.px - cx) * fx + (o.pz - cz) * fz, back = -FACTORY.tile * 0.5 - 4;
        if (along > back) { const d = along - back; o.px -= d * fx; o.pz -= d * fz; }
        o.vx = 0; o.vz = 0;
      }
    } else if (here && here.kind === "magnetron" && o.py <= deckY + CATCH) {
      // MAGNETRON: rides like a conveyor; the hovering disk magnetizes ore ONCE per pass (value ×+ MAGNETIZED bit 16).
      o.py = deckY; if (o.vy < 0) o.vy = 0; o.floor = F;
      const nk = gx + gz * 100 + F * 10000, [dx, dz] = DIR_VEC[here.dir], k = Math.min(1, 7 * dt);
      o.vx += (FACTORY.oreSpeed * dx - o.vx) * k;
      o.vz += (FACTORY.oreSpeed * dz - o.vz) * k;
      if (o.capKey !== nk) { o.value *= this.caffMult(o, FACTORY.magnetronMult); o.elemMask |= 16; o.capKey = nk; here.flash = 1; } // once per pass + kick the disk spin
    } else if (here && here.kind === "infernace" && o.py <= deckY + CATCH) {
      // INFERNACE (geothermal): rides through the furnace's own channel; fire-imbues ONCE per pass + stamps the top down.
      o.py = deckY; if (o.vy < 0) o.vy = 0; o.floor = F;
      const nk = gx + gz * 100 + F * 10000, [dx, dz] = DIR_VEC[here.dir], k = Math.min(1, 7 * dt);
      o.vx += (FACTORY.oreSpeed * dx - o.vx) * k;
      o.vz += (FACTORY.oreSpeed * dz - o.vz) * k;
      if (o.capKey !== nk) { o.elem *= this.caffMult(o, FACTORY.infernaceFire); o.elemMask |= 1; o.capKey = nk; here.flash = 1; } // fire (bit 1) once per pass + stamp the top
    } else if (here && here.kind === "teleportera" && o.py <= deckY + CATCH) {
      // TELEPORTER A → B. Round-robin is per ENTRANCE, so 1 A + N B alternates between exits (a splitter) and
      // N A + 1 B lands everything on one pad (a condenser) — both fall out of the same cursor without a
      // special case. Deliberately CROSS-FLOOR: this is the vertical transport the archived lift/chute left
      // missing, and the only way to use the plate's 10 floors without a ramp for every one.
      o.py = deckY; if (o.vy < 0) o.vy = 0; o.floor = F;
      const tnk = gx + gz * 100 + F * 10000;
      if (o.capKey !== tnk) {                        // once per pass, like every other inline machine
        o.capKey = tnk;
        const exits = this.teleportExits();
        if (!exits.length) {
          o.active = false;                          // an unpaired A eats the ore, so a half-built link is loud
          this.fling(this.worldX(gx), this.baseY + 44, this.worldZ(gz), 130, BUILDINGS.teleportera.accent);
        } else {
          const ck = key(gx, gz, F);
          const i = (this.tpCursor.get(ck) ?? 0) % exits.length;
          this.tpCursor.set(ck, i + 1);
          const t = exits[i];
          const tx = this.worldX(t.gx), tz = this.worldZ(t.gz), ty = this.floorY(t.floor) + FACTORY.oreDeck;
          const [tdx, tdz] = DIR_VEC[t.dir];
          const vx = tdx * PHYS.speed, vz = tdz * PHYS.speed;
          // Move the BODY, not just the bookkeeping — under Jolt the body is authoritative and readInto copies
          // back over anything written here. setBody is the same call the ramp climb uses.
          o.px = tx; o.py = ty; o.pz = tz; o.gx = t.gx; o.gz = t.gz; o.floor = t.floor;
          o.vx = vx; o.vy = 0; o.vz = vz;
          o.capKey = t.gx + t.gz * 100 + t.floor * 10000; // count the exit tile as already used, so B doesn't re-fire
          // (legacy `?jolt=0` twin — kept in parity with oreEconomyPass; this path derives the tile from o.px/o.pz
          //  so it never had the stale-contact double-jump, but the two copies must not diverge.)
          if (o.bodyH !== undefined && this.jolt) { this.jolt.setBody(o.bodyH, tx, ty, tz, vx, 0, vz); this.jolt.forgetContact(o.bodyH); }
          this.fling(this.worldX(gx), this.baseY + 40, this.worldZ(gz), 90, BUILDINGS.teleportera.accent); // vanish
          this.fling(tx, ty + 24, tz, 90, BUILDINGS.teleporterb.accent);                                   // arrive
        }
      }
    } else if (here && here.kind === "ecobox" && o.py <= deckY + CATCH) {
      // ECOBOX (Infestor): rides through the machine's own deck; a "spore" that GROWS with the ore's accumulated
      // value → a value × once per pass, scaling up the more the ore is already worth (near-nothing on raw ore).
      o.py = deckY; if (o.vy < 0) o.vy = 0; o.floor = F;
      const nk = gx + gz * 100 + F * 10000, [dx, dz] = DIR_VEC[here.dir], k = Math.min(1, 7 * dt);
      o.vx += (FACTORY.oreSpeed * dx - o.vx) * k;
      o.vz += (FACTORY.oreSpeed * dz - o.vz) * k;
      if (o.capKey !== nk) {
        const grown = Math.max(0, o.value - 1);
        const boost = FACTORY.ecoboxGrowMax * grown / (grown + FACTORY.ecoboxGrowScale); // 0 (raw) → ecoboxGrowMax (very grown)
        o.value *= this.caffMult(o, 1 + boost);
        o.capKey = nk;
        here.flash = Math.min(1, 0.35 + 0.65 * (boost / FACTORY.ecoboxGrowMax)); // pulse the neon, BRIGHTER the more the spore grew
      }
    } else if (here && (here.kind === "mutator" || here.kind === "laser") && o.py <= deckY + CATCH) {
      // MUTATOR / LASER on the legacy integrator (see oreEconomyPass for the design notes).
      o.py = deckY; if (o.vy < 0) o.vy = 0; o.floor = F;
      const nk = gx + gz * 100 + F * 10000, [dx, dz] = DIR_VEC[here.dir], k = Math.min(1, 7 * dt);
      o.vx += (FACTORY.oreSpeed * dx - o.vx) * k;
      o.vz += (FACTORY.oreSpeed * dz - o.vz) * k;
      if (o.capKey !== nk) {
        o.capKey = nk;
        if (here.kind === "mutator") {
          o.value *= this.caffMult(o, FACTORY.mutatorMin + Math.random() * (FACTORY.mutatorMax - FACTORY.mutatorMin));
          o.elemMask |= 1 << ((Math.random() * 5) | 0);
          here.flash = 1;
        } else {
          const off = Math.abs((o.px - this.worldX(gx)) * -dz + (o.pz - this.worldZ(gz)) * dx);
          if (off <= FACTORY.laserWindow) {
            if (o.elemMask & 1) { o.elemMask &= ~1; here.charge = 1; here.flash = 1; }
            else { o.value *= this.caffMult(o, FACTORY.laserMult * (here.charge ? FACTORY.laserBurn : 1)); here.charge = 0; here.flash = 1; }
          } else here.flash = Math.max(here.flash ?? 0, 0.15);
        }
      }
    } else if (here && here.kind === "diversifier" && o.py <= deckY + CATCH) {
      // DIVERSIFIER: paints one missing element, then × on the resulting variety (see oreEconomyPass for notes).
      o.py = deckY; if (o.vy < 0) o.vy = 0; o.floor = F;
      const nk = gx + gz * 100 + F * 10000, [dx, dz] = DIR_VEC[here.dir], k = Math.min(1, 7 * dt);
      o.vx += (FACTORY.oreSpeed * dx - o.vx) * k;
      o.vz += (FACTORY.oreSpeed * dz - o.vz) * k;
      if (o.capKey !== nk) {
        const miss: number[] = [];
        for (let e = 0; e < 5; e++) if (!(o.elemMask & (1 << e))) miss.push(e);
        if (miss.length) o.elemMask |= 1 << miss[(Math.random() * miss.length) | 0];
        const n = popcount(o.elemMask);
        o.value *= this.caffMult(o, 1 + FACTORY.diversifierPer * n);
        o.capKey = nk;
        here.flash = n / 5;
      }
    } else if (here && here.kind === "scanner" && o.py <= deckY + CATCH) {
      // SCANNER (Ore Tracker): rides through; a tiny × once per pass, floats the ore's worth as a label + records it
      // to the rolling median/peak readout, and kicks the rings (+45° snap + neon light-up).
      o.py = deckY; if (o.vy < 0) o.vy = 0; o.floor = F;
      const nk = gx + gz * 100 + F * 10000, [dx, dz] = DIR_VEC[here.dir], k = Math.min(1, 7 * dt);
      o.vx += (FACTORY.oreSpeed * dx - o.vx) * k;
      o.vz += (FACTORY.oreSpeed * dz - o.vz) * k;
      if (o.capKey !== nk) {
        o.value *= this.caffMult(o, FACTORY.scannerMult);
        o.capKey = nk;
        const worth = this.oreValue(o.value, o.elem, o.elemMask); // the ore's full current worth (value × elements)
        this.recordScan(worth);
        if (this.mode === "player") this.scanLabels.spawn(o.px, deckY + 56, o.pz, worth); // float the value above the belt
        here.flash = 1;                                           // light up the neon ring
        here.ringSnapTgt = (here.ringSnapTgt ?? 0) + FACTORY.scannerSnap; // +45° snap
      }
    } else if (here && here.kind === "waverider" && o.py <= deckY + CATCH) {
      // WAVERIDER (Schrödinger): a 2-tile-long belt. Rolls one random outcome per pass — capKey on the ANCHOR tile
      // (here.gx/gz) so it fires exactly once across both tiles. Blue neon waves while ore is on it.
      o.py = deckY; if (o.vy < 0) o.vy = 0; o.floor = F;
      const nk = here.gx + here.gz * 100 + F * 10000, [dx, dz] = DIR_VEC[here.dir], k = Math.min(1, 7 * dt);
      o.vx += (FACTORY.oreSpeed * dx - o.vx) * k;
      o.vz += (FACTORY.oreSpeed * dz - o.vz) * k;
      here.busy = true;
      if (o.capKey !== nk) { o.capKey = nk; this.rollWaverider(o, here.dir); }
    } else if (here && here.kind === "leech" && o.py <= deckY + CATCH) {
      // LEECH: its own belt rides the ore; if POWERED by an upgrader directly below (floor F-1) it adds +15% value
      // ONCE per pass and lights its neon. Without an upgrader below it's just a plain (unpowered) belt.
      o.py = deckY; if (o.vy < 0) o.vy = 0; o.floor = F;
      const nk = gx + gz * 100 + F * 10000, [dx, dz] = DIR_VEC[here.dir], k = Math.min(1, 7 * dt);
      o.vx += (FACTORY.oreSpeed * dx - o.vx) * k;
      o.vz += (FACTORY.oreSpeed * dz - o.vz) * k;
      const under = F > 0 ? this.buildings.get(key(gx, gz, F - 1)) : undefined;
      if (under && isUpgraderLike(under.kind)) { here.busy = true; if (o.capKey !== nk) { o.value *= this.caffMult(o, FACTORY.leechMult); o.capKey = nk; } } // powered by ANY upgrader below (incl. magnetron/pressurizer/infernace) → +15% + glow
    } else if (here && here.kind === "triplexer" && o.py <= deckY + CATCH) {
      // TRIPLEXER (Caffeinator) on the legacy integrator. Its three cells do NOT share a heading: the anchor runs
      // along `dir`, the corner and exit along (dir+1)%4 — which is the whole point of the building, an L that
      // turns ore 90° inside itself. capKey on the ANCHOR tile, so a pass across all three cells fires once.
      o.py = deckY; if (o.vy < 0) o.vy = 0; o.floor = F;
      const nk = here.gx + here.gz * 100 + F * 10000;
      const cdir = (gx === here.gx && gz === here.gz) ? here.dir : (((here.dir + 1) % 4) as Dir); // entry rides `dir`; corner + exit turn
      const [dx, dz] = DIR_VEC[cdir], k = Math.min(1, 7 * dt);
      o.vx += (FACTORY.oreSpeed * dx - o.vx) * k;
      o.vz += (FACTORY.oreSpeed * dz - o.vz) * k;
      here.busy = true;                                        // drives the four violet strips (animateTriplexers)
      // caffeinate: the next N upgraders hit harder. `!o.caff` keeps a re-touch from topping the counter back up
      // (see the oreEconomyPass twin — under Jolt a straddling ore otherwise banked a 4th boosted application).
      if (o.capKey !== nk) { if (!o.caff) o.caff = FACTORY.triplexerCharges; o.capKey = nk; }
    } else {
      o.vx *= 0.995; o.vz *= 0.995; o.capKey = -1;                    // airborne — keep gliding, keep falling / ride off the end
    }
    // WALLS contain ore: a walled tile edge is impassable — clamp the ore inside it (soft bounce back onto the belt).
    if (this.walls.size) {
      const lim = FACTORY.tile * 0.5 - 10, cX = this.worldX(gx), cZ = this.worldZ(gz);
      if (o.px > cX + lim && this.edgeWalled(gx, gz, o.floor, 0)) { o.px = cX + lim; if (o.vx > 0) o.vx = -o.vx * 0.2; }
      else if (o.px < cX - lim && this.edgeWalled(gx, gz, o.floor, 2)) { o.px = cX - lim; if (o.vx < 0) o.vx = -o.vx * 0.2; }
      if (o.pz > cZ + lim && this.edgeWalled(gx, gz, o.floor, 1)) { o.pz = cZ + lim; if (o.vz > 0) o.vz = -o.vz * 0.2; }
      else if (o.pz < cZ - lim && this.edgeWalled(gx, gz, o.floor, 3)) { o.pz = cZ - lim; if (o.vz < 0) o.vz = -o.vz * 0.2; }
    }
  }

  /** Phase 1 (flag ON): the capKey-gated ECONOMY, lifted out of updateOre to run over Jolt-provided positions.
   *  Movement (position/velocity/deck-catch) is Jolt's; this only re-derives the tile/floor/building from the
   *  read-back px/py/pz and fires each once-per-pass hook with the SAME formula updateOre uses. capKey stays
   *  sim-owned (the once-per-pass guard here; splitter routing / pressurizer batch are the movement path's, Inc 5).
   *  NOTE: waverider's fling side of rollWaverider writes o.vx/vy/vz — harmless under Jolt (readBack overwrites),
   *  replaced by applyFling in Inc 5; the value roll is the economy kept here. */
  private oreEconomyPass(o: Item): void {
    if (o.py < this.floorY(0) - 250) { o.active = false; return; } // fell off the world (updateOre:873) — Jolt's kill plane
    const CATCH = 8;
    // ── ADDRESS BY CONTACT, NOT BY CENTER TILE ────────────────────────────────────────────────────────────────
    // Ask the physics which machine this ore is actually TOUCHING (Roblox upgraders work off `Touched` for exactly
    // this reason). Deriving a tile from the ore's CENTER was the silent killer: physically-honest ore ride
    // off-center after every turn, so the grid quietly lost them — which is what made corners look broken and what
    // `beltSteer` was really propping up. A straddling ore resolves to the cell it is most on.
    const cell = o.bodyH != null ? this.jolt!.contactCell(o.bodyH) : null;
    let gx: number, gz: number, F: number, here: Placed | undefined, resting: boolean;
    if (cell) {
      // A ramp registers TWO bodies over one tile: the slope, keyed `gx,gz,floor`, and its flat crest, keyed
      // `gx,gz,floor|crest` (syncBelts). Splitting the RAW key on "," made floor = +"0|crest" = NaN and the
      // building lookup miss, so an ore standing on a crest resolved to no machine and this pass bailed out
      // treating it as airborne — measured at 1.5% of every contact sample on a single-ramp line, and the crest
      // is exactly where ore dwell longest. Address by the TILE the body belongs to, whatever it is called.
      const tile = cell.split("|")[0];
      const c = tile.split(",");
      gx = +c[0]; gz = +c[1]; F = +c[2];
      here = this.buildings.get(tile) ?? this.reserved.get(tile);
      resting = true; // in contact by definition — no height guess needed
    } else {
      // Airborne (or on something with no body, e.g. the receptacle): fall back to the positional derivation.
      gx = Math.floor((o.px - this.originX) / FACTORY.tile);
      gz = Math.floor((o.pz - this.originZ) / FACTORY.tile);
      const inZ = this.inZone(gx, gz);
      F = Math.max(0, Math.min(FACTORY.maxFloors - 1, Math.floor((o.py - this.floorY(0) - FACTORY.oreDeck + CATCH) / FACTORY.floorHeight)));
      here = inZ ? this.buildings.get(key(gx, gz, F)) : undefined;
      if (!here && inZ) { const r = this.reserved.get(key(gx, gz, F)); if (r && (r.kind === "waverider" || r.kind === "triplexer")) here = r; }
      resting = false;
    }
    o.gx = gx; o.gz = gz;
    const deckY = this.floorY(F) + FACTORY.oreDeck;
    // Sink, but NOT from below: a tall clog raises py enough for F to resolve to the floor ABOVE (measured: an
    // 11-layer pile reads as floor 1/2), and an elevated receptacle would then eat ore that never rode into it.
    if (this.sinkThroughReceptacle(o)) return; // banked only once it has ridden out over the lip, not on contact
    // On a machine's deck if the physics says we're touching it; airborne ore fall back to the height band.
    const onDeck = !!here && (resting || o.py <= deckY + CATCH);
    // splitter/pressurizer own capKey as movement/batch state — never clear it out from under them
    const captured = here?.kind === "splitter" || here?.kind === "pressurizer";
    // Release the once-per-pass guard ONLY when the ore genuinely leaves the tile that armed it. Under Jolt ore
    // jostle vertically in a clog, dropping out of the deck band and back in; clearing on "off deck" let a jammed
    // ore re-enter the SAME upgrader indefinitely (measured: 24× re-application on one magnetron, ~9× value).
    // `here.gx/gz` is the building's ANCHOR, so the waverider's far tile maps to the same owner and stays guarded.
    const ownTile = here ? here.gx + here.gz * 100 : gx + gz * 100;
    const releaseGuard = () => { if (o.capKey < 0 || o.capKey % 10000 !== ownTile) o.capKey = -1; };
    if (!onDeck) { if (!captured) releaseGuard(); return; } // airborne / off-deck (updateOre:996)
    o.floor = F; // updateOre writes this on every deck-catch (used by updateMods/cryo + part routing)
    const nk = gx + gz * 100 + F * 10000;
    const w = this.jolt!, bh = o.bodyH;
    if (o.flung && bh != null) { w.relockNoTumble(bh); o.flung = false; } // landed after a fling → stop tumbling (keeps piles flat)
    // Guide ore along the belt they're riding so corners actually turn them instead of letting momentum carry
    // them off the edge (a friction-only conveyor can't; the legacy integrator steered velocity outright).
    const steer = (globalThis as { __steer?: number }).__steer ?? PHYS.steer;
    const dsp = this.deckSpeed(here!.kind);
    if (bh != null && steer > 0 && dsp) { const [gdx, gdz] = DIR_VEC[here!.dir]; w.steerToward(bh, dsp * gdx, dsp * gdz, steer); }
    switch (here!.kind) {
      case "conveyor": releaseGuard(); break;
      // No once-per-pass guard: the cannon is not an upgrader, it is a door. Every ore that touches the deck
      // leaves the same step, which is exactly what lets one cannon swallow two belts' output.
      case "cannon": { releaseGuard(); if (bh != null) this.fireCannon(here!, o, bh, w); break; }
      case "teleporterb": releaseGuard(); break;                                                                    // the exit pad is just a belt that ore arrive on
      case "teleportera": {
        // THE JUMP. Round-robin per ENTRANCE, so 1 A + N B alternates (splitter) and N A + 1 B condenses —
        // both fall out of one cursor. Cross-FLOOR on purpose: this is the vertical transport the archived
        // lift/chute left missing. NB the same logic exists in the legacy (`?jolt=0`) ore path above; ecobox
        // and friends are duplicated the same way, and this is the copy that actually runs.
        if (o.capKey === nk) break;                       // once per pass, like every other inline machine
        o.capKey = nk;
        const exits = this.teleportExits();
        if (!exits.length) {                              // unpaired A eats the ore, so a half-built link is loud
          o.active = false;
          this.fling(this.worldX(gx), this.baseY + 44, this.worldZ(gz), 130, BUILDINGS.teleportera.accent);
          break;
        }
        const ck = key(gx, gz, F);
        const ci = (this.tpCursor.get(ck) ?? 0) % exits.length;
        this.tpCursor.set(ck, ci + 1);
        const t = exits[ci];
        const tx = this.worldX(t.gx), tz = this.worldZ(t.gz), ty = this.floorY(t.floor) + FACTORY.oreDeck;
        const [tdx, tdz] = DIR_VEC[t.dir];
        const tvx = tdx * PHYS.speed, tvz = tdz * PHYS.speed;
        // Move the BODY, not just the bookkeeping — under Jolt the body is authoritative and readInto copies
        // straight back over o.px/py/pz. setBody is the same call the ramp climb uses.
        o.px = tx; o.py = ty; o.pz = tz; o.gx = t.gx; o.gz = t.gz; o.floor = t.floor;
        o.vx = tvx; o.vy = 0; o.vz = tvz;
        o.capKey = t.gx + t.gz * 100 + t.floor * 10000;   // the exit tile counts as already used, so B can't re-fire
        // WARP THE BODY *AND* FORGET ITS CONTACT. Jolt steps at 1/30 while update() runs at 60 fps, so the frame
        // after a warp never re-runs the contact census — `contactCell` would still answer "teleporter A" while
        // the ore is already standing on the exit pad, and A fired a SECOND time. The cursor was innocent all
        // along; it simply advanced twice per ore, which with an EVEN number of exits reaches only one parity
        // class (2 pads → everything on one; 4 pads → pads 0 and 2 get literally nothing). forgetContact makes
        // that frame read as airborne, and the positional fallback resolves the freshly written exit tile.
        if (bh != null) { w.setBody(bh, tx, ty, tz, tvx, 0, tvz); w.forgetContact(bh); }
        this.fling(this.worldX(gx), this.baseY + 40, this.worldZ(gz), 90, BUILDINGS.teleportera.accent); // vanish
        this.fling(tx, ty + 24, tz, 90, BUILDINGS.teleporterb.accent);                                   // arrive
        break;
      }                                                                       // riding a belt re-arms for the next upgrader (updateOre:880)
      // TRIPLEXER (MMF Caffeinator): the L-shaped 3-tile corner. It applies no upgrade of its own — it CAFFEINATES
      // the ore, so the next `triplexerCharges` upgraders downstream each hit `triplexerBoost` harder (see caffMult).
      // capKey on the ANCHOR tile so all three of its cells count as one pass, exactly like the waverider.
      case "triplexer": {
        const tnk = here!.gx + here!.gz * 100 + F * 10000;
        here!.busy = true;                                   // drives the four violet strips (animateTriplexers)
        // `!o.caff` is load-bearing, not belt-and-braces. capKey alone re-arms on a STRADDLE: an ore bridging the
        // seam between the Triplexer's exit cell and the machine next to it flips contact cells frame to frame,
        // the machine overwrites capKey with its own tile, and the next flip back onto the Triplexer looks like a
        // fresh pass. Measured with the code path instrumented: 22 of 23 ore took FOUR boosted applications
        // instead of three, on every trial — systematic, not noise. Refusing to top up an ore that still has
        // charges pins it to exactly `triplexerCharges`, and an ore that has SPENT them (or never had any) still
        // arms normally, so looping a line back through the same Triplexer works as you'd expect.
        if (o.capKey !== tnk && !o.caff) { o.capKey = tnk; o.caff = FACTORY.triplexerCharges; }
        else if (o.capKey !== tnk) o.capKey = tnk;
        break;
      }
      case "enricher": if (o.capKey !== nk) {                                                                       // flat +value. NB there is no generic `def.proc` case in this switch — the legacy chain has one, this does not, so an upgrader without an explicit case here silently does nothing under Jolt.
        const eEff = here!.climateOK ? 1 : FACTORY.chokeEff;
        o.value += this.caffAdd(o, BUILDINGS.enricher.add! * eEff); o.capKey = nk; here!.flash = 1;
      } break;
      case "magnetron": if (o.capKey !== nk) { o.value *= this.caffMult(o, FACTORY.magnetronMult); o.elemMask |= 16; o.capKey = nk; here!.flash = 1; } break;
      case "infernace": if (o.capKey !== nk) { o.elem *= this.caffMult(o, FACTORY.infernaceFire); o.elemMask |= 1; o.capKey = nk; here!.flash = 1; } break;
      case "ecobox": if (o.capKey !== nk) {
        const grown = Math.max(0, o.value - 1);
        const boost = FACTORY.ecoboxGrowMax * grown / (grown + FACTORY.ecoboxGrowScale);
        o.value *= this.caffMult(o, 1 + boost); o.capKey = nk;
        here!.flash = Math.min(1, 0.35 + 0.65 * (boost / FACTORY.ecoboxGrowMax));
      } break;
      case "scanner": if (o.capKey !== nk) {
        o.value *= this.caffMult(o, FACTORY.scannerMult); o.capKey = nk;
        const worth = this.oreValue(o.value, o.elem, o.elemMask);
        this.recordScan(worth);
        if (this.mode === "player") this.scanLabels.spawn(o.px, deckY + 56, o.pz, worth);
        here!.flash = 1; here!.ringSnapTgt = (here!.ringSnapTgt ?? 0) + FACTORY.scannerSnap;
      } break;
      // DIVERSIFIER: PAINTS, then PAYS. It adds one element the ore is missing (MMF's "paints it random
      // colors") and then multiplies by the resulting VARIETY. Both halves matter: paying alone does nothing on
      // a world whose ore start bare (elemMask 0), which is most of them — a chain of three then multiplies by
      // exactly 1 three times. Painting is what makes chaining them the play. Once all five are on, it just pays.
      // `flash` carries the count so the R/G/B slabs light one per element (animateDiversifiers).
      case "diversifier": if (o.capKey !== nk) {
        const miss: number[] = [];
        for (let e = 0; e < 5; e++) if (!(o.elemMask & (1 << e))) miss.push(e);
        if (miss.length) o.elemMask |= 1 << miss[(Math.random() * miss.length) | 0];
        const n = popcount(o.elemMask);
        o.value *= this.caffMult(o, 1 + FACTORY.diversifierPer * n); o.capKey = nk;
        here!.flash = n / 5;                                                                                        // 0 (raw) → 1 (all five elements)
      } break;
      // MUTATOR: cheap chaos. A random slice of value and one RANDOM element — which may be one it already has,
      // where the Diversifier deliberately fills a gap. Same family, opposite temperament.
      case "mutator": if (o.capKey !== nk) {
        o.value *= this.caffMult(o, FACTORY.mutatorMin + Math.random() * (FACTORY.mutatorMax - FACTORY.mutatorMin));
        o.elemMask |= 1 << ((Math.random() * 5) | 0);
        o.capKey = nk; here!.flash = 1;
      } break;
      // LASER: precision, not power. It only pays an ore riding CENTERED in the lane — the first mechanic to read
      // the off-center riding that contact addressing made possible, so a clean straight approach is worth
      // building and a corner right before it throws ore wide and wastes it. A FIRE ore is absorbed instead:
      // the emitter banks the burn and hands it to the next ore through.
      // BOMB PACKER: arms the ore PERMANENTLY. From here it gains value every second — and its chance to
      // detonate each second climbs just as steadily (see tickBombs). Bank it quickly for a modest win, or run
      // it through more upgraders and risk losing the lot.
      case "packer": if (o.capKey !== nk) { if (o.bombT == null) o.bombT = 0; o.capKey = nk; here!.flash = 1; } break;
      // VAT: a second furnace. Pays like the receptacle, and every vatFuse ore it swallows it drops an AMALGAM
      // out of its spout carrying a slice of the pooled worth — a sink that feeds the line back into itself.
      case "vat": {
        const worth = this.oreValue(o.value, o.elem, o.elemMask);
        this.launch(worth, gx, gz);
        here!.pool = (here!.pool ?? 0) + worth;
        here!.eaten = (here!.eaten ?? 0) + 1;
        here!.flash = 1;
        if (here!.eaten >= FACTORY.vatFuse) { this.dropAmalgam(here!, here!.pool * FACTORY.vatAmalgam); here!.eaten = 0; here!.pool = 0; }
        o.active = false;
        return;
      }
      case "laser": if (o.capKey !== nk) {
        const [ldx, ldz] = DIR_VEC[here!.dir];
        const off = Math.abs((o.px - this.worldX(gx)) * -ldz + (o.pz - this.worldZ(gz)) * ldx); // distance off the center line
        o.capKey = nk;
        if (off <= FACTORY.laserWindow) {
          if (o.elemMask & 1) { o.elemMask &= ~1; here!.charge = 1; here!.flash = 1; }         // absorb the burn, bank it (no × applied ⇒ no caffeine spent)
          else { o.value *= this.caffMult(o, FACTORY.laserMult * (here!.charge ? FACTORY.laserBurn : 1)); here!.charge = 0; here!.flash = 1; }
        } else here!.flash = Math.max(here!.flash ?? 0, 0.15);                                  // a miss: barely a flicker
      } break;
      case "waverider": {
        const wnk = here!.gx + here!.gz * 100 + F * 10000;                                                          // anchor-tile key: fires once across both tiles
        here!.busy = true;
        if (o.capKey !== wnk) { o.capKey = wnk; this.rollWaverider(o, here!.dir); }
        break;
      }
      case "leech": {
        const under = F > 0 ? this.buildings.get(key(gx, gz, F - 1)) : undefined;
        if (under && isUpgraderLike(under.kind)) { here!.busy = true; if (o.capKey !== nk) { o.value *= this.caffMult(o, FACTORY.leechMult); o.capKey = nk; } }
        break;
      }
      case "splitter": {
        // Ride into the center box; once engulfed the splitter takes control, cycles its 3 outputs and ejects.
        // capKey here is ROUTING state ("captured by this splitter, driving out o.dir"), NOT an economy guard.
        if (bh == null) break;
        const cx = this.worldX(gx), cz = this.worldZ(gz);
        const kick = FACTORY.oreSpeed * FACTORY.splitterKick;
        if (o.capKey === nk) {                                                                                      // already captured → KEEP kicking until it clears the tile
          const [ex, ez] = DIR_VEC[o.dir];
          w.setVelocity(bh, kick * ex, 0, kick * ez);
        } else {
          const dcx = o.px - cx, dcz = o.pz - cz, d = Math.hypot(dcx, dcz);
          if (d < FACTORY.splitterGrab) {                                                                           // in range → take control + THROW
            const outs: Dir[] = [here!.dir, ((here!.dir + 1) % 4) as Dir, ((here!.dir + 3) % 4) as Dir];
            here!.splitIx = ((here!.splitIx ?? -1) + 1) % 3;
            o.dir = outs[here!.splitIx]; o.capKey = nk;
            const [ex, ez] = DIR_VEC[o.dir];
            // Line it up ACROSS the exit lane only — never teleport it to the tile center. Snapping every ore
            // to one point stacked them inside each other under load, and the solver's escape shove is what
            // made a busy splitter jam. Keeping its position ALONG the exit means arrivals never overlap.
            const along = (o.px - cx) * ex + (o.pz - cz) * ez;
            const nx = cx + ex * along, nz = cz + ez * along;
            w.setBody(bh, nx, deckY, nz, kick * ex, 0, kick * ez);
            o.px = nx; o.pz = nz;
          } else {                                                                                                  // still approaching → ride toward the center
            w.setVelocity(bh, (d > 0 ? -dcx / d : 0) * FACTORY.oreSpeed, 0, (d > 0 ? -dcz / d : 0) * FACTORY.oreSpeed);
          }
        }
        break;
      }
      case "pressurizer": {
        // The oven: LOAD captures ore into the sealed chamber (capKey = batch membership, the ECONOMY's own state —
        // stepPressurizer multiplies every capKey===nk member at COOK and frees them at RELEASE); COOK holds them
        // kinematically inside; RELEASE drives them out the front. Sealed/full → shove arrivals back out the entrance.
        if (bh == null) break;
        const cx = this.worldX(gx), cz = this.worldZ(gz);
        const [fx, fz] = DIR_VEC[here!.dir], lim = FACTORY.tile * 0.5 - 12;
        if (o.capKey === nk) {
          if (here!.phase === "release") {
            w.setVelocity(bh, FACTORY.oreSpeed * fx, 0, FACTORY.oreSpeed * fz);
          } else {
            // Load/cook: CONTAIN the batch, don't compress it. Dragging every held ore toward the tile center
            // piled them into each other until the solver flung them out (measured: 63% lost across this stage).
            // Just stop them and clamp anything that escapes the chamber — they settle against each other.
            const nx = Math.max(cx - lim, Math.min(cx + lim, o.px));
            const nz = Math.max(cz - lim, Math.min(cz + lim, o.pz));
            if (nx !== o.px || nz !== o.pz) { w.setBody(bh, nx, deckY, nz, 0, 0, 0); o.px = nx; o.pz = nz; }
            else w.setVelocity(bh, 0, 0, 0);
          }
        } else if (here!.phase === "load" && (here!.heldCount ?? 0) < FACTORY.pressurizerSlots) {
          if ((here!.heldCount ?? 0) === 0) here!.phaseT = FACTORY.pressurizerLoad;                                  // first ore starts the load window
          here!.heldCount = (here!.heldCount ?? 0) + 1;
          o.capKey = nk;                                                                                            // captured into this batch
          w.setVelocity(bh, FACTORY.oreSpeed * fx * 0.3, 0, FACTORY.oreSpeed * fz * 0.3);                           // drift inward
        } else {
          // Doors shut / oven full: just STOP the ore at the door. The old code teleported it ~54 units back up
          // the belt, which slammed it through whatever was queued behind and scattered the line. Ore collide for
          // real now, so a held ore is its own blocker and the queue forms behind it by itself.
          w.setVelocity(bh, 0, 0, 0);
        }
        break;
      }
      default: releaseGuard(); break;                                                                               // any other deck (drill/wall/etc.) — treat like airborne (updateOre:996)
    }
  }

  /** The height ore RIDE at on a given cell, or null if that cell carries no deck. Flat decks sit at the floor's
   *  deck height; a ramp cell sits partway up its slope, which is why "is there a deck next door" is not the same
   *  question as "can an ore cross to it". */
  /** Surface height of ramp `r` at `along` units from its back edge — THE one source for every ramp height.
   *  MUST match the bodies syncBelts registers: a crest shortens the slope to `run` and then runs FLAT.
   *  Modeling a plain 3-tile climb here while the body crests is what broke ramps twice — the crest cell's
   *  curbs get seated ~17 units off, ore walk straight over them, and nothing anywhere reports a problem.
   *  Anything needing a ramp height asks this; three independent copies of one height is the recurring bug. */
  private rampSurfaceY(r: Placed, along: number): number {
    const crest = PHYS.rampCrest * FACTORY.tile, run = 3 * FACTORY.tile - crest;
    return this.floorY(r.floor) + FACTORY.oreDeck + Math.min(1, along / run) * FACTORY.floorHeight;
  }

  /** Which of a ramp's 3 cells this one is (0 = back edge). */
  private rampStep(gx: number, gz: number, r: Placed): number {
    const [dx, dz] = DIR_VEC[r.dir];
    return (gx - r.gx) * dx + (gz - r.gz) * dz;
  }

  /** The LOW and HIGH surface heights ACROSS a cell. A flat deck has lo === hi; a ramp cell climbs ~35 units
   *  between them. Curbs seat from this rather than from the center height: a curb sized to the center stands
   *  the intended 4 units proud there but ends up ~13 units BELOW the surface at the cell's top edge, so the
   *  upper half of every ramp cell had no working barrier and off-center ore simply left the belt. */
  private cellDeckSpan(gx: number, gz: number, floor: number): { lo: number; hi: number } | null {
    const r = this.rampCells.get(key(gx, gz, floor));
    if (r) {
      const s = this.rampStep(gx, gz, r);
      return { lo: this.rampSurfaceY(r, s * FACTORY.tile), hi: this.rampSurfaceY(r, (s + 1) * FACTORY.tile) };
    }
    const y = this.cellDeckY(gx, gz, floor);
    return y == null ? null : { lo: y, hi: y };
  }

  private cellDeckY(gx: number, gz: number, floor: number): number | null {
    const k = key(gx, gz, floor);
    const r = this.rampCells.get(k);
    if (r) return this.rampSurfaceY(r, (this.rampStep(gx, gz, r) + 0.5) * FACTORY.tile); // this cell's CENTER
    const b = this.buildings.get(k) ?? this.reserved.get(k);
    if (b && (this.deckSpeed(b.kind) !== null || b.kind === "ramp")) return this.floorY(floor) + FACTORY.oreDeck;
    return null;
  }

  /** Surface speed of a building's deck, or null if ore don't rest on it at all. A DRIVEN deck (oreSpeed) carries
   *  ore along `dir`; an INERT deck (0) just holds them up while the tile's own scripted logic moves them
   *  (splitter routing, pressurizer chamber). Ramps are the kinematic climb (Inc 4), so no body. */
  private deckSpeed(kind: BuildingKind): number | null {
    if (kind === "conveyor" || kind === "magnetron" || kind === "infernace" || kind === "ecobox" ||
        kind === "scanner" || kind === "waverider" || kind === "leech" || kind === "diversifier" || kind === "mutator" || kind === "laser" || kind === "packer" ||
        kind === "triplexer" || kind === "teleportera" || kind === "teleporterb" ||
        kind === "receptacle" || kind === "enricher" || kind === "cannon") return PHYS.speed;   // DRIVES: ore ride across it
    // Inert decks: ore REST on them and are addressed by contact. The receptacle needs one too — without a body
    // an ore stays in contact with the belt behind it right up until it falls past the sink, so it never gets
    // addressed to the furnace at all.
    if (kind === "splitter" || kind === "pressurizer" || kind === "vat") return 0;
    return null;
  }

  /** Phase 1 (flag ON): reconcile the Jolt static belt bodies to the current layout — one surface-velocity slab per
   *  belt-deck cell (the 2-tile waverider gets ONE slab spanning anchor + its forward reserved cell). Idempotent +
   *  diff-based, so it's a no-op once stable and only touches Jolt on a real place/bulldoze. */
  private syncBelts(): void {
    const w = this.jolt;
    if (!w) return;
    const desired = new Set<string>();
    for (const b of this.buildings.values()) {
      if (b.kind === "ramp") {
        // A ramp is a REAL inclined conveyor: one sloped slab spanning its 3 cells, rising a full floor, with the
        // surface velocity pointing up the slope. Ore climb it by friction and are free to queue, pile or spill —
        // no scripted rail. Run spans the anchor's back edge (-½ tile) to the far edge of the 3rd cell (+2½).
        const rk = key(b.gx, b.gz, b.floor);
        const crest = PHYS.rampCrest * FACTORY.tile;
        desired.add(rk);
        // BOTH keys must be declared here. Declaring the crest inside a block that only ran on first creation
        // meant every later sync saw it as unwanted and deleted it — the slab existed for one frame, then ore
        // ran off the end of a slope the crest had shortened. Half of why ramps broke twice.
        if (crest > 0) desired.add(rk + "|crest");
        const [dx, dz] = DIR_VEC[b.dir];
        // A real conveyor ramp tops out with a flat CREST before anything else acts on the ore. Ours climbed to
        // the very last millimeter, so ore arrived at the mouth still ascending — and a belt turning them there
        // shoved them back down the slope (measured: 55% of ore at a ramp top fell straight back onto the ramp,
        // ping-ponging). Finish the rise early and run out flat, so ore level off before the next tile takes over.
        const run = 3 * FACTORY.tile - crest, rise = FACTORY.floorHeight;
        const slope = Math.hypot(run, rise), ct = run / slope, st = rise / slope, th = Math.atan2(rise, run);
        // midpoint of the SLOPE along the run. The run starts at the anchor's back edge (-tile/2) and is `run`
        // long, so its center sits at (run/2 - tile/2) — this must track `run`, which the crest shortens.
        const mid = run / 2 - FACTORY.tile / 2;
        const sx = this.worldX(b.gx) + dx * mid, sz = this.worldZ(b.gz) + dz * mid;
        const sy = this.floorY(b.floor) + FACTORY.oreDeck + rise / 2;
        // Orientation = YAW the slab's long axis onto the travel direction, THEN tilt it up the slope. (Yaw alone
        // was missing at first, so a +Z ramp's slab lay crosswise and ore fell straight through it.)
        const psi = Math.atan2(-dz, dx);
        const sy2 = Math.sin(psi / 2), cy2 = Math.cos(psi / 2), st2 = Math.sin(th / 2), ct2 = Math.cos(th / 2);
        w.registerRamp(rk, sx, sy, sz, -dx * st, ct, -dz * st,
                       sy2 * st2, sy2 * ct2, cy2 * st2, cy2 * ct2,
                       slope / 2, FACTORY.tile / 2, PHYS.speed * dx * ct, PHYS.speed * st, PHYS.speed * dz * ct);
        if (crest > 0) { // flat run-out at the top deck, driving straight ahead
          const topY = this.floorY(b.floor) + FACTORY.oreDeck + rise;
          const cxm = this.worldX(b.gx) + dx * (run - FACTORY.tile / 2 + crest / 2);
          const czm = this.worldZ(b.gz) + dz * (run - FACTORY.tile / 2 + crest / 2);
          w.registerBelt(rk + "|crest", cxm, topY, czm, b.dir,
                         dx !== 0 ? crest / 2 : FACTORY.tile / 2, dz !== 0 ? crest / 2 : FACTORY.tile / 2, PHYS.speed);
          desired.add(rk + "|crest");
        }
        continue;
      }
      const speed = this.deckSpeed(b.kind);
      if (speed === null) continue;
      const k = key(b.gx, b.gz, b.floor);
      desired.add(k);
      // TRIPLEXER IS HANDLED BEFORE THE `hasBelt` EARLY-OUT, and that placement is load-bearing. It owns THREE
      // cells, so all three have to be re-added to `desired` on EVERY sync — the sweep at the bottom of this
      // function unregisters any belt not in `desired`. Sitting after the early-out, the anchor existing was
      // enough to skip the branch, so the corner and exit were registered once and then deleted on the very
      // next sync, and ore rode straight off the end of the entry tile. This is the second time that exact
      // ordering has bitten: the ramp's flat crest had the same bug for the same reason.
      if (b.kind === "triplexer") {
        // L-SHAPED: three SEPARATE 1-tile decks, because unlike every other multi-tile building the ore has to
        // CHANGE DIRECTION inside it. One straight along `dir`, then two turned 90° that carry it out the side.
        // The waverider's trick (one double-length slab) cannot express a corner — a slab has ONE surface
        // velocity, so a turn needs one deck per heading.
        const deckY3 = this.floorY(b.floor) + FACTORY.oreDeck;
        const [dx3, dz3] = DIR_VEC[b.dir];
        const turn = ((b.dir + 1) % 4) as Dir;            // +perp — matches FOOTPRINT [[1,0],[1,1]]
        const [tx3, tz3] = DIR_VEC[turn];
        const cells: [number, number, Dir][] = [
          [b.gx, b.gz, b.dir],                              // entry — straight on
          [b.gx + dx3, b.gz + dz3, turn],                   // corner — turns
          [b.gx + dx3 + tx3, b.gz + dz3 + tz3, turn],       // exit — carries on out
        ];
        for (const [cgx, cgz, cdir] of cells) {
          const ck = key(cgx, cgz, b.floor);
          desired.add(ck);
          w.registerBelt(ck, this.worldX(cgx), deckY3, this.worldZ(cgz), cdir, 50, 50, speed);
        }
        continue;
      }
      const deckY = this.floorY(b.floor) + FACTORY.oreDeck;
      if (b.kind === "waverider") {
        const [dx, dz] = DIR_VEC[b.dir];
        const cx = (this.worldX(b.gx) + this.worldX(b.gx + dx)) / 2, cz = (this.worldZ(b.gz) + this.worldZ(b.gz + dz)) / 2;
        w.registerBelt(k, cx, deckY, cz, b.dir, dx !== 0 ? FACTORY.tile : FACTORY.tile / 2, dz !== 0 ? FACTORY.tile : FACTORY.tile / 2, speed);
      } else {
        w.registerBelt(k, this.worldX(b.gx), deckY, this.worldZ(b.gz), b.dir, 50, 50, speed);
      }
    }
    for (const k of w.beltKeys()) if (!desired.has(k)) w.unregisterBelt(k);

    // CURBS — the MMF answer to ore falling off. Miner's Haven makes "Conveyor Has Walls?" a per-item stat and
    // walled belts are the norm; berezaa twice had to LOWER them so adjacent conveyors could still transition on.
    // We get that for free by only curbing an OPEN edge: a lane side with no neighboring deck. Where two belts
    // meet there's no curb at all, so corners and merges stay unobstructed, but a lane with a drop beside it is
    // fenced. Ore can still be flung clear over one — it retains drift, it doesn't imprison.
    const wantCurbs = new Set<string>();
    if (PHYS.curb > 0) {
      for (const b of this.buildings.values()) {
        if (this.deckSpeed(b.kind) === null && b.kind !== "ramp") continue;
        const footY = this.floorY(b.floor) + FACTORY.oreDeck;
        // A ramp RISES, so each of its 3 cells needs its curb at THAT cell's surface height — using the foot
        // height for all three buries the upper two below the slope and ore sail straight over them.
        // Ask cellDeckY rather than re-deriving the climb: this used to inline its own copy of the formula, so
        // when the crest changed the real slope, curbs kept being seated for the old one and ore walked over
        // them. Three independent copies of one height is what made every ramp bug invisible.
        // EVERY DECK CELL, not just the anchor. This used to be `[[b.gx, b.gz, footY]]` for anything that isn't a
        // ramp, so a multi-tile building's other cells (which live in `this.reserved`) got NO curbs at all — the
        // Triplexer's corner and exit were open on both sides and, under back-pressure, ~53% of its ore spilled
        // off them (measured 49-54 lost vs 0-7 for an equivalent hand-built conveyor L, which does get curbed).
        // Each cell carries its OWN heading too, because the Triplexer's corner and exit run at (dir+1)%4 — using
        // the anchor's heading would curb the wrong pair of edges and leave the outside of the turn open anyway.
        const turnDir = ((b.dir + 1) % 4) as Dir;
        const cells: Array<[number, number, number, Dir]> = b.kind === "ramp"
          ? [0, 1, 2].map((s) => {
              const cgx = b.gx + s * DIR_VEC[b.dir][0], cgz = b.gz + s * DIR_VEC[b.dir][1];
              return [cgx, cgz, this.cellDeckY(cgx, cgz, b.floor) ?? footY, b.dir] as [number, number, number, Dir];
            })
          : b.kind === "triplexer"
            ? [[b.gx, b.gz, footY, b.dir],                                                                              // entry — straight on
               [b.gx + DIR_VEC[b.dir][0], b.gz + DIR_VEC[b.dir][1], footY, turnDir],                                    // corner — turns
               [b.gx + DIR_VEC[b.dir][0] + DIR_VEC[turnDir][0], b.gz + DIR_VEC[b.dir][1] + DIR_VEC[turnDir][1], footY, turnDir]] // exit
            : b.kind === "waverider"
              ? [[b.gx, b.gz, footY, b.dir], [b.gx + DIR_VEC[b.dir][0], b.gz + DIR_VEC[b.dir][1], footY, b.dir]]
              : [[b.gx, b.gz, footY, b.dir]];
        for (const [cgx, cgz, deckY, cdir] of cells) {
          for (const side of [1, 3]) {                                   // the two lateral edges of this lane
            const L = ((cdir + side) % 4) as Dir, [lx, lz] = DIR_VEC[L];
            // Leave an edge OPEN only where ore can genuinely cross: a neighboring deck at (roughly) THIS height.
            // Mere adjacency isn't enough — two ramps running side by side are neighbors whose surfaces can be a
            // whole floor apart, and treating that as "open" drops ore down the gap between them.
            const ny = this.cellDeckY(cgx + lx, cgz + lz, b.floor);
            // ...but never leave it open to a lane running the OTHER WAY. Two antiparallel ramps side by side sit
            // at matching heights where they cross, so ore leaked straight onto the opposing belt. A perpendicular
            // neighbor is a corner/merge and must stay open; only a head-on lane gets a divider.
            const nb = this.rampCells.get(key(cgx + lx, cgz + lz, b.floor)) ?? this.buildings.get(key(cgx + lx, cgz + lz, b.floor));
            const opposed = !!nb && (nb.dir + 2) % 4 === cdir;
            if (ny != null && !opposed && Math.abs(ny - deckY) <= 20) continue;
            // ...and a RAMP one floor down delivers ore INTO this edge, so it must stay open too. Curbing it walls
            // off the ramp's exit — exactly the transition problem berezaa hit when MH's conveyor walls blocked
            // adjacent belts (he had to lower them twice).
            const below = b.floor > 0 ? this.cellDeckY(cgx + lx, cgz + lz, b.floor - 1) : null;
            if (below != null && Math.abs(below - deckY) <= 20) continue;
            // NOTE: dropping the curb where a player WALL already covers the edge (the two sit 1 unit apart)
            // was tried and MEASURED WORSE — pinned ore fell 5.4%→3.8% but the remaining pins lasted twice as
            // long (17.8s→36.8s) and deliveries collapsed (36→6). The duplicate barrier is not the cause; an
            // ore left on the wall alone pins harder. Do not re-try this without re-measuring.
            const ck = `curb:${cgx},${cgz},${b.floor}|${L}`;
            wantCurbs.add(ck);
            if (w.hasWall(ck)) continue;
            const h = FACTORY.tile / 2;
            // A curb must never poke up into a lane running ABOVE it. A ramp's top cell climbs to within an ore
            // radius of the next floor's deck, so its curb was standing proud of that floor's belt surface and
            // ore riding past clipped a corner on an obstacle that is invisible and belongs to another story.
            // (It cleared by 0.67 units before the crest existed — latent, not new, but the crest exposed it.)
            // Seat from the cell's LOW edge and carry the top to its HIGH edge, so the curb stands the same
            // 4 units proud of the riding surface everywhere across the cell. Sizing it to the center height
            // (which is what this did) leaves it buried ~13 units UNDER the surface over a ramp cell's top
            // half — measured on a live ramp: curb top 251.5 against a surface reaching 265. Flat decks have
            // lo === hi and come out byte-identical to before.
            const span = this.cellDeckSpan(cgx, cgz, b.floor) ?? { lo: deckY, hi: deckY };
            const base = span.lo - 12;
            let top = span.hi + (PHYS.curb - 12);
            for (let up = b.floor + 1; up < FACTORY.maxFloors; up++) {
              for (const [ax, az] of [[0, 0], [lx, lz]]) {
                const over = this.cellDeckY(cgx + ax, cgz + az, up);
                // ...but a ramp's TOP cell climbs to MEET the next floor's deck, so that deck is this same lane
                // continuing, not a story overhead. Ducking under it buried the crest curb 13 units below the
                // ore's own riding surface — the one place ore are slowest and most likely to drift off. Only
                // clamp under a lane genuinely running above this cell's high edge; at the crest the curb lands
                // exactly where that floor's own curb would sit anyway, so it adds no new obstacle.
                if (over != null && over - span.hi > 20) top = Math.min(top, over - PHYS.half - 1);
              }
            }
            if (top - base < 2) continue;                                    // no room left for a useful curb
            w.registerWall(ck, this.worldX(cgx) + lx * (h - 3), (base + top) / 2, this.worldZ(cgz) + lz * (h - 3),
                           lx !== 0 ? 3 : h, (top - base) / 2, lz !== 0 ? 3 : h);
          }
        }
      }
    }
    for (const k of w.wallKeys()) if (k.startsWith("curb:") && !wantCurbs.has(k)) w.unregisterWall(k);

    // WALLS: one static slab per walled tile edge. The legacy integrator faked these with a px/pz clamp inside
    // updateOre; as real bodies they contain the whole pile (this is what makes walls actually matter).
    const wantWalls = new Set<string>();
    for (const arr of this.walls.values()) {
      for (const b of arr) {
        const wk = `${b.gx},${b.gz},${b.floor}|${b.dir}`;
        wantWalls.add(wk);
        if (w.hasWall(wk)) continue;
        const [dx, dz] = DIR_VEC[b.dir], deckY = this.floorY(b.floor) + FACTORY.oreDeck, h = FACTORY.tile / 2;
        w.registerWall(wk, this.worldX(b.gx) + dx * h, deckY + 30, this.worldZ(b.gz) + dz * h,
                       dx !== 0 ? 5 : h, 50, dz !== 0 ? 5 : h); // thin across the edge, full tile along it, tall enough to hold a pile
      }
    }
    for (const k of w.wallKeys()) if (!k.startsWith("curb:") && !wantWalls.has(k)) w.unregisterWall(k);
  }


  /** Push overlapping ores apart (horizontally) so drops back up into clogs instead of stacking. */
  private separateOres(): void {
    const h = this._oreHash; h.clear();
    for (const o of this.items) {
      if (!o.active || o.part) continue;
      const k = key(o.gx, o.gz, o.floor);
      let arr = h.get(k); if (!arr) { arr = []; h.set(k, arr); } arr.push(o);
    }
    const sep = FACTORY.oreSep, sep2 = sep * sep;
    for (const o of this.items) {
      if (!o.active || o.part) continue;
      for (let ax = -1; ax <= 1; ax++) for (let az = -1; az <= 1; az++) {
        const arr = h.get(key(o.gx + ax, o.gz + az, o.floor)); if (!arr) continue;
        for (const p of arr) {
          if (p === o || !p.active) continue;
          const dx = o.px - p.px, dz = o.pz - p.pz, d2 = dx * dx + dz * dz;
          if (d2 < sep2 && d2 > 0.01) {
            const d = Math.sqrt(d2), push = (sep - d) * 0.5, nx = dx / d, nz = dz / d;
            o.px += nx * push; o.pz += nz * push;
            p.px -= nx * push; p.pz -= nz * push;
            // bleed off the approaching velocity so a jammed pile packs (touches) instead of ramming/overlapping
            const relv = (o.vx - p.vx) * nx + (o.vz - p.vz) * nz;
            if (relv < 0) { o.vx -= nx * relv * 0.5; o.vz -= nz * relv * 0.5; p.vx += nx * relv * 0.5; p.vz += nz * relv * 0.5; }
          }
        }
      }
    }
  }

  private neighbor(b: Placed): Placed | undefined {
    const [dx, dz] = DIR_VEC[b.dir];
    return this.buildings.get(key(b.gx + dx, b.gz + dz, b.floor));
  }

  private spawn(part: boolean, gx: number, gz: number, floor: number, dir: Dir, value: number, tier: number): void {
    for (const o of this.items) {
      if (o.active) continue;
      o.active = true; o.part = part; o.gx = gx; o.gz = gz; o.floor = floor; o.dir = dir; o.t = 0; o.tier = tier; o.applied = false; o.capKey = -1; o.uid = ++this.oreUid;
      // EVERY per-ore field is reset here. The pool recycles slots and survives importCode/rebuildPhysics, so a
      // field left over from the previous occupant leaks into the next ore (a leaked bombT once armed 100% of ore
      // at birth). `caff` is the Triplexer's charge counter and belongs to exactly this list.
      o.qx = 0; o.qy = 0; o.qz = 0; o.qw = 1; o.bombT = undefined; o.caff = 0;
      if (part) { o.value = value; o.elem = 1; o.elemMask = 0; }
      else { o.value = this.oreStartValue; o.elemMask = this.oreStartMask; o.elem = this.oreStartMask ? 1.15 : 1; } // planet flavor
      return;
    }
  }

  /** Final Receptacle payout for an ore: raw value × elemental factor × (diversity + combo bonuses). Routing
   *  ore through a VARIETY of elements (and matched pairs) is worth more than stacking one — the P2 puzzle. */
  private oreValue(value: number, elem: number, elemMask: number): number {
    let bonus = 1 + FACTORY.diversity * popcount(elemMask);
    if ((elemMask & 3) === 3) bonus += FACTORY.combo;    // fire + ice → steam
    if ((elemMask & 12) === 12) bonus += FACTORY.combo;  // electric + plant → growth
    if ((elemMask & 20) === 20) bonus += FACTORY.combo;  // electric + magnetized → electromagnetic
    return value * elem * bonus;
  }

  private launch(value: number, gx: number, gz: number): void {
    const gain = Math.max(1, Math.round(value));
    const before = this.launched;
    this.materials += gain;
    this.launched += gain;
    this.fling(this.worldX(gx), this.baseY + 120, this.worldZ(gz), 900 + value * 4, 0xffce8a);
    if (this.mode === "player") this.checkUnlocks(before, this.launched); // P5: crossing a milestone opens a "pack"
  }

  /** BLASTER fires: any ore riding the beam tiles (1..blasterRange forward, on the blaster's floor, on the center-
   *  line) gets the FIRE bit + a small fire-factor nudge (accrues as it rides through); a red muzzle burst + neon pop
   *  mark the shot. Ore doesn't route THROUGH the blaster — you sit it beside a belt and the beam imbues the belt. */
  private blasterBlast(b: Placed): void {
    b.flash = 1;
    // MMF interface: a Cryo cooler capping the blaster RETOOLS the beam — same magnitude, but ICE (bit 2) not FIRE (bit 1).
    const iced = this.mods.get(key(b.gx, b.gz, b.floor))?.kind === "cryo";
    const bit = iced ? 2 : 1, fac = iced ? FACTORY.cryoBlasterIce : FACTORY.blasterFire, burst = iced ? 0x59d5ff : 0xff2a2a;
    const [dx, dz] = DIR_VEC[b.dir], R = FACTORY.blasterRange;
    for (const o of this.items) {
      if (!o.active || o.part || o.floor !== b.floor) continue;
      const along = (o.gx - b.gx) * dx + (o.gz - b.gz) * dz;   // steps forward along the beam
      const side = (o.gx - b.gx) * -dz + (o.gz - b.gz) * dx;   // 0 = on the beam center-line
      if (side === 0 && along >= 1 && along <= R) { o.elemMask |= bit; o.elem *= fac; } // fire (bit 1) or, if capped, ice (bit 2)
    }
    this.fling(this.worldX(b.gx + dx), this.floorY(b.floor) + 44, this.worldZ(b.gz + dz), 70, burst); // muzzle burst (blue when iced)
  }

  /** Per-frame blaster visuals: the INNER barrel grows forward with charge (reaching toward the ore), the OUTER
   *  shell brightens, and a blast pops both neon-bright for a moment (the "red light flash"). */
  private animateBlasters(dt: number): void {
    for (const b of this.buildings.values()) {
      if (b.kind !== "blaster") continue;
      const charge = 1 - Math.max(0, b.cooldown) / FACTORY.blasterCharge; // 0..1
      b.flash = Math.max(0, (b.flash ?? 0) - dt / 0.14);                  // decay the shot pop
      const neon = this.mods.get(key(b.gx, b.gz, b.floor))?.kind === "cryo" ? 0x59d5ff : 0xff2a2a; // capped by a cryo ⇒ ice-blue beam
      const inner = b.mesh?.getObjectByName("Part1") as THREE.Mesh | undefined;
      const outer = b.mesh?.getObjectByName("Union1") as THREE.Mesh | undefined;
      if (inner) {
        if (!inner.geometry.boundingBox) inner.geometry.computeBoundingBox();
        const bb = inner.geometry.boundingBox!, s = 0.35 + charge * 1.15;
        // grow the barrel FROM ITS BACK EDGE forward toward the item: anchor min-z (back), extend toward +z (muzzle).
        // never scales past the back / clips into the body — only the muzzle end reaches out with charge.
        inner.scale.z = s;
        inner.position.z = bb.min.z * (1 - s);
        const mt = inner.material as THREE.MeshStandardMaterial; mt.emissive.setHex(neon); mt.emissiveIntensity = 0.6 + charge * 0.8 + b.flash * 2.5;
      }
      // brighter emissive with charge/flash → the selective-bloom pass haloes it more (genuine light bleed).
      if (outer) { const mt = outer.material as THREE.MeshStandardMaterial; mt.emissive.setHex(neon); mt.emissiveIntensity = 0.5 + charge * 0.9 + b.flash * 2.5; }
    }
  }

  /** Per-frame cryo FLYWHEEL visuals. The top wheel (`Union3`) free-spins FAST when idle (showing it isn't cooling
   *  anything) and is DRAINED — slowed, but never below `cryoTopMin` — while it cools ore, as if the flywheel's
   *  stored energy is being spent on the work; it spins back up once idle. The blue-neon bottom wheel (`Union2`)
   *  spins up (counter-rotating) while cooling and pops its glow on each cooling pulse. */
  /** Rewrite one instanced cryo WHEEL's transform = tile position + (DIR_YAW + accumulated spin) about vertical Y.
   *  The wheels are centered on the tile axis, so a Y-rotation spins them in place. */
  private setWheelMatrix(m: Placed, part: string, spin: number): void {
    if (!m.inst) return;
    this.tileCenter(m.gx, m.gz, m.floor, this._c);
    this._q.setFromAxisAngle(this._yAxis, DIR_YAW[m.dir] + spin);
    this._animMat.compose(this._c, this._q, this._one);
    this.instancer.setMatrix(m.inst.key, m.inst.slot, part, this._animMat);
  }

  private animateCryos(dt: number): void {
    for (const m of this.mods.values()) {
      if (m.kind !== "cryo" || !m.inst) continue;
      m.rotTop = m.rotTop ?? 0; m.rotBot = m.rotBot ?? 0;
      const host = this.buildings.get(key(m.gx, m.gz, m.floor));
      if (host && host.kind === "blaster") {
        // ON A BLASTER the flywheel serves the GUN, not ore throughput. The spinner (Union3) winds UP with the beam
        // charge and RELEASES to 0 on each shot (powering the blast), winding fully back up by the next shot. The blue
        // ring's glow is handed over to the blaster's STATIC-shell neon logic (brighten with charge, pop on shot) —
        // matching animateBlasters' Union1 formula exactly, so ring + shell pulse as one. The ring itself doesn't spin.
        const charge = Math.min(1, Math.max(0, 1 - Math.max(0, host.cooldown) / FACTORY.blasterCharge));
        const tw = charge * FACTORY.cryoTopMax;
        m.spinTop = tw; m.spinBot = 0; m.rotTop += tw * dt;
        this.setWheelMatrix(m, "Union3", m.rotTop);
        this.instancer.setEmissive(m.inst.key, m.inst.slot, "Union2", 0.5 + charge * 0.9 + (host.flash ?? 0) * 2.5);
        continue;
      }
      const frac = Math.min(1, (m.load ?? 0) / FACTORY.cryoLoadMax); // 0 idle .. 1 at ~12-dropper throughput (fully drained)
      // TOP flywheel: target speed falls cryoTopMax→cryoTopMin as throughput rises; approached with ASYMMETRIC accel —
      // drain FAST when over-speed (you see the energy get taken on a busy belt), climb back up SLOWLY when quiet.
      const target = FACTORY.cryoTopMax - frac * (FACTORY.cryoTopMax - FACTORY.cryoTopMin);
      let tw = m.spinTop ?? FACTORY.cryoTopMax;
      const dv = (tw > target ? FACTORY.cryoDrainAccel : FACTORY.cryoRecoverAccel) * dt;
      tw += Math.abs(target - tw) <= dv ? target - tw : Math.sign(target - tw) * dv;
      // BOTTOM neon wheel: turns with ACTIVITY — at least cryoBotMin on any ore, scaling to cryoBotMax at full load.
      const botTarget = m.busy ? FACTORY.cryoBotMin + frac * (FACTORY.cryoBotMax - FACTORY.cryoBotMin) : 0;
      const bw = (m.spinBot ?? 0) + (botTarget - (m.spinBot ?? 0)) * Math.min(1, FACTORY.cryoBotResponse * dt);
      m.spinTop = tw; m.spinBot = bw;
      m.rotTop += tw * dt; m.rotBot -= bw * dt;                                   // Union2 counter-rotates for a mechanical read
      this.setWheelMatrix(m, "Union3", m.rotTop);
      this.setWheelMatrix(m, "Union2", m.rotBot);
      m.flash = Math.max(0, (m.flash ?? 0) - dt / 0.2);                           // decay the cooling-pulse pop
      this.instancer.setEmissive(m.inst.key, m.inst.slot, "Union2", 0.5 + (bw / FACTORY.cryoBotMax) * 0.7 + (m.flash ?? 0) * 1.8);
    }
  }

  /** Pressurizer (MMF Oven) batch cycle. LOAD: entrance open, ore piles into the sealed chamber, load window counts
   *  from the first ore. Window expires (with ore) → doors SLAM shut → COOK (5s) applies the value ×to the batch →
   *  RELEASE: exit opens, the batch ejects → LOAD. An empty oven just keeps its entrance open, waiting. */
  private stepPressurizer(b: Placed, dt: number): void {
    if (b.phase === undefined) { b.phase = "load"; b.phaseT = FACTORY.pressurizerLoad; b.heldCount = 0; } // lazy init
    const nk = b.gx + b.gz * 100 + b.floor * 10000;
    // COUNT THE BATCH, don't tally it. heldCount used to be incremented on capture and reset only at the end of
    // a cycle, so every ore that left the chamber, was destroyed, or was flung out still counted — the oven
    // filled up with ghosts, `heldCount < slots` stopped being true, and arrivals were held at the door FOREVER
    // on an empty oven that was sitting in LOAD. (Measured: cooking with held=1 and nothing inside, and ore
    // parked on the tile never admitted.) Membership already lives on the ore as capKey; this just reads it.
    let held = 0;
    for (const o of this.items) if (o.active && !o.part && o.capKey === nk) held++;
    b.heldCount = held;
    b.phaseT = (b.phaseT ?? 0) - dt;
    if (b.phase === "load") {
      if (b.phaseT <= 0) {
        if ((b.heldCount ?? 0) > 0) { b.phase = "cook"; b.phaseT = FACTORY.pressurizerCook / (b.climateOK ? 1 : FACTORY.chokeEff); } // slam shut → cook
        else b.phaseT = FACTORY.pressurizerLoad; // empty → keep the entrance open, keep waiting
      }
    } else if (b.phase === "cook") {
      if (b.phaseT <= 0) {
        for (const o of this.items) if (o.active && !o.part && o.capKey === nk) o.value *= this.caffMult(o, FACTORY.pressurizerMult); // pressurize the whole batch (per-ore caffeine)
        b.phase = "release"; b.phaseT = FACTORY.pressurizerRelease;
      }
    } else { // release
      if (b.phaseT <= 0) {
        for (const o of this.items) if (o.active && !o.part && o.capKey === nk) o.capKey = -1; // free any stragglers still inside
        b.phase = "load"; b.phaseT = FACTORY.pressurizerLoad; b.heldCount = 0;
      }
    }
  }

  /** Per-frame pressurizer door visuals: the entrance (Part1) is open only while LOADING, the exit (Part2) only while
   *  RELEASING; both sealed while cooking. Each door retracts by scaling to ~0 along its long axis, anchored at one
   *  end (the "sliding door" look); closing is faster than opening so a cook start reads as a SLAM. */
  private animatePressurizers(dt: number): void {
    for (const b of this.buildings.values()) {
      if (b.kind !== "pressurizer" || !b.mesh) continue;
      const rate = (cur: number, tgt: number): number => cur + (tgt - cur) * Math.min(1, (tgt < cur ? 15 : 6) * dt); // close fast (slam), open slower
      b.doorEnt = rate(b.doorEnt ?? 1, b.phase === "load" ? 1 : 0);
      b.doorExit = rate(b.doorExit ?? 0, b.phase === "release" ? 1 : 0);
      this.setDoor(b.mesh.getObjectByName("Part1") as THREE.Mesh | undefined, b.doorEnt);
      this.setDoor(b.mesh.getObjectByName("Part2") as THREE.Mesh | undefined, b.doorExit);
    }
  }

  /** Retract a door panel by its open-fraction: scale its LONGER horizontal axis toward ~0, anchored at the min end. */
  private setDoor(d: THREE.Mesh | undefined, open: number): void {
    if (!d) return;
    if (!d.geometry.boundingBox) d.geometry.computeBoundingBox();
    const bb = d.geometry.boundingBox!, s = 1 - (open ?? 0) * 0.95; // open 0→full panel (scale 1), 1→retracted (scale 0.05)
    if (bb.max.x - bb.min.x >= bb.max.z - bb.min.z) { d.scale.x = s; d.position.x = bb.min.x * (1 - s); }
    else { d.scale.z = s; d.position.z = bb.min.z * (1 - s); }
  }

  /** Place the magnetron's hovering disk (both of its parts) at height `y`, spun `rot` radians about its own axis.
   *  The pool stamps ONE tile matrix on every part, so the hover is the building's own transform with the offset
   *  RIGHT-multiplied in — model space, like the infernace's stamp. (The disk is centered on the tile axis, so the
   *  spin folds into the tile yaw exactly as `setWheelMatrix` does for the cryo's wheels.) */
  private setDiskMatrix(b: Placed, y: number, rot: number): void {
    if (!b.inst) return;
    this.tileCenter(b.gx, b.gz, b.floor, this._c);
    this._c.y += y;
    this._q.setFromAxisAngle(this._yAxis, DIR_YAW[b.dir] + rot);
    this._animMat.compose(this._c, this._q, this._one);
    for (const p of MAGNETRON.parts) this.instancer.setMatrix(b.inst.key, b.inst.slot, p.name, this._animMat);
  }

  /** Per-frame magnetron visuals: the hovering disk SPINS (faster for a moment after each ore pass) and BOBS
   *  vertically (bottom never below `magnetronMinY`); normally no glow. BUT if a CRYO caps the same tile, the disk
   *  spins WITH the cooler's flywheel and its brown-copper inner (`Union2`) is taken over by the cryo's BLUE NEON.
   *  Instanced: height+spin are a per-instance matrix and the blue is a per-instance emissive COLOR (the disk is
   *  the one part whose neon hue differs per building), so 150 magnetrons still cost one draw call per part. */
  private animateMagnetrons(dt: number): void {
    for (const b of this.buildings.values()) {
      if (b.kind !== "magnetron" || (!b.mesh && !b.inst)) continue;
      const cryo = this.mods.get(key(b.gx, b.gz, b.floor));       // a cryo cooler capping this magnetron?
      const capped = cryo?.kind === "cryo";
      let y: number;
      if (capped) {
        // sync to the cryo's BLUE wheel (Union2 = spinBot, same counter-rotation) and sit STEADY just below it — no
        // bob, so the disk can't climb into the cooler's wheels.
        b.rotDisk = (b.rotDisk ?? 0) - (cryo.spinBot ?? 0) * dt;
        y = FACTORY.magnetronCapY;
      } else {
        // own idle spin + per-ore kick + vertical bob (bottom never below magnetronMinY)
        b.flash = Math.max(0, (b.flash ?? 0) - dt / 0.5);
        b.rotDisk = (b.rotDisk ?? 0) + (FACTORY.magnetronSpin + (b.flash ?? 0) * FACTORY.magnetronSpinKick) * dt;
        b.bobT = (b.bobT ?? 0) + FACTORY.magnetronBobRate * dt;
        y = Math.max(FACTORY.magnetronMinY, FACTORY.magnetronHoverY + Math.sin(b.bobT) * FACTORY.magnetronBobAmp);
      }
      // glow: 0 when uncapped (plain brown), else the cryo's own pulse — same formula the cooler's ring uses
      const glow = capped ? 0.5 + ((cryo.spinBot ?? 0) / FACTORY.cryoBotMax) * 0.7 + (cryo.flash ?? 0) * 1.8 : 0;
      if (b.inst) {
        this.setDiskMatrix(b, y, b.rotDisk ?? 0);
        if (capped) {
          this.instancer.setEmissiveColor(b.inst.key, b.inst.slot, "Union2", CRYO_BLUE.r * glow, CRYO_BLUE.g * glow, CRYO_BLUE.b * glow);
          this.world.bloomActive = true;
        } else {
          this.instancer.setEmissive(b.inst.key, b.inst.slot, "Union2", 0); // no glow, and no bloom contribution
        }
        continue;
      }
      const disk = b.mesh!.getObjectByName("magDisk"); if (!disk) continue;
      const brown = disk.getObjectByName("Union2") as THREE.Mesh | undefined; // the brown-copper inner
      if (brown) {
        const mt = brown.material as THREE.MeshStandardMaterial;
        if (capped) {                                             // taken over by the cryo's blue neon, pulsing WITH the cooler
          mt.emissive.setHex(0x59d5ff);
          mt.emissiveIntensity = glow;
          brown.layers.enable(BLOOM_LAYER); this.world.bloomActive = true;
        } else if (mt.emissiveIntensity > 0) {                    // reverted (cryo removed) → back to plain brown
          mt.emissive.setHex(0x000000); mt.emissiveIntensity = 0; brown.layers.disable(BLOOM_LAYER);
        }
      }
      disk.rotation.y = b.rotDisk ?? 0;
      disk.position.y = y;
    }
  }

  /** Per-frame infernace visuals: the 4-oval top (`Union4`) STAMPS down `infernacePress` studs on each ore pass
   *  (`flash` set to 1 in updateOre) and springs back up. No glow. */
  private animateInfernaces(dt: number): void {
    for (const b of this.buildings.values()) {
      if (b.kind !== "infernace") continue;
      b.flash = Math.max(0, (b.flash ?? 0) - dt / 0.22);                 // stamp springs back up
      const y = -(b.flash ?? 0) * FACTORY.infernacePress;                // down on an ore pass, ease back
      if (b.inst && b.mat) {
        // Instanced: the pool stamps ONE matrix on every part, so a per-part offset is the building's own
        // matrix post-multiplied by the local translate — right-multiplied, so it lands in MODEL space (where
        // the part geometry lives) rather than world space, which is what `top.position.y` meant on the Group.
        this._partMat.copy(b.mat).multiply(this._partOff.makeTranslation(0, y, 0));
        this.instancer.setMatrix(b.inst.key, b.inst.slot, "Union4", this._partMat);
      } else if (b.mesh) {
        const top = b.mesh.getObjectByName("Union4"); if (!top) continue;
        top.position.y = y;
      }
    }
  }

  /** Per-frame leech visuals: the neon glow SCROLLS up the columns (`uFlow`), and FADES in/out (`uActive`) with
   *  whether powered ore is currently on the belt — a smooth fade-in / flow / fade-out. */
  private animateLeeches(dt: number): void {
    for (const b of this.buildings.values()) {
      if (b.kind !== "leech" || !b.mesh) continue;
      const mat = b.mesh.userData.neonMat as THREE.MeshStandardMaterial | undefined; if (!mat) continue;
      b.flowT = (b.flowT ?? 0) + FACTORY.leechFlowSpeed * dt;
      b.neonA = (b.neonA ?? 0) + ((b.busy ? 1 : 0) - (b.neonA ?? 0)) * Math.min(1, FACTORY.leechFadeRate * dt);
      (mat.userData.uFlow as { value: number }).value = b.flowT;
      (mat.userData.uActive as { value: number }).value = b.neonA;
    }
  }

  /** The richest ore get a real LENS FLARE — the same procedural pass the campaign hangs on its stars (glare +
   *  anamorphic streaks + starburst, one additive fullscreen draw for all of them). Only ore past
   *  `oreFlareFrom` qualify and only the brightest `oreFlares.max` of those, so a flare stays the mark of
   *  something exceptional and the cost is one quad no matter how many ore are on the belt. Bloom already
   *  handles the glow; this is the thing on top that says "look at THIS one". */
  private updateOreFlares(): void {
    const F = this.oreFlares, span = FACTORY.oreHueSpan, from = FACTORY.oreFlareFrom;
    let n = 0;
    // OBLITERATOR BURST — a real star flare on the same pass, not a lookalike. Claimed BEFORE the ore and
    // never displaced by them (see `reserved` below): a burst is a deliberate event the player just caused,
    // so it must not lose its slot to an ambient rich ore that happens to be brighter this frame.
    if (this.oblFlash > 0.01) {
      for (const b of this.buildings.values()) {
        if (b.kind !== "obliterator" || n >= F.max) continue;
        this._ndc.set(this.worldX(b.gx), this.floorY(b.floor) + 60, this.worldZ(b.gz)).project(this.camera);
        if (this._ndc.z > 1 || Math.abs(this._ndc.x) > 1.25 || Math.abs(this._ndc.y) > 1.25) continue;
        F.pos[n].set(this._ndc.x, this._ndc.y);
        F.col[n].copy(this.oblCol);
        F.inten[n] = this.oblFlash * FACTORY.obliteratorFlareGain;
        n++;
      }
    }
    const reserved = n;                                      // obliterator slots are off-limits to the ore below
    let weakest = 1e9, weakestAt = -1;                       // keep only the best `max` — a tiny selection sort
    for (const o of this.items) {
      if (!o.active || o.part) continue;
      const t = Math.min(1, Math.max(0, Math.log2(Math.max(1, this.oreValue(o.value, o.elem, o.elemMask)))) / span);
      const glow = t * t;
      if (glow < from) continue;
      this._ndc.set(o.px, o.py, o.pz).project(this.camera);
      if (this._ndc.z > 1 || Math.abs(this._ndc.x) > 1.25 || Math.abs(this._ndc.y) > 1.25) continue; // off-screen
      const inten = (glow - from) / (1 - from);              // 0 at the threshold → 1 at a full-wheel ore
      let slot = -1;
      if (n < F.max) slot = n++;
      else if (inten > weakest) slot = weakestAt;            // displace the dimmest one we kept
      if (slot < 0) continue;
      F.pos[slot].set(this._ndc.x, this._ndc.y);
      F.col[slot].setHSL(t, 0.8, 0.6);                       // its own place on the wheel, lifted toward white
      F.inten[slot] = inten * FACTORY.oreFlareGain;
      weakest = 1e9; weakestAt = -1;
      for (let k = reserved; k < n; k++) if (F.inten[k] < weakest) { weakest = F.inten[k]; weakestAt = k; }
    }
    F.set(n, this.camera.aspect, FACTORY.oreFlareScale);
  }

  /** ARMED ore tick every frame, wherever they are: value climbs steadily and so does the chance of losing the
   *  lot. The risk is per-second and scales with how long it has been armed, so a bomb you leave running gets
   *  both more valuable and more likely to go — which is the whole decision the Bomb Packer poses. */
  // ── ORE CANNON ──────────────────────────────────────────────────────────────────────────────────────────────
  // PART MAP. Measured from the .obj with an oriented (PCA) box, not an axis-aligned one — an AABB cannot tell a
  // tube from a plate, and reading "topmost part = barrel" off an AABB is how a flat disc ended up elevating to
  // 78°. Every part in the current export is wider than it is long (max aspect 1.00):
  //   02001  110×46×45 TUBE, axis +Y .... BARREL, near-black. Group name is the authored pivot `0,-20,0`.
  //   Union1 101×103×15 gray plate ....... conveyor support
  //   Union2 141×141×21 gray plate ....... belt (45° in plan)
  //   Union3 101×102×94 near-cube ........ base (orange)
  //   Union4  99× 99×50 flat disc ....... GEAR (dark red), sits directly under the barrel — this yaws
  //   Union5  70× 70×50 vertical cylinder  base (orange, same Kd as Union3)
  // The barrel is authored pointing STRAIGHT UP (+Y), not forward, so elevating it is R_x(π/2 − pitch): at
  // pitch 90° it stands as modeled, at pitch 0 it lies flat along the bore. Getting this wrong is silent —
  // the ballistics read turYaw/turPitch, never the meshes, so a mis-rigged barrel still shoots correctly and
  // only LOOKS wrong. Which is how a flat disc ended up elevating to 78° before the barrel existed.

  /** A freshly placed cannon aims 3 tiles straight ahead at its own deck height — a sane, visible default that
   *  lands on the pad rather than lobbing the player's first ore into the void. Also stamps the turret matrices
   *  immediately: importCode places outside the animate pass, so without this a loaded build draws every barrel
   *  unaimed for a frame. */
  private seedCannon(b: Placed): void {
    const [dx, dz] = DIR_VEC[b.dir];
    b.turYaw = DIR_YAW[b.dir]; b.turPitch = 0; b.tgtManual = false;
    const p = this.cannonPivot(b, this._cv);
    b.tgtX = p.x + dx * FACTORY.tile * 3;
    b.tgtZ = p.z + dz * FACTORY.tile * 3;
    b.tgtY = this.floorY(b.floor) + FACTORY.oreDeck;
    const a = this.cannonSolve(b, b.tgtX, b.tgtY, b.tgtZ);
    b.turYaw = a.yaw; b.turPitch = a.pitch; b.muzzleV = a.v; // start ON target, not slewing onto it
    if (!b.inst) return;
    this.tileCenter(b.gx, b.gz, b.floor, this._c);
    this._q.setFromAxisAngle(this._yAxis, b.turYaw);
    this._animMat.compose(this._c, this._q, this._one);
    for (const p of CANNON_TURRET) this.instancer.setMatrix(b.inst.key, b.inst.slot, p, this._animMat);
    if (CANNON_BARREL) this.instancer.setMatrix(b.inst.key, b.inst.slot, CANNON_BARREL, this.cannonBarrelMatrix(b, this._cmB));
  }

  /** Open the aiming menu for one cannon. Everything the panel needs to draw itself, in tile units where the
   *  player thinks in tiles and degrees where they think in degrees. */
  private openCannonMenu(b: Placed): void {
    this.aimed = b;
    this.hud?.setCannon(this.cannonState(b));
  }

  /** The cannon's aim as the HUD shows it: target in TILE coordinates (what the player reads off the pad) and
   *  height in floors, plus the raw angles for the direct sliders. `reachable` is reported, never hidden — a
   *  cannon quietly lobbing ore into a gap is precisely the failure this factory is worst at surfacing. */
  cannonState(b: Placed): {
    gx: number; gz: number; tileX: number; tileZ: number; floor: number; maxFloor: number;
    yawDeg: number; pitchDeg: number; powerPct: number; manual: boolean; reachable: boolean;
    why: "ok" | "close" | "far"; rangeTiles: number; minTiles: number;
  } {
    // Copy the tile center out to PLAIN NUMBERS before solving. `tileCenter` hands back the shared `_cv`
    // scratch, and cannonSolve reuses that same scratch for the muzzle fixed-point — so anything still holding
    // the vector afterwards is reading the muzzle, not the tile. That aliasing put the floor readout 1.8 floors
    // out while every angle it reported was correct, which is exactly the sort of bug that survives a demo.
    const c = this.tileCenter(b.gx, b.gz, b.floor, this._cv);
    const cx = c.x, cy = c.y, cz = c.z;
    const dx = (b.tgtX ?? cx) - cx, dz = (b.tgtZ ?? cz) - cz, dy = (b.tgtY ?? cy) - cy;
    const a = this.cannonSolve(b, b.tgtX, b.tgtY, b.tgtZ);
    return {
      gx: b.gx, gz: b.gz,
      tileX: b.gx + dx / FACTORY.tile, tileZ: b.gz + dz / FACTORY.tile,
      // FLOORS, not studs — a stud is a number the player has no feel for, and every other height control in
      // the game ([ and ], the build zone) already speaks in floors.
      floor: (cy + dy - FACTORY.oreDeck - this.floorY(0)) / FACTORY.floorHeight,
      maxFloor: FACTORY.maxFloors - 1,
      yawDeg: ((b.turYaw ?? 0) * 180) / Math.PI, pitchDeg: ((b.turPitch ?? 0) * 180) / Math.PI,
      powerPct: (100 * ((b.muzzleV ?? FACTORY.cannonMuzzle) - FACTORY.cannonMuzzleMin)) /
                (FACTORY.cannonMuzzleMax - FACTORY.cannonMuzzleMin),
      manual: !!b.tgtManual, reachable: a.reachable, why: a.why,
      // flat max range = v²/g, in tiles — what the panel warns against
      rangeTiles: (FACTORY.cannonMuzzle * FACTORY.cannonMuzzle) / PHYS.gravity / FACTORY.tile,
      // closest the LOB can reach at max elevation: v²·sin(2θ)/g at θ = cannonMaxPitch
      minTiles: (FACTORY.cannonMuzzle * FACTORY.cannonMuzzle * Math.sin(2 * FACTORY.cannonMaxPitch)) / PHYS.gravity / FACTORY.tile,
    };
  }

  /** Menu → aim at a TILE on a FLOOR. Height arrives as a floor number and is turned into that floor's deck
   *  height here, so the player never meets a stud. */
  aimCannonAtTile(tileX: number, tileZ: number, floor: number): boolean {
    const b = this.aimed; if (!b) return false;
    const fl = Math.max(0, Math.min(FACTORY.maxFloors - 1, floor));
    const c = this.tileCenter(b.gx, b.gz, b.floor, this._cv);
    const dy = this.floorY(0) + fl * FACTORY.floorHeight + FACTORY.oreDeck - c.y;
    const ok = this.aimCannon(b, (tileX - b.gx) * FACTORY.tile, (tileZ - b.gz) * FACTORY.tile, dy, false);
    this.hud?.setCannon(this.cannonState(b));
    return ok;
  }

  /** Menu → drive the gun directly. The sliders OWN the aim from here (tgtManual), and tgt* becomes a derived
   *  readout rather than the source of truth — which is why they are separate fields. */
  aimCannonManual(yawDeg: number, pitchDeg: number, powerPct: number): void {
    const b = this.aimed; if (!b) return;
    b.tgtManual = true;
    b.manYaw = (yawDeg * Math.PI) / 180;
    b.manPitch = Math.max(FACTORY.cannonMinPitch, Math.min(FACTORY.cannonMaxPitch, (pitchDeg * Math.PI) / 180));
    b.manV = FACTORY.cannonMuzzleMin +
             (Math.max(0, Math.min(100, powerPct)) / 100) * (FACTORY.cannonMuzzleMax - FACTORY.cannonMuzzleMin);
    this.hud?.setCannon(this.cannonState(b));
  }

  closeCannonMenu(): void { this.aimed = null; this.hud?.setCannon(null); }

  /** Point a cannon at an offset from its own tile center and snap the turret onto it. Offsets rather than world
   *  coordinates so a shared build stays aimed correctly wherever its pad happens to sit. Returns whether the
   *  target is actually reachable at the fixed muzzle speed, which the menu surfaces rather than hides. */
  aimCannon(b: Placed | undefined, dx: number, dz: number, dy: number, snap = true): boolean {
    if (!b || b.kind !== "cannon") return false;
    const c = this.tileCenter(b.gx, b.gz, b.floor, this._cv);
    b.tgtX = c.x + dx; b.tgtZ = c.z + dz; b.tgtY = c.y + dy; b.tgtManual = false;
    const a = this.cannonSolve(b, b.tgtX, b.tgtY, b.tgtZ);
    if (snap) { b.turYaw = a.yaw; b.turPitch = a.pitch; }   // loading a build shouldn't spray its first ore
    return a.reachable;
  }

  /** Solve the launch angles that put a shot from `f` onto `t` at the FIXED muzzle speed, and say whether the
   *  target was actually reachable. Returns the LOW arc — the flat, fast one — because it spends less time in the
   *  air and therefore gives the ore less chance to clip something on the way.
   *
   *  Two angles reach any reachable target: a flat fast one and a lobbed one. We take the LOB. The flat shot
   *  arrives with most of its speed still horizontal, so the ore skids across the target tile and off the far
   *  side — visible in play, which is how this was caught. The lob comes down steeply, the deck absorbs it, and
   *  the ore stays where it was sent. Costs flight time, buys the thing the cannon exists for.
   *  Below ~1.2 tiles even 85° cannot lob (the arc would need to be near-vertical), so those fall back to flat.
   *
   *  Out of range there is no real solution (the discriminant goes negative), and the honest best effort is 45°,
   *  the angle that throws furthest. The gun still fires; the ore just lands short. The menu reports it rather
   *  than silently pretending, because "my cannon is quietly dumping ore in a gap" is exactly the kind of failure
   *  this factory is bad at surfacing. */
  /** Ballistic solve at a GIVEN speed — the lob (+ root), which comes down steeply so ore stay where they land. */
  private cannonArc(fx: number, fy: number, fz: number, tx: number, ty: number, tz: number, v: number):
      { yaw: number; pitch: number; ok: boolean } {
    const dx = tx - fx, dz = tz - fz, dy = ty - fy, d = Math.hypot(dx, dz);
    const yaw = Math.atan2(dx, dz);                       // DIR_YAW convention: local +Z is forward
    const v2 = v * v, g = PHYS.gravity;
    const disc = v2 * v2 - g * (g * d * d + 2 * dy * v2);
    if (d < 1 || disc < 0) return { yaw, pitch: Math.PI / 4, ok: false };
    return { yaw, pitch: Math.atan2(v2 + Math.sqrt(disc), g * d), ok: true };
  }

  /** Pick the launch SPEED as well as the angles. A real gunner winds up for something far or high and eases off
   *  for something close, and doing the same here means "too far" and "too close" become the gun's genuine
   *  physical limits rather than artefacts of one hardcoded speed.
   *
   *  Three cases, in the order they are worth trying:
   *   1. nominal speed reaches it on a lob within the mount's elevation — the common case, nothing clever needed;
   *   2. it is too CLOSE for a nominal lob (the arc would need more than max elevation). Rather than overshoot,
   *      hold max elevation and solve for the speed that lands exactly there: v² = g·d² / (2cos²θ·(d·tanθ − dy));
   *   3. it is too FAR at nominal — wind up. The least speed that can reach a target at all is the minimum-energy
   *      solution v² = g·(dy + √(d²+dy²)); go a hair above it so the two roots separate, capped at full power. */
  private cannonAim(fx: number, fy: number, fz: number, tx: number, ty: number, tz: number):
      { yaw: number; pitch: number; v: number; reachable: boolean; why: "ok" | "close" | "far" } {
    const dx = tx - fx, dz = tz - fz, dy = ty - fy, d = Math.hypot(dx, dz);
    const yaw = Math.atan2(dx, dz);
    const g = PHYS.gravity, hi = FACTORY.cannonMaxPitch, lo = FACTORY.cannonMinPitch;
    const vNom = FACTORY.cannonMuzzle, vMax = FACTORY.cannonMuzzleMax, vMin = FACTORY.cannonMuzzleMin;
    if (d < 1) return { yaw, pitch: hi, v: vNom, reachable: false, why: "close" };

    let a = this.cannonArc(fx, fy, fz, tx, ty, tz, vNom);
    if (a.ok && a.pitch <= hi) return { yaw: a.yaw, pitch: a.pitch, v: vNom, reachable: true, why: "ok" };

    if (a.ok) {                                            // too close for a nominal lob → same angle, less powder
      const c = Math.cos(hi), den = 2 * c * c * (d * Math.tan(hi) - dy);
      if (den > 0) {
        const v = Math.sqrt((g * d * d) / den);
        if (v >= vMin && v <= vMax) return { yaw, pitch: hi, v, reachable: true, why: "ok" };
        if (v < vMin) return { yaw, pitch: hi, v: vMin, reachable: false, why: "close" };
      }
      return { yaw, pitch: hi, v: vNom, reachable: false, why: "close" };
    }

    // Comfortably ABOVE the minimum-energy speed, not a hair above it. At the minimum the target sits at the very
    // top of the arc, so the ore arrives traveling almost horizontally and skids straight off the pad — the same
    // flat-arrival failure the lob exists to avoid, smuggled back in by the power solver. Measured: a floor-6
    // target was hit dead center (miss 0) and still delivered NOTHING, because every ore slid off the far edge.
    // Surplus speed buys a steeper descent, which is what makes the ore stay.
    const vReq = Math.sqrt(Math.max(0, g * (dy + Math.hypot(d, dy))));
    const v = Math.min(vMax, vReq * 1.22);
    a = this.cannonArc(fx, fy, fz, tx, ty, tz, v);
    if (!a.ok) return { yaw, pitch: Math.PI / 4, v: vMax, reachable: false, why: "far" };
    return { yaw: a.yaw, pitch: Math.max(lo, Math.min(hi, a.pitch)), v,
             reachable: a.pitch <= hi && a.pitch >= lo, why: a.pitch > hi ? "close" : "far" };
  }

  /** World position of the barrel's trunnion. The model's local origin sits at the tile center, and the pivot is
   *  on the model's own Y axis, so yaw never moves it. */
  private cannonPivot(b: Placed, out: THREE.Vector3): THREE.Vector3 {
    this.tileCenter(b.gx, b.gz, b.floor, out);
    out.y += FACTORY.cannonPivotY;
    return out;
  }

  /** The barrel's world matrix: pivot, then yaw, then elevate about the trunnion, then undo the local pivot
   *  offset so the geometry lands back where it belongs. Rendering and the muzzle spawn point BOTH read this, so
   *  the ore can never leave from somewhere the barrel isn't pointing. */
  private cannonBarrelMatrix(b: Placed, out: THREE.Matrix4): THREE.Matrix4 {
    return this.cannonBarrelMatrixFor(b, b.turYaw ?? DIR_YAW[b.dir], b.turPitch ?? 0, out);
  }

  /** As above but for ARBITRARY angles, so the aim solver can ask "where would the muzzle be if I pointed
   *  there?" without moving the gun. */
  private cannonBarrelMatrixFor(b: Placed, yaw: number, pitch: number, out: THREE.Matrix4): THREE.Matrix4 {
    this.cannonPivot(b, this._c);
    this._q.setFromAxisAngle(this._yAxis, yaw);
    out.compose(this._c, this._q, this._one);                       // T(pivot)·R_y
    // The barrel is modeled pointing UP (+Y), so R_x(π/2 − pitch) maps its own axis onto the aim: pitch 90°
    // leaves it as authored, pitch 0 lays it flat along the bore.
    this._cmA.makeRotationX(Math.PI / 2 - pitch);
    out.multiply(this._cmA);
    this._cmA.makeTranslation(0, -FACTORY.cannonPivotY, 0);
    out.multiply(this._cmA);
    return out;
  }

  /** Where the ore actually appears for a given aim. THE one muzzle formula — the solver and the shot both call
   *  it, so they can never disagree about where the gun's mouth is. */
  private cannonMuzzle(b: Placed, yaw: number, pitch: number, out: THREE.Vector3): THREE.Vector3 {
    // The trunnion sits ON the barrel's own axis, so the muzzle is simply `pivot + len × aim` — no matrix, and
    // no way for the spawn point to drift off the line the shot is fired along.
    const cp = Math.cos(pitch), L = FACTORY.cannonMuzzleLen;
    this.cannonPivot(b, out);
    return out.set(out.x + Math.sin(yaw) * cp * L, out.y + Math.sin(pitch) * L, out.z + Math.cos(yaw) * cp * L);
  }

  /** Aim at a world point, correcting for the fact that the shot does NOT leave from the trunnion — it leaves
   *  from the muzzle, ~57 forward and ~23 up. Solving from the pivot and firing from the muzzle overshot every
   *  shot by ~98 units, which on a 6-tile pad is the difference between landing on the target and sailing off
   *  the edge into the void. (Measured: survivors piling up at gx=11 on a 6-wide pad.)
   *
   *  The muzzle's position depends on the pitch we are solving for, so it is a fixed point: seed from the pivot,
   *  then re-solve twice against the muzzle that answer implies. It converges immediately — the muzzle barely
   *  moves between neighboring solutions. */
  private cannonSolve(b: Placed, tx?: number, ty?: number, tz?: number):
      { yaw: number; pitch: number; v: number; reachable: boolean; why: "ok" | "close" | "far" } {
    const pv = this.cannonPivot(b, this._cv);
    const gx = tx ?? pv.x, gy = ty ?? pv.y, gz = tz ?? pv.z + 300;
    let a = this.cannonAim(pv.x, pv.y, pv.z, gx, gy, gz);
    // Iterate to CONVERGENCE, not a fixed two passes. At high elevation the muzzle swings ~75 units, which moves
    // the target's relative height enough to change the answer — two passes left the returned angle (78°) and the
    // returned speed disagreeing about where the shot lands, and a floor-6 target fell 47 units short with no
    // warning. Damped, because the muzzle chases the angle it is trying to compute.
    for (let k = 0; k < 12; k++) {
      const mz = this.cannonMuzzle(b, a.yaw, a.pitch, this._cv);
      const n = this.cannonAim(mz.x, mz.y, mz.z, gx, gy, gz);
      const d = Math.abs(n.pitch - a.pitch) + Math.abs(n.yaw - a.yaw);
      a = { ...n, pitch: a.pitch + (n.pitch - a.pitch) * 0.6, yaw: n.yaw };
      if (d < 1e-4) { a = n; break; }
    }
    // Judge the OUTCOME, not the branch it came from. A clamped shot frequently still lands on the target (the
    // overshoot is a fraction of a tile), and crying "out of range" at a shot that works is as misleading as
    // staying quiet about one that doesn't. So fly the solution we are actually about to use and see.
    const mz = this.cannonMuzzle(b, a.yaw, a.pitch, this._cv);
    const cp = Math.cos(a.pitch), vy = Math.sin(a.pitch) * a.v;
    const vx = Math.sin(a.yaw) * cp * a.v, vz = Math.cos(a.yaw) * cp * a.v;
    const dsc = vy * vy + 2 * PHYS.gravity * (mz.y - gy);
    if (dsc < 0) return { ...a, reachable: false };
    const t = (vy + Math.sqrt(dsc)) / PHYS.gravity;
    const miss = Math.hypot(mz.x + vx * t - gx, mz.z + vz * t - gz);
    return { ...a, reachable: miss < FACTORY.tile * 0.5 };
  }

  /** SIMULATION, not decoration: slew the turret toward its commanded aim. Runs in `tickBuildings`, i.e. inside
   *  the fixed step, because where the barrel is pointing decides where a shot goes — putting this in an animate*
   *  pass would make the gun's accuracy depend on frame rate. */
  private stepCannon(b: Placed, dt: number): void {
    if (b.turYaw == null) b.turYaw = DIR_YAW[b.dir];
    if (b.turPitch == null) b.turPitch = 0;
    let cy: number, cp: number;
    if (b.tgtManual) { cy = b.manYaw ?? b.turYaw; cp = b.manPitch ?? b.turPitch; b.muzzleV = b.manV ?? FACTORY.cannonMuzzle; }
    else {
      const a = this.cannonSolve(b, b.tgtX, b.tgtY, b.tgtZ);
      cy = a.yaw; cp = a.pitch; b.muzzleV = a.v;   // power is chosen with the angle, and winds instantly
    }
    // shortest way round, so a re-aim across the ±π seam doesn't spin the long way
    let d = cy - b.turYaw;
    while (d > Math.PI) d -= Math.PI * 2;
    while (d < -Math.PI) d += Math.PI * 2;
    const my = FACTORY.cannonYawRate * dt;
    b.turYaw += Math.max(-my, Math.min(my, d));
    const mp = FACTORY.cannonPitchRate * dt;
    b.turPitch += Math.max(-mp, Math.min(mp, cp - b.turPitch));
    b.turPitch = Math.max(FACTORY.cannonMinPitch, Math.min(FACTORY.cannonMaxPitch, b.turPitch));
  }

  /** THE SHOT. An ore touching the deck is warped to the muzzle and fired the same step, along wherever the
   *  barrel actually is — mid-slew included. From there it is an ordinary Jolt body under gravity: it arcs,
   *  lands, bounces, rolls, and if nothing is under it when it comes down, it is gone.
   *
   *  setBody then applyFling composes exactly right: the first teleports it to the muzzle and kills the belt
   *  velocity it arrived with, the second unlocks full 6-DOF (so it tumbles in flight) and adds the launch
   *  velocity. forgetContact is the teleporter's lesson — without it the contact census still answers "cannon
   *  deck" for a step and the ore gets fired a second time from wherever it now is. */
  private fireCannon(b: Placed, o: Item, bh: number, w: JoltWorld): void {
    const yaw = b.turYaw ?? DIR_YAW[b.dir], pitch = b.turPitch ?? 0, v = b.muzzleV ?? FACTORY.cannonMuzzle;
    this.cannonMuzzle(b, yaw, pitch, this._cv);   // same formula the solver aimed with
    const cp = Math.cos(pitch);
    w.setBody(bh, this._cv.x, this._cv.y, this._cv.z, 0, 0, 0);
    w.applyFling(bh, Math.sin(yaw) * cp * v, Math.sin(pitch) * v, Math.cos(yaw) * cp * v);
    w.forgetContact(bh);
    o.flung = true;            // re-locked to no-tumble when it lands (oreEconomyPass)
    b.fired = 1;               // muzzle flash, decays in animateCannons
    b.busy = true;
  }

  /** Per-FRAME visuals only: stamp the gear's and barrel's matrices from the simulation state the fixed step
   *  already decided, and fade the muzzle flash. Reading (not writing) turYaw/turPitch here is what keeps the
   *  gun's behavior identical at 20fps and 144fps. */
  private animateCannons(dt: number): void {
    // The menu must not outlive its cannon — bulldozing one, or loading a new build, would otherwise leave a
    // panel editing a building that no longer exists. Checked here so there is ONE place that can get it wrong.
    if (this.aimed && this.buildings.get(key(this.aimed.gx, this.aimed.gz, this.aimed.floor)) !== this.aimed) this.closeCannonMenu();
    for (const b of this.buildings.values()) {
      if (b.kind !== "cannon") continue;
      b.fired = Math.max(0, (b.fired ?? 0) - dt / 0.12);
      // keep the open menu's angle readouts live while the turret slews onto a new bearing
      if (b === this.aimed) this.hud?.setCannon(this.cannonState(b));
      if (!b.inst) continue;
      this.tileCenter(b.gx, b.gz, b.floor, this._c);
      this._q.setFromAxisAngle(this._yAxis, b.turYaw ?? DIR_YAW[b.dir]);
      this._animMat.compose(this._c, this._q, this._one);
      for (const p of CANNON_TURRET) this.instancer.setMatrix(b.inst.key, b.inst.slot, p, this._animMat);
      if (CANNON_BARREL) this.instancer.setMatrix(b.inst.key, b.inst.slot, CANNON_BARREL,
                                                  this.cannonBarrelMatrix(b, this._cmB));
    }
  }

  private tickBombs(dt: number): void {
    for (const o of this.items) {
      if (!o.active || o.part || o.bombT == null) continue;
      o.bombT += dt;
      o.value += FACTORY.packerPerSec * dt;
      if (Math.random() < FACTORY.packerRisk * o.bombT * dt) {          // detonation: the ore is simply gone
        this.fling(o.px, o.py, o.pz, 220, 0xff5a2a);
        if (o.bodyH != null && this.jolt) { this.jolt.despawnOre(o.bodyH); o.bodyH = undefined; }
        o.active = false;
      }
    }
  }

  /** The Vat's payoff: fuse what it has eaten into one AMALGAM ore, dropped from the spout that overhangs its
   *  tile (the model's transparent tip is the drop point). A sink that hands part of its take back to the line. */
  private dropAmalgam(b: Placed, worth: number): void {
    const conf = INST[b.kind];
    const drops = conf ? conf.model.dropPoints : (b.mesh?.userData.dropPoints as [number, number, number][] | undefined);
    const xf: THREE.Matrix4 | undefined = b.mat ?? (b.mesh ? (b.mesh.updateMatrixWorld(), b.mesh.matrixWorld) : undefined);
    if (drops && drops.length && xf) this._v.set(drops[0][0], drops[0][1], drops[0][2]).applyMatrix4(xf);
    else this._v.set(this.worldX(b.gx), this.floorY(b.floor) + 90, this.worldZ(b.gz));
    for (const o of this.items) {
      if (o.active) continue;
      o.active = true; o.part = false; o.floor = b.floor; o.applied = false; o.tier = 0; o.t = 0; o.uid = ++this.oreUid;
      o.qx = 0; o.qy = 0; o.qz = 0; o.qw = 1; o.bombT = undefined; o.caff = 0; // recycled slot → reset EVERY per-ore field
      if (this.jolt && o.bodyH != null) this.jolt.despawnOre(o.bodyH);
      o.bodyH = undefined;
      o.px = this._v.x; o.py = this._v.y; o.pz = this._v.z; o.vx = 0; o.vy = -10; o.vz = 0; o.capKey = -1;
      o.gx = Math.floor((o.px - this.originX) / FACTORY.tile);
      o.gz = Math.floor((o.pz - this.originZ) / FACTORY.tile);
      o.value = Math.max(1, worth); o.elemMask = 0; o.elem = 1;         // fused: one dense ore, elements spent
      return;
    }
  }

  /** Per-frame Mutator + Laser visuals. The Mutator's reactor block pops on every mutation. The Laser's emitter
   *  pops on a hit and then STAYS LIT while it holds an absorbed burn, so you can see at a glance that the next
   *  ore through is the one that cashes it — and a near-dark emitter means ore are riding too wide to be hit. */
  private animateBeams(dt: number): void {
    for (const b of this.buildings.values()) {
      if (!b.mesh && !b.inst) continue; // instanced now: brightness is a per-instance attribute, not a material
      if (b.kind === "mutator") {
        b.flash = Math.max(0, (b.flash ?? 0) - FACTORY.ecoboxPulseDecay * dt);
        this.setPartGlow(b, "Union3", 0.14 + (b.flash ?? 0) * 1.7);
      } else if (b.kind === "enricher") {
        b.flash = Math.max(0, (b.flash ?? 0) - FACTORY.ecoboxPulseDecay * dt);
        this.setPartGlow(b, "Union3", 0.10 + (b.flash ?? 0) * 1.8);
      } else if (b.kind === "laser") {
        b.flash = Math.max(0, (b.flash ?? 0) - FACTORY.ecoboxPulseDecay * dt);
        this.setPartGlow(b, "Union2", 0.12 + (b.flash ?? 0) * 1.9 + (b.charge ? 0.5 : 0));
      }
    }
  }

  /** Per-frame Diversifier visuals: the three R/G/B slabs light ONE PER ELEMENT the passing ore carried, so the
   *  machine reads out the thing it is paying for. `flash` holds elements/5 (set once per pass); with 5 elements
   *  and 3 slabs each slab covers 5/3 of an element, so a rich ore lights all three and a plain one lights none. */
  private animateDiversifiers(dt: number): void {
    const SLABS = ["Union4", "Union5", "Union6"];
    for (const b of this.buildings.values()) {
      if (b.kind !== "diversifier" || !b.mesh) continue;
      b.flash = Math.max(0, (b.flash ?? 0) - FACTORY.diversifierDecay * dt);
      const lit = (b.flash ?? 0) * 5;                       // back to a count of elements
      for (let i = 0; i < SLABS.length; i++) {
        const m = b.mesh.getObjectByName(SLABS[i]) as THREE.Mesh | undefined;
        if (!m) continue;
        const share = Math.max(0, Math.min(1, lit - i * (5 / 3))); // slabs fill up in order as the count rises
        (m.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.16 + share * 1.5;
      }
    }
  }

  /** Per-frame ecobox (Infestor) visuals: its green neon (`Union3`) pulses on each ore pass — `flash` is set to a
   *  magnitude in updateOre (bigger the more the spore grew), and decays FAST here for a quick pop over a dim idle glow. */
  private animateEcoboxes(dt: number): void {
    for (const b of this.buildings.values()) {
      if (b.kind !== "ecobox" || (!b.mesh && !b.inst)) continue;
      b.flash = Math.max(0, (b.flash ?? 0) - FACTORY.ecoboxPulseDecay * dt);
      this.setPartGlow(b, "Union3", 0.25 + (b.flash ?? 0) * 1.6); // dim idle + bright pulse
    }
  }

  /** Per-frame scanner visuals: both rings spin smoothly and ease through the +45° per-pass snaps; the neon ring
   *  lights up on each pass (`flash` set in updateOre) over a dim idle glow. */
  private animateScanners(dt: number): void {
    for (const b of this.buildings.values()) {
      if (b.kind !== "scanner" || !b.mesh) continue;
      b.ringSpin = (b.ringSpin ?? 0) + FACTORY.scannerSpin * dt;                                              // smooth continuous spin
      b.ringSnap = (b.ringSnap ?? 0) + ((b.ringSnapTgt ?? 0) - (b.ringSnap ?? 0)) * Math.min(1, FACTORY.scannerSnapRate * dt); // ease onto the +45° kicks
      const rings = b.mesh.getObjectByName("scanRings");
      if (rings) rings.rotation.z = -((b.ringSpin ?? 0) + (b.ringSnap ?? 0));                                 // spin about the belt axis — ore passes THROUGH (negated = the nicer direction)
      b.flash = Math.max(0, (b.flash ?? 0) - FACTORY.scannerPulseDecay * dt);
      for (const n of ["Union1", "Union2"]) { const m = b.mesh.getObjectByName(n) as THREE.Mesh | undefined; if (m) (m.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.3 + (b.flash ?? 0) * 1.8; }
    }
  }

  /** Record a scanned worth into the rolling window + running max (feeds the scanner HUD readout). */
  private recordScan(worth: number): void {
    this.scanBuf[this.scanBufPos] = worth;
    this.scanBufPos = (this.scanBufPos + 1) % this.scanBuf.length;
    if (this.scanBufN < this.scanBuf.length) this.scanBufN++;
    if (worth > this.scanMax) this.scanMax = worth;
    this.scanCount++;
  }

  /** Median of the rolling window of recent scanned worths (0 if none yet). */
  private scanMedian(): number {
    const n = this.scanBufN;
    if (n === 0) return 0;
    const a = Array.from(this.scanBuf.subarray(0, n)).sort((x, y) => x - y);
    return n % 2 ? a[(n - 1) / 2] : (a[n / 2 - 1] + a[n / 2]) / 2;
  }

  private scanHudT = 0;
  /** Push the scanner readout (rolling median + peak) to the HUD at ~5 Hz; shown only while a scanner exists. */
  private updateScanHud(dt: number): void {
    this.scanHudT -= dt;
    if (this.scanHudT > 0) return;
    this.scanHudT = 0.2;
    let has = false;
    for (const b of this.buildings.values()) if (b.kind === "scanner") { has = true; break; }
    this.hud?.setScanStats(has, this.scanMedian(), this.scanMax);
  }

  /** Per-frame waverider visuals: the blue neon wave SCROLLS along the strip (`uFlow`) and fades in/out (`uActive`)
   *  with whether ore is currently on the 2-tile belt. */
  private animateWaveriders(dt: number): void {
    for (const b of this.buildings.values()) {
      if (b.kind !== "waverider" || !b.mesh) continue;
      const mat = b.mesh.userData.waveMat as THREE.MeshStandardMaterial | undefined; if (!mat) continue;
      b.waveT = (b.waveT ?? 0) + FACTORY.waveriderWaveSpeed * dt;
      b.waveA = (b.waveA ?? 0) + ((b.busy ? 1 : 0) - (b.waveA ?? 0)) * Math.min(1, FACTORY.waveriderFadeRate * dt);
      (mat.userData.uFlow as { value: number }).value = b.waveT;
      (mat.userData.uActive as { value: number }).value = b.waveA;
    }
  }

  // ── TRIPLEXER CAFFEINATION ────────────────────────────────────────────────────────────────────────────────
  // The Triplexer (MMF Caffeinator) upgrades nothing itself. It stamps the ore with FACTORY.triplexerCharges
  // charges, and each of the next N upgraders that actually APPLY an effect to that ore has its effect amplified
  // by FACTORY.triplexerBoost, spending one charge.
  //
  // "+15%" SCALES THE GAIN, NOT THE RAW FACTOR — see the note beside triplexerBoost in defs.ts. A ×M becomes
  // ×(1 + (M−1)·1.15); a +A becomes +(A·1.15); an element imbuer's elemental FACTOR is scaled the same way while
  // the element BIT is untouched. One rule for every machine, so a Triplexer is worth the same 15% of whatever
  // is downstream of it instead of being a lottery on which upgrader happens to have the smallest multiplier.
  //
  // A charge is spent only where an effect is genuinely amplified: a Laser that MISSES (ore off the center line)
  // or absorbs a burn, a Packer arming a bomb, and the Waverider's reset/destroy outcomes all leave the counter
  // alone. An ore that still holds charges is never topped up, so the count is exactly `triplexerCharges`.

  /** Amplify a value/element MULTIPLIER for a caffeinated ore, spending one charge. Returns `mult` unchanged
   *  (and spends nothing) when the ore has no charges left. */
  private caffMult(o: Item, mult: number): number {
    if (!o.caff) return mult;
    o.caff--;
    return 1 + (mult - 1) * (1 + FACTORY.triplexerBoost);
  }

  /** Amplify a FLAT value addition for a caffeinated ore, spending one charge (the "+3" becomes "+3.45"). */
  private caffAdd(o: Item, add: number): number {
    if (!o.caff) return add;
    o.caff--;
    return add * (1 + FACTORY.triplexerBoost);
  }

  /** Per-frame Triplexer visuals: its FOUR violet strips — Union2/Union3 at the entry, Union1/Union4 at the exit —
   *  fade IN while ore are inside the L and fade OUT once it empties. Same presence-fade the leech and waverider
   *  use (`busy` is cleared at the top of every simulate and re-set by the ore pass), eased toward the target so
   *  a single ore rolling through reads as a swell rather than a strobe. Group path only — the Triplexer is not
   *  instanced (it is L-shaped, so its footprint cells differ per rotation), so `setPartGlow` walks the mesh. */
  /** The RECEPTACLE's screen: a plain ore COUNT that ticks up as it swallows them (value is the Scanner's job).
   *  Redrawn only when the number actually changes — a canvas + texture upload every frame for a static readout
   *  would be pure waste. The canvas is built lazily on first paint so placement ghosts never allocate one.
   *  NB this per-building canvas is exactly the kind of state that blocks instancing, which is why the
   *  receptacle stays on the per-Group render path (see PERF.md / MACHINE_INST). */
  private animateReceptacles(): void {
    for (const b of this.buildings.values()) {
      if (b.kind !== "receptacle" || !b.mesh) continue;
      const n = b.through ?? 0;
      const scr = b.mesh.getObjectByName("Part1") as THREE.Mesh | undefined;
      if (!scr) continue;
      const ud = scr.userData as { cv?: HTMLCanvasElement; cx?: CanvasRenderingContext2D; tex?: THREE.CanvasTexture; shown?: number };
      if (ud.shown === n) continue;
      if (!ud.cv) {
        const cv = document.createElement("canvas");
        cv.width = 256; cv.height = 128;
        ud.cv = cv; ud.cx = cv.getContext("2d")!;
        ud.tex = new THREE.CanvasTexture(cv);
        ud.tex.colorSpace = THREE.SRGBColorSpace; // authored in sRGB like every other color here
        const mt = scr.material as THREE.MeshStandardMaterial;
        mt.map = ud.tex;
        mt.emissive.setHex(0xffffff); mt.emissiveMap = ud.tex; mt.emissiveIntensity = 0.9; // it is a lit panel
        mt.needsUpdate = true;
        scr.layers.enable(BLOOM_LAYER);
        this.world.bloomActive = true;
      }
      const cx = ud.cx!;
      cx.fillStyle = "#06131a"; cx.fillRect(0, 0, 256, 128);
      cx.fillStyle = "#7cf6ff";
      cx.font = "bold 84px 'Chakra Petch', 'Space Mono', monospace";
      cx.textAlign = "center"; cx.textBaseline = "middle";
      cx.fillText(String(n), 128, 68);
      ud.tex!.needsUpdate = true;
      ud.shown = n;
    }
  }

  /** Ability flash colors. Idle is the model's own red star; each action punches a different hue in and it
   *  decays back. Kept as a table so retuning is one line, not a hunt through the animate pass. */
  private static readonly OBL_HUES = { clear: 0xffe07a, pause: 0x59d5ff, resume: 0x8cff7a, step: 0xc78bff };

  /** Wipe every ore THIS factory owns. Scoped to `this.items` — the enemy Factory has its own pool and is not
   *  reachable from here, which is the whole point: the panic button must never touch the other team. */
  obliterate(): number {
    let n = 0;
    for (const o of this.items) {
      if (!o.active || o.part) continue;   // parts are the unit line, not ore — leave them alone
      if (o.bodyH != null && this.jolt) { this.jolt.despawnOre(o.bodyH); o.bodyH = undefined; }
      o.active = false;
      n++;
    }
    this.flashObliterators("clear");
    this.hud?.flash(`obliterated ${n} ore`);
    return n;
  }

  /** Punch the spike ring to an ability's hue; it eases back to red on its own. */
  private flashObliterators(kind: keyof typeof Factory.OBL_HUES): void {
    this.oblHue.setHex(Factory.OBL_HUES[kind]);
    this.oblFlash = 1;
  }

  setOrePaused(on: boolean): void {
    if (this.orePaused === on) return;
    this.orePaused = on;
    this.flashObliterators(on ? "pause" : "resume");
    this.hud?.setOrePaused(on);
  }

  stepOre(frames = 1): void {
    if (!this.orePaused) return;   // stepping only means anything against a stopped sim
    this.oreStep += frames;
    this.flashObliterators("step");
  }

  /** Does this factory own an Obliterator? Drives whether its control panel is offered at all. */
  hasObliterator(): boolean {
    for (const b of this.buildings.values()) if (b.kind === "obliterator") return true;
    return false;
  }

  /** The spike ring spins and pulses like a star. Color is per-INSTANCE (the vec3 emissive attribute), so an
   *  ability flash tints THIS factory's rings without touching the shared material or the enemy's. */
  private animateObliterators(dt: number): void {
    this.oblSpin += FACTORY.obliteratorSpin * dt;
    this.oblFlash = Math.max(0, this.oblFlash - FACTORY.obliteratorFlashDecay * dt);
    // smooth star pulse, plus a bright kick while an ability flash is live
    const puls = 0.5 - 0.5 * Math.cos(performance.now() / 1000 * FACTORY.obliteratorPulseHz * Math.PI * 2);
    const lo = FACTORY.obliteratorIdleLo, hi = FACTORY.obliteratorIdleHi;
    const amp = lo + (hi - lo) * puls + this.oblFlash * 2.2;
    // ease the flash hue back toward the idle red rather than snapping
    this._col.setHex(0xff1919).lerp(this.oblHue, this.oblFlash);
    this.oblCol.copy(this._col);   // the flare pass reads this (it runs in a different call)
    for (const b of this.buildings.values()) {
      if (b.kind !== "obliterator") continue;
      if (b.inst && b.mat) {
        this._partMat.copy(b.mat).multiply(this._partOff.makeRotationY(this.oblSpin));
        this.instancer.setMatrix(b.inst.key, b.inst.slot, "Union2", this._partMat);
        this.instancer.setEmissiveColor(b.inst.key, b.inst.slot, "Union2",
          this._col.r * amp, this._col.g * amp, this._col.b * amp);
      } else if (b.mesh) {
        const ring = b.mesh.getObjectByName("Union2") as THREE.Mesh | undefined;
        if (ring) {
          ring.rotation.y = this.oblSpin;
          const mt = ring.material as THREE.MeshStandardMaterial;
          mt.emissive.copy(this._col); mt.emissiveIntensity = amp;
        }
      }
    }
  }

  private animateTriplexers(dt: number): void {
    const STRIPS = ["Union1", "Union2", "Union3", "Union4"];
    for (const b of this.buildings.values()) {
      if (b.kind !== "triplexer" || (!b.mesh && !b.inst)) continue;
      b.neonA = (b.neonA ?? 0) + ((b.busy ? 1 : 0) - (b.neonA ?? 0)) * Math.min(1, FACTORY.triplexerFadeRate * dt);
      const v = FACTORY.triplexerNeonIdle + (b.neonA ?? 0) * (FACTORY.triplexerNeonLit - FACTORY.triplexerNeonIdle);
      for (const n of STRIPS) this.setPartGlow(b, n, v);
    }
  }

  /** Roll the Waverider's random outcome for one ore, ONCE per pass (see WAVERIDER_TABLE — adapted MH Schrödinger). */
  private rollWaverider(o: Item, dir: Dir): void {
    let r = Math.random() * WAVERIDER_TABLE_TOTAL, e = WAVERIDER_TABLE[0];
    for (const entry of WAVERIDER_TABLE) { if (r < entry.w) { e = entry; break; } r -= entry.w; }
    if (e.kind === "mult") {
      o.value *= this.caffMult(o, e.x!);   // caffeine amplifies a WIN; the reset/destroy outcomes below spend nothing
      if (e.fling) {
        // Knocked into the air. The waverider branch re-pins py to the deck every frame the ore is over its belt
        // (CATCH band = 8), which would swallow the launch — so pop the ore clear of that band NOW, then let the
        // strong upward kick + gravity arc it. The horizontal kick is biased FORWARD along the belt so it usually
        // arcs down onto the belt ahead (keeping its rolled value); a small sideways wobble spills some off the lane.
        const [dx, dz] = DIR_VEC[dir];
        const fwd = 70 + Math.random() * 130, side = (Math.random() - 0.5) * 150; // forward boost + perpendicular wobble
        const kx = dx * fwd - dz * side, kz = dz * fwd + dx * side;
        if (this.jolt && o.bodyH != null) {
          // Under Jolt it's a REAL launch: the ore arcs, tumbles (DOF unlocked), and COLLIDES with the stream on
          // the way down — the whole point of the pivot. No py nudge needed; nothing re-pins it to the deck.
          this.jolt.applyFling(o.bodyH, kx, FACTORY.waveriderFlingUp, kz);
          o.flung = true; // re-locked to no-tumble when it lands (oreEconomyPass)
        } else {
          o.vy = FACTORY.waveriderFlingUp;
          o.py += 14; // > CATCH(8): clears the deck band so the belt can't snap it back down this frame
          o.vx += kx; o.vz += kz;
        }
      }
    } else if (e.kind === "reset") {
      o.value = this.oreStartValue; // its "set to $1" — strip accumulated value back to base
    } else if (e.kind === "destroy") {
      o.active = false; this.fling(o.px, o.py + 20, o.pz, 130, 0x3a7bff); // gone
    } else { // explode
      o.active = false; this.fling(o.px, o.py + 20, o.pz, 240, 0xff4a4a); // destroyed, red burst
    }
  }

  /** P5: when total launched crosses a rarity milestone, announce the buildings it unlocks (a "pack"). */
  private checkUnlocks(before: number, after: number): void {
    const freshly: string[] = [];
    for (const kind of PALETTE) { const at = unlockAt(kind); if (at > before && at <= after) freshly.push(BUILDINGS[kind].name); }
    if (freshly.length) { this.hud?.pack(freshly); this.hud?.refreshLocks(after); }
  }

  private deployUnit(tier: number, d: Placed): void {
    this.deploy(this.team, tier >= FACTORY.giantTier ? "giant" : "little");
    this.partsWasted = 0; // the line is delivering — clear the waste streak
    this.fling(this.worldX(d.gx), this.baseY + 80, this.worldZ(d.gz), 320, this.team === 0 ? 0x8fd6ff : 0xff8a7a);
  }

  private noteWaste(): void { this.partsWasted++; this.sinceWaste = 0; }

  /** Warn (throttled) when a fabricator keeps spending materials on parts that never reach a deployer.
   *  Only fires while waste is ONGOING (sinceWaste small) so an old, no-longer-draining break goes quiet. */
  private updateWasteWarning(dt: number): void {
    this.sinceWaste += dt;
    this.wasteWarnCd -= dt;
    if (this.partsWasted >= 3 && this.sinceWaste < 1.5 && this.wasteWarnCd <= 0) {
      this.hud?.flash("⚠ parts lost — route the belt into a deployer");
      this.wasteWarnCd = 5;
    }
  }

  private fling(x: number, y: number, z: number, up: number, color: number): void {
    for (const l of this.launches) {
      if (l.active) continue;
      l.active = true;
      l.pos.set(x, y, z);
      l.vel.set((Math.random() - 0.5) * 60, up, (Math.random() - 0.5) * 60);
      l.life = 1.2;
      l.mesh.visible = true;
      (l.mesh.material as THREE.MeshBasicMaterial).color.setHex(color);
      (l.mesh.material as THREE.MeshBasicMaterial).opacity = 1;
      return;
    }
  }

  private updateLaunches(dt: number): void {
    for (const l of this.launches) {
      if (!l.active) continue;
      l.vel.y -= 400 * dt;
      l.pos.addScaledVector(l.vel, dt);
      l.life -= dt;
      l.mesh.position.copy(l.pos);
      const k = Math.max(0, l.life / 1.2);
      (l.mesh.material as THREE.MeshBasicMaterial).opacity = k;
      l.mesh.scale.setScalar(0.5 + k);
      if (l.life <= 0) { l.active = false; l.mesh.visible = false; }
    }
  }

  private renderItems(): void {
    const d = this._dummy, c = this._c;
    let i = 0, colorDirty = false;
    for (const o of this.items) {
      if (!o.active) continue;
      if (o.part) {
        this.tileCenter(o.gx, o.gz, o.floor, c); // c.y = this floor's height
        const [dx, dz] = DIR_VEC[o.dir];
        const off = (o.t - 0.5) * FACTORY.tile; d.position.set(c.x + dx * off, c.y + FACTORY.oreY, c.z + dz * off);
        d.rotation.set(0, o.t * 6, 0);
      } else {
        d.position.set(o.px, o.py, o.pz);      // PHYSICS ore rides at its own world position...
        d.quaternion.set(o.qx, o.qy, o.qz, o.qw); // ...at its own ORIENTATION. Zeroing this drew every ore
        // axis-aligned and made the sim look direction-locked, when the bodies were tumbling the whole time.
      }
      // ORE ARE A FIXED SIZE. Value used to scale them up to ×2.4, which drew a lie: the collider is always
      // PHYS.half, so a "huge" ore still collided as a 24-unit cube — every pile, arch and jam we tuned was
      // being rendered at a size the solver never saw. Worth is shown in LIGHT instead (below).
      d.scale.setScalar(o.part ? 0.9 + o.tier * 0.18 : (PHYS.half * 2) / 20);
      d.updateMatrix();
      this.itemMesh.setMatrixAt(i, d.matrix);
      this.instItem[i] = o; // this loop REPACKS instance slots every frame, so a raycast hit means nothing without it
      if (o.part) {
        this.itemMesh.setColorAt(i, this._col.copy(PART_COL).offsetHSL(0, 0, o.tier * 0.08));
        this.oreAttr.setXY(i, 0, 0);
        this.sparkAttr.setXYZ(i, 0, 0, 0);
      } else {
        // WORTH → one trip round the color wheel. Dim red at the bottom, through orange/green/blue/violet, and
        // back to red at the top — but blazing and sparkling by then, so the two reds never read alike.
        const worth = this.oreValue(o.value, o.elem, o.elemMask);
        // log2(worth), NOT log2(worth+1): a base ore is worth 1, and log2(1)=0 puts it at pure red with zero
        // glow — the bottom of the wheel. (+1 started it a tenth of the way round, at orange.)
        const t = Math.min(1, Math.max(0, Math.log2(Math.max(1, worth))) / FACTORY.oreHueSpan);
        this._col.setHSL(t, 0.85, 0.34 + 0.26 * t);   // hue 0(red) → 1(red again); brightens as it climbs
        this.itemMesh.setColorAt(i, this._col);
        // glow ramps QUADRATICALLY (the top end is the spectacle); the seed is a FRACTION, not the raw uid —
        // it is multiplied by 2π in the shader, and a uid in the thousands there would wreck the phase.
        this.oreAttr.setXY(i, t * t, ((o.uid ?? 0) * 0.6180339887) % 1);
        // sparkle color = the elements it carries (white-ish when it carries none, so plain ore still twinkle faintly)
        const c = this._sparkC.setRGB(0, 0, 0); let en = 0;
        for (let b = 0; b < 5; b++) if (o.elemMask & (1 << b)) { c.add(ELEM_COL[b]); en++; }
        if (en) c.multiplyScalar(1 / en); else c.copy(ORE_COL);
        this.sparkAttr.setXYZ(i, c.r, c.g, c.b);
      }
      colorDirty = true;
      if (++i >= FACTORY.oreCap) break;
    }
    d.scale.setScalar(0); d.position.set(0, -100000, 0); d.rotation.set(0, 0, 0); d.updateMatrix();
    for (; i < FACTORY.oreCap; i++) { this.itemMesh.setMatrixAt(i, d.matrix); this.instItem[i] = null; }
    this.itemMesh.instanceMatrix.needsUpdate = true;
    if (colorDirty && this.itemMesh.instanceColor) this.itemMesh.instanceColor.needsUpdate = true;
    this.oreAttr.needsUpdate = true; this.sparkAttr.needsUpdate = true; // glow/seed + sparkle color, uploaded with the matrices
    if (i > 0) this.world.bloomActive = true; // ore are on BLOOM_LAYER; a dim one bleeds nothing, a rich one blazes
    this.updateOreFlares();
  }

  // ---- ghost preview ----------------------------------------------------------------------------
  private rebuildGhost(): void {
    disposeChildren(this.ghost);
    this.ghostKind = this.sel === BULLDOZE ? null : this.sel;
    if (this.ghostKind) this.ghost.add(this.makeMesh(this.ghostKind, this.ghostDir, true));
    else this.ghost.add(this.tileMarker(0xff5a5a));
  }

  private updateGhost(): void {
    if (!this.hover) { this.ghost.visible = false; return; }
    this.ghost.visible = true;
    this.ghost.position.copy(this.tileCenter(this.hover.gx, this.hover.gz, this.activeFloor, this._hit));
    if (this.sel === "receptacle") this.fitReceptacleTube(this.ghost); // preview the real tube depth, not the stub
    // Do NOT rotate the group by DIR_YAW here: rebuildGhost() already bakes the orientation into the
    // child via makeMesh(kind, ghostDir), exactly like a placed building. Rotating the group too
    // double-applied it, so R stepped the PREVIEW by 180° (only 2 of 4 orientations ever shown) while
    // the real placement rotated a correct 90°. Keep the group upright — the child carries the facing.
    this.ghost.rotation.y = 0;
    const tk = key(this.hover.gx, this.hover.gz, this.activeFloor);
    const occupied = this.buildings.has(tk);
    let ok: boolean;
    if (this.sel === BULLDOZE) {
      ok = occupied || this.mods.has(tk); // a cooler is bulldozeable too; the receptacle is now an ordinary building
    } else if (this.sel === "cryo") {
      const u = this.buildings.get(tk); // valid only ON TOP of a conveyor / upgrader / blaster with no cooler yet
      ok = !!u && (u.kind === "conveyor" || u.kind === "magnetron" || isUpgrader(u.kind)) && !this.mods.has(tk) && this.materials >= BUILDINGS.cryo.cost;
    } else {
      ok = !occupied && !this.reserved.has(tk) && !this.rampCells.has(tk) && this.materials >= BUILDINGS[this.sel].cost;
      // a multi-tile building's OTHER footprint cells must be free too, or the ghost reads placeable and isn't
      const ex = this.extraCells(this.sel, this.hover.gx, this.hover.gz, this.activeFloor, this.ghostDir);
      const ob = FOOTPRINT_OUTBOARD.has(this.sel as BuildingKind);
      if (ex.some((e) => (!ob && !this.inZone(e.gx, e.gz)) || this.buildings.has(e.k) || this.reserved.has(e.k) || this.rampCells.has(e.k) || this.mods.has(e.k))) ok = false;
    }
    this.ghost.traverse((o) => {
      const raw = (o as THREE.Mesh).material;
      const mats = Array.isArray(raw) ? raw : raw ? [raw] : []; // model parts may be multi-material
      for (const m of mats) if (m && "emissive" in m) (m as THREE.MeshStandardMaterial).emissive.setHex(ok ? 0x2b6b2b : 0x6b1f1f);
    });
  }

  // ---- meshes -----------------------------------------------------------------------------------
  private tint(hex: number): number { return this.team === 0 ? hex : new THREE.Color(hex).lerp(RED_TINT, 0.4).getHex(); }

  /** Instantiate a loaded FactoryModel into `g`: per-part materials (team-tinted, opacity-aware so glass parts
   *  stay transparent, ghost-fadeable), cloned geometry (disposal-safe), + collider / drop-point metadata. */
  private addModel(g: THREE.Group, model: FactoryModel, ghost: boolean): void {
    for (const part of model.parts) {
      const mats = part.colors.map((c, i) => {
        const op = part.opacities[i] ?? 1;
        const glass = op < 1;
        const m = new THREE.MeshStandardMaterial({
          color: this.tint(c), roughness: 0.7,
          transparent: ghost || glass,
          opacity: ghost ? 0.55 : op,
          depthWrite: !glass, // glass blends cleanly instead of depth-fighting its own faces
        });
        m.userData.baseOpacity = op; // floor-view dimmer keeps glass transparent instead of forcing it opaque
        return m;
      });
      const pm = new THREE.Mesh(part.geometry.clone(), mats.length === 1 ? mats[0] : mats);
      pm.name = part.name; g.add(pm);
    }
    if (model.collider) g.userData.collider = model.collider;
    if (model.dropPoints.length) g.userData.dropPoints = model.dropPoints;
  }

  /** The standard conveyor DECK color (near-black), read straight from the CONVEYOR model. The leech/infernace ship
   *  their OWN belt geometry whose deck part exported a gray base-color (a Blender `usepartcolor`-off quirk — the
   *  union's stored color got written instead of the intended black), so we normalize those belts to this. Drop the
   *  overrides once those source models are re-exported with the correct part color. */
  private conveyorDeckColor(): number {
    return this.tint(CONVEYOR.parts.find((p) => p.name === "Union1")?.colors[0] ?? 0x090909);
  }

  /** The conveyor's SUPPORT gray (its `Union2`), for models whose support is authored some other color.
   *  Belt vs support is told apart by SHAPE, not color: 100×20×100 is the belt, ~100×15×102 the support. */
  private conveyorSupportColor(): number {
    return this.tint(CONVEYOR.parts.find((p) => p.name === "Union2")?.colors[0] ?? 0xa4a3a6);
  }

  /** Resolve (and cache, once per kind per Factory) the instancer config for a MACHINE_INST kind.
   *  The shared FactoryModel carries the AUTHORED colors, but the Group path did two things to them before
   *  drawing: `addModel` team-tinted every part, and makeMesh repainted the deck part belt-black. Both have to
   *  happen HERE instead, because the pool bakes one material per part for every building of the kind. Geometry
   *  is shared by reference — Pool clones it per pool, so this copy owns nothing that needs disposing.
   *  Each neon hue is READ BACK from the tinted color, which is exactly what `mt.emissive.copy(mt.color)` did. */
  private machineConf(kind: BuildingKind): InstConf | undefined {
    const cached = this.instConfs.get(kind);
    if (cached) return cached;
    const md = MACHINE_INST[kind];
    if (!md) return undefined;
    const deckHex = this.conveyorDeckColor(), supHex = this.conveyorSupportColor();
    const asSet = (v?: string | string[]) => new Set(v == null ? [] : (Array.isArray(v) ? v : [v]));
    const decks = asSet(md.deck), sups = asSet(md.support);
    const parts = md.model.parts.map((p) => ({
      ...p,
      colors: decks.has(p.name) ? p.colors.map(() => deckHex)
            : sups.has(p.name)  ? p.colors.map(() => supHex)
            : p.colors.map((c) => this.tint(c)),
    }));
    const model = { ...md.model, parts } as FactoryModel;
    const hues = new Set(md.hueParts ?? []);
    const neon = md.neon
      ? (Object.fromEntries(md.neon.map((n): [string, NeonSpec] => {
          const col = parts.find((p) => p.name === n)?.colors[0] ?? 0xffffff;
          // A hue part keeps its authored surface color and bakes WHITE emissive, so the instance attribute is
          // the emissive color outright; a plain neon part is the fixed-hue form (color == emissive == hue).
          return [n, hues.has(n) ? { color: col, emissive: 0xffffff, maskedBloom: true } : col];
        })) as Record<string, NeonSpec>)
      : undefined;
    const conf: InstConf = { key: `m_${kind}`, model, neon, arrow: md.arrow, rest: md.rest, lazyBloom: md.lazyBloom };
    this.instConfs.set(kind, conf);
    return conf;
  }

  /** Set a building part's glow whichever path it renders on. Instanced buildings have no `mesh` to walk — their
   *  brightness is a per-instance attribute — so every animate pass goes through here instead of getObjectByName. */
  private setPartGlow(b: Placed, part: string, v: number): void {
    if (b.inst) { this.instancer.setEmissive(b.inst.key, b.inst.slot, part, v); return; }
    const m = b.mesh?.getObjectByName(part) as THREE.Mesh | undefined;
    if (m) (m.material as THREE.MeshStandardMaterial).emissiveIntensity = v;
  }

  private makeMesh(kind: BuildingKind, dir: Dir, ghost = false): THREE.Group {
    const g = new THREE.Group();
    const def = BUILDINGS[kind];
    const T = FACTORY.tile;
    const F = T * 0.9;   // 90 — UNIFORM footprint: every buildable fills its grid cell identically
    const BH = 72;       // UNIFORM "machine" body-block height; every machine tops out at ~TOP (identity
    const TOP = 88;      // ornament reaches TOP), so floors stack predictably. Belts are flat; lift/chute span floors.
    const body = this.tint(def.color);
    const mat = (color: number, emissive = 0x000000, ei = 0): THREE.MeshStandardMaterial =>
      new THREE.MeshStandardMaterial({ color, emissive, emissiveIntensity: ei, roughness: 0.7, transparent: ghost, opacity: ghost ? 0.55 : 1 });

    // EVERY buildable = a uniform F×F footprint. Transport pieces are a flat belt; "machines" are a BH-tall
    // base block; each carries its own distinctive TOP ornament for identity. (Placeholders — swap for models.)
    if (kind === "conveyor") {
      this.addModel(g, CONVEYOR, ghost); // Union1 = 100×20×100 deck (collider); Union2 = decorative flare
      g.add(this.beltArrow(ghost));
    } else if (kind === "splitter") {
      this.addModel(g, SPLITTER, ghost); // 1 intake + 3 output holes + no-collision black center (ore enters, gets flung out one output)
    } else if (kind === "wall") {
      this.addModel(g, WALL, ghost); // thin edge barrier: gold post + translucent panel, hugs one tile edge
    } else if (kind === "blaster") {
      this.addModel(g, BLASTER, ghost);
      for (const n of ["Union1", "Part1"]) { // the two red parts are NEON: high emissive + real selective bloom bleed
        const m = g.getObjectByName(n) as THREE.Mesh | undefined;
        if (m) { const mt = m.material as THREE.MeshStandardMaterial; mt.emissive.setHex(0xff2a2a); mt.emissiveIntensity = ghost ? 0.3 : 0.7; if (!ghost) m.layers.enable(BLOOM_LAYER); }
      }
      if (!ghost) this.world.bloomActive = true; // turn the selective-bloom pass on now that a neon object exists
    } else if (kind === "cryo") {
      this.addModel(g, FREEZER, ghost); // overhead cooler: gray housing (Union1) + top flywheel (Union3) + blue coil (Union2)
      const coil = g.getObjectByName("Union2") as THREE.Mesh | undefined; // the blue ring is NEON (real blue bloom bleed, like the blaster's red)
      if (coil) { const mt = coil.material as THREE.MeshStandardMaterial; mt.emissive.setHex(0x59d5ff); mt.emissiveIntensity = ghost ? 0.3 : 0.85; if (!ghost) coil.layers.enable(BLOOM_LAYER); }
      if (!ghost) this.world.bloomActive = true;
    } else if (kind === "pressurizer") {
      this.addModel(g, PRESSURIZER, ghost); // batch oven: red neon strip (Union1) + housing + door panels (Part1 entrance / Part2 exit)
      const neon = g.getObjectByName("Union1") as THREE.Mesh | undefined; // the red strip is STATIC neon (constant glow)
      if (neon) { const mt = neon.material as THREE.MeshStandardMaterial; mt.emissive.setHex(0xff2a2a); mt.emissiveIntensity = ghost ? 0.3 : 0.9; if (!ghost) neon.layers.enable(BLOOM_LAYER); }
      for (const n of ["Part1", "Part2"]) { // the doors are semi-transparent so you can watch ore cook inside (0.3 transparent ⇒ 0.7 opaque)
        const d = g.getObjectByName(n) as THREE.Mesh | undefined;
        if (d) { const mt = d.material as THREE.MeshStandardMaterial; mt.transparent = true; mt.opacity = ghost ? 0.4 : 0.7; mt.depthWrite = false; }
      }
      if (!ghost) this.world.bloomActive = true;
    } else if (kind === "magnetron") {
      this.addModel(g, CONVEYOR, ghost);   // the bundled conveyor deck ore rides through (the magnetron comes WITH it)
      g.add(this.beltArrow(ghost));        // flow direction
      const disk = new THREE.Group();      // the hovering magnetic disk — spins + bobs (never below magnetronMinY), no glow
      this.addModel(disk, MAGNETRON, ghost);
      disk.position.y = FACTORY.magnetronHoverY;
      disk.name = "magDisk";
      g.add(disk);
    } else if (kind === "infernace") {
      this.addModel(g, INFERNACE, ghost); // self-contained inline furnace (own deck + channel); Union4 = 4-oval top that stamps down. No glow.
      const idk = g.getObjectByName("Union3") as THREE.Mesh | undefined; // ONLY the deck (Union3, 100×20×100) → standard belt black; Union1 is the SUPPORT — keep its model gray (≈163,162,165)
      if (idk) (idk.material as THREE.MeshStandardMaterial).color.setHex(this.conveyorDeckColor());
    } else if (kind === "ecobox") {
      this.addModel(g, ECOBOX, ghost);     // MMF Infestor: own deck (Union1) the ore rides through + dark-green housing (Union4)
      g.add(this.beltArrow(ghost));
      const edk = g.getObjectByName("Union1") as THREE.Mesh | undefined; // ONLY the deck (Union1, 100×20×100) → standard belt black; Union2 is the SUPPORT — keep its model gray (≈163,162,165)
      if (edk) (edk.material as THREE.MeshStandardMaterial).color.setHex(this.conveyorDeckColor());
      const neon = g.getObjectByName("Union3") as THREE.Mesh | undefined; // green neon (Union3): color READ from the model; dim idle glow, pulses on each ore pass (animateEcoboxes)
      if (neon) { const mt = neon.material as THREE.MeshStandardMaterial; mt.emissive.copy(mt.color); mt.emissiveIntensity = ghost ? 0.3 : 0.25; if (!ghost) neon.layers.enable(BLOOM_LAYER); }
      if (!ghost) this.world.bloomActive = true;
    } else if (kind === "packer") {
      this.addModel(g, PACKER, ghost);         // MMF Bomb Packer: own deck (Union4) + dark navy casing
      g.add(this.beltArrow(ghost));
      const pDeck = g.getObjectByName("Union4") as THREE.Mesh | undefined;
      if (pDeck) (pDeck.material as THREE.MeshStandardMaterial).color.setHex(this.conveyorDeckColor());
      const arm = g.getObjectByName("Union1") as THREE.Mesh | undefined;   // red arming strip: color READ from the model
      if (arm) { const mt = arm.material as THREE.MeshStandardMaterial; mt.emissive.copy(mt.color); mt.emissiveIntensity = ghost ? 0.3 : 0.2; if (!ghost) arm.layers.enable(BLOOM_LAYER); }
      if (!ghost) this.world.bloomActive = true;
    } else if (kind === "vat") {
      this.addModel(g, VAT, ghost);            // MMF Vat: a tank with a spout overhanging the tile (drop point at its tip)
      const surf = g.getObjectByName("Part2") as THREE.Mesh | undefined;   // the red liquid surface glows as it works
      if (surf) { const mt = surf.material as THREE.MeshStandardMaterial; mt.emissive.copy(mt.color); mt.emissiveIntensity = ghost ? 0.3 : 0.25; if (!ghost) surf.layers.enable(BLOOM_LAYER); }
      if (!ghost) this.world.bloomActive = true;
    } else if (kind === "mutator") {
      this.addModel(g, MUTATOR, ghost);        // MMF Mutator: own deck (Union4) + burnt-orange reactor housing
      g.add(this.beltArrow(ghost));
      const mDeck = g.getObjectByName("Union4") as THREE.Mesh | undefined; // 100×20×100 deck → belt black (Union2 is the 102-long gray frame)
      if (mDeck) (mDeck.material as THREE.MeshStandardMaterial).color.setHex(this.conveyorDeckColor());
      const mn = g.getObjectByName("Union3") as THREE.Mesh | undefined;    // reactor block: color READ from the model, flares on each mutation
      if (mn) { const mt = mn.material as THREE.MeshStandardMaterial; mt.emissive.copy(mt.color); mt.emissiveIntensity = ghost ? 0.3 : 0.14; if (!ghost) mn.layers.enable(BLOOM_LAYER); }
      if (!ghost) this.world.bloomActive = true;
    } else if (kind === "laser") {
      this.addModel(g, LASER, ghost);          // MMF Laser: own deck (Union3) + tall red emitter (Union2)
      g.add(this.beltArrow(ghost));
      const lDeck = g.getObjectByName("Union3") as THREE.Mesh | undefined; // 100×20×100 deck → belt black (Union1 is the gray frame)
      if (lDeck) (lDeck.material as THREE.MeshStandardMaterial).color.setHex(this.conveyorDeckColor());
      const em = g.getObjectByName("Union2") as THREE.Mesh | undefined;    // emitter: pure red from the model; fires on a hit, stays lit while charged
      if (em) { const mt = em.material as THREE.MeshStandardMaterial; mt.emissive.copy(mt.color); mt.emissiveIntensity = ghost ? 0.3 : 0.12; if (!ghost) em.layers.enable(BLOOM_LAYER); }
      if (!ghost) this.world.bloomActive = true;
    } else if (kind === "teleportera" || kind === "teleporterb") {
      this.addModel(g, kind === "teleportera" ? TELEPORTER_A : TELEPORTER_B, ghost);
      // Match the real conveyor, which identifies its pieces by SHAPE, not by authored color:
      //   100×20×100  = the BELT    → belt black   (conveyor Union1, Kd ≈ 0.0026)
      //   ~100×15×102 = the SUPPORT → support gray (conveyor Union2, Kd ≈ 0.374)
      // The teleporter's .mtl is misleading here — its belt (Union1) is authored gray and its support (Union3)
      // pure cyan, so going by color gets both backwards. Union3 being cyan is why I first lit it as "the pad";
      // it is the support, and the thing that should actually glow is the rings.
      const dk = g.getObjectByName("Union1") as THREE.Mesh | undefined;   // 100×20×100 → the belt
      if (dk) (dk.material as THREE.MeshStandardMaterial).color.setHex(this.conveyorDeckColor());
      const sup = g.getObjectByName("Union3") as THREE.Mesh | undefined;  // 95×15×102 → the support
      if (sup) (sup.material as THREE.MeshStandardMaterial).color.setHex(this.conveyorSupportColor());
    } else if (kind === "triplexer") {
      this.addModel(g, TRIPLEXER, ghost);      // MMF Caffeinator: L-shaped 3-tile belt, ore enters one end and leaves 90° round the corner
      // all six deck segments (entry / corner / exit, each a base + a slightly wider rider) go belt-black
      for (const n of ["Union6", "Union7", "Union8", "Union9", "Union10", "Union11"]) {
        const d = g.getObjectByName(n) as THREE.Mesh | undefined;
        if (d) (d.material as THREE.MeshStandardMaterial).color.setHex(this.conveyorDeckColor());
      }
      // four violet strips — TWO PER END (Union2/Union3 at the entry, Union1/Union4 at the exit). Color READ
      // from the model; they fade in/out with whether ore are actually inside (animateTriplexers), starting at
      // `triplexerNeonIdle` and easing up to `triplexerNeonLit`.
      for (const n of ["Union1", "Union2", "Union3", "Union4"]) {
        const m = g.getObjectByName(n) as THREE.Mesh | undefined;
        if (!m) continue;
        const mt = m.material as THREE.MeshStandardMaterial;
        mt.emissive.copy(mt.color); mt.emissiveIntensity = ghost ? 0.3 : FACTORY.triplexerNeonIdle;
        if (!ghost) m.layers.enable(BLOOM_LAYER);
      }
      if (!ghost) this.world.bloomActive = true;
    } else if (kind === "diversifier") {
      this.addModel(g, DIVERSIFIER, ghost);    // MMF Diversifier: own deck (Union3) + three R/G/B slabs straddling the lane
      g.add(this.beltArrow(ghost));
      const vDeck = g.getObjectByName("Union3") as THREE.Mesh | undefined; // the 100×20×100 deck → standard belt black (Union1 is the 102-long gray frame, Union2 the housing)
      if (vDeck) (vDeck.material as THREE.MeshStandardMaterial).color.setHex(this.conveyorDeckColor());
      for (const n of ["Union4", "Union5", "Union6"]) { // R/G/B NEON (color READ from the model): idle dim, and one lights per element the passing ore carries
        const m = g.getObjectByName(n) as THREE.Mesh | undefined;
        if (m) { const mt = m.material as THREE.MeshStandardMaterial; mt.emissive.copy(mt.color); mt.emissiveIntensity = ghost ? 0.3 : 0.16; if (!ghost) m.layers.enable(BLOOM_LAYER); }
      }
      if (!ghost) this.world.bloomActive = true;
    } else if (kind === "scanner") {
      this.addModel(g, SCANNER, ghost);        // MH Ore Tracker: own deck (Union5) ore rides through; rings encircle the belt
      g.add(this.beltArrow(ghost));
      const sDeck = g.getObjectByName("Union5") as THREE.Mesh | undefined; // deck → standard belt black (support Union4 keeps its model gray)
      if (sDeck) (sDeck.material as THREE.MeshStandardMaterial).color.setHex(this.conveyorDeckColor());
      for (const n of ["Union1", "Union2"]) { // NEON (color READ from the model): dim idle glow, pulses per pass. Union1 = the spinning ring; Union2 = a STATIC neon arc that ties into it for continuity.
        const m = g.getObjectByName(n) as THREE.Mesh | undefined;
        if (m) { const mt = m.material as THREE.MeshStandardMaterial; mt.emissive.copy(mt.color); mt.emissiveIntensity = 0.3; if (!ghost) m.layers.enable(BLOOM_LAYER); }
      }
      // ONLY the neon RING (Union1 — pure blocks arranged in a circle, already CENTERED on the belt) spins; the Union2
      // neon arc + the big Union3 housing stay static. Pivot the ring on its OWN center (= the belt axis) so it spins
      // IN PLACE — animateScanners rotates it about that axis (ore passes THROUGH, MRI-style).
      const ring = g.getObjectByName("Union1") as THREE.Mesh | undefined;
      if (ring) {
        ring.geometry.computeBoundingBox();
        const rc = ring.geometry.boundingBox!.getCenter(new THREE.Vector3());
        const rings = new THREE.Group(); rings.name = "scanRings";
        rings.position.copy(rc); ring.position.sub(rc); rings.add(ring); g.add(rings);
      }
      if (!ghost) this.world.bloomActive = true;
    } else if (kind === "waverider") {
      this.addModel(g, WAVERIDER, ghost);   // 2-tile-long: Union2/Union4 deck segments, Union1/Union5 housing, Union6 = blue neon strip
      g.add(this.beltArrow(ghost));
      const wDeck = this.conveyorDeckColor();
      for (const n of ["Union2", "Union4"]) { const d = g.getObjectByName(n) as THREE.Mesh | undefined; if (d) (d.material as THREE.MeshStandardMaterial).color.setHex(wDeck); } // both deck segments → standard belt black
      const neon = g.getObjectByName("Union6") as THREE.Mesh | undefined; // blue neon strip: color READ from the model; waves along its length (animateWaveriders)
      if (neon) { const mat = this.waveNeonMaterial(ghost, (neon.material as THREE.MeshStandardMaterial).color.getHex()); neon.material = mat; if (!ghost) neon.layers.enable(BLOOM_LAYER); g.userData.waveMat = mat; }
      if (!ghost) this.world.bloomActive = true;
    } else if (kind === "leech") {
      this.addModel(g, LEECH, ghost);      // tall tower with its own belt (Union2); Part1..4 = the 4 yellow neon stake-columns
      const belt = g.getObjectByName("Union2") as THREE.Mesh | undefined;
      if (belt) (belt.material as THREE.MeshStandardMaterial).color.setHex(this.conveyorDeckColor()); // gray deck → standard belt black
      g.add(this.beltArrow(ghost));
      const p1 = g.getObjectByName("Part1") as THREE.Mesh | undefined;
      const hue = p1 ? (p1.material as THREE.MeshStandardMaterial).color.getHex() : 0xefb838; // READ the neon color from the model
      const neonMat = this.leechNeonMaterial(ghost, hue); // ONE shared flowing-neon material for all 4 columns (scrolls up, faded by uActive)
      for (const n of ["Part1", "Part2", "Part3", "Part4"]) { const m = g.getObjectByName(n) as THREE.Mesh | undefined; if (m) { m.material = neonMat; if (!ghost) m.layers.enable(BLOOM_LAYER); } }
      g.userData.neonMat = neonMat;
      if (!ghost) this.world.bloomActive = true;
    } else if (kind === "drill" || kind === "drill2") {
      // real dropper model (single or dual): base tile + gold body + overhang arm + glass drop-point(s); the
      // overhang shows the drop direction (hangs over the fed tile), so no separate arrow.
      this.addModel(g, kind === "drill2" ? DROPPER_DOUBLE : DROPPER_SINGLE, ghost);
    } else if (kind === "ramp") {
      this.addModel(g, RAMP, ghost); // recovered inclined belt: 3-tile run, 1-floor (100) rise
    } else if (kind === "enricher") {
      this.addModel(g, ENRICHER, ghost);       // real model replaces the procedural "+" placeholder
      g.add(this.beltArrow(ghost));
      // Belt vs support by SHAPE, not authored color (see conveyorSupportColor): Union1 is 100x20x100 = belt,
      // Union2 is 100x15x102 = support. Both are authored the same gray here, so only the belt changes.
      const ed = g.getObjectByName("Union1") as THREE.Mesh | undefined;
      if (ed) (ed.material as THREE.MeshStandardMaterial).color.setHex(this.conveyorDeckColor());
    } else if (kind === "fabricator") {
      const base = new THREE.Mesh(new THREE.BoxGeometry(F, BH, F), mat(body, def.accent, 0.04));
      base.position.y = BH / 2; g.add(base);
      const hop = new THREE.Mesh(new THREE.CylinderGeometry(F * 0.28, F * 0.14, 30, 6), mat(def.accent, def.accent, 0.5));
      hop.position.y = TOP - 15; g.add(hop);
      g.add(this.arrow(def.accent, ghost));
    } else if (kind === "assembler") {
      const base = new THREE.Mesh(new THREE.BoxGeometry(F, BH, F), mat(body));
      base.position.y = BH / 2; g.add(base);
      const gantry = new THREE.Mesh(new THREE.BoxGeometry(F, 10, F * 0.24), mat(def.accent, def.accent, 0.45));
      gantry.position.y = TOP - 5; g.add(gantry);
      const arm = new THREE.Mesh(new THREE.CylinderGeometry(6, 6, 26, 8), mat(def.accent, def.accent, 0.5));
      arm.position.y = TOP - 13; g.add(arm);
      g.add(this.arrow(def.accent, ghost));
    } else if (kind === "deployer") {
      const base = new THREE.Mesh(new THREE.BoxGeometry(F, BH, F), mat(body, def.accent, 0.04));
      base.position.y = BH / 2; g.add(base);
      const bay = new THREE.Mesh(new THREE.BoxGeometry(F * 0.5, 18, F * 0.5), mat(def.accent, def.accent, 0.7));
      bay.position.y = TOP - 9; g.add(bay);
      const mouth = new THREE.Mesh(new THREE.BoxGeometry(F * 0.44, 26, 18), mat(0x160a12));
      mouth.position.set(0, 20, -F * 0.5); g.add(mouth); // part enters this face (-Z)
    } else if (kind === "generator") {
      const base = new THREE.Mesh(new THREE.BoxGeometry(F, BH, F), mat(body));
      base.position.y = BH / 2; g.add(base);
      const panel = new THREE.Mesh(new THREE.BoxGeometry(F, 6, F * 0.66), mat(def.accent, def.accent, 0.55));
      panel.position.y = TOP - 16; panel.rotation.x = -0.5; g.add(panel);
    } else if (kind === "cooler") {
      const base = new THREE.Mesh(new THREE.BoxGeometry(F, BH, F), mat(body));
      base.position.y = BH / 2; g.add(base);
      const fin = new THREE.Mesh(new THREE.CylinderGeometry(F * 0.34, F * 0.34, 18, 16), mat(def.accent, def.accent, 0.6));
      fin.position.y = TOP - 16; g.add(fin);
      const stack = new THREE.Mesh(new THREE.CylinderGeometry(9, 9, 16, 8), mat(def.accent, def.accent, 0.4));
      stack.position.y = TOP - 8; g.add(stack);
    } else if (kind === "cannon") {
      // Group path (ghost preview / pool overflow). The turret parts are added as ordinary children here and
      // simply sit at rest — only the INSTANCED path aims them, which is the path a real placed cannon takes.
      this.addModel(g, ORECANNON, ghost);
    } else if (kind === "obliterator") {
      this.addModel(g, OBLITERATOR, ghost);   // dark body (Union1) + the floating red spike ring (Union2)
      const spikes = g.getObjectByName("Union2") as THREE.Mesh | undefined;
      if (spikes) {
        const mt = spikes.material as THREE.MeshStandardMaterial;
        // idle red star. Placed obliterators are POOLED and get hue+brightness per instance from
        // animateObliterators; this path is the build ghost, the inventory preview and the detail pane.
        mt.emissive.copy(mt.color);
        mt.emissiveIntensity = ghost ? 0.3 : (FACTORY.obliteratorIdleLo + FACTORY.obliteratorIdleHi) / 2;
        if (!ghost) spikes.layers.enable(BLOOM_LAYER);
      }
      if (!ghost) this.world.bloomActive = true;
    } else if (kind === "receptacle") { // the ore sink
      this.addModel(g, RECEPTACLE, ghost);
      const rd = g.getObjectByName("Union3") as THREE.Mesh | undefined;   // 100x20x100 = the belt ore ride in on
      if (rd) (rd.material as THREE.MeshStandardMaterial).color.setHex(this.conveyorDeckColor());
      const rs = g.getObjectByName("Union2") as THREE.Mesh | undefined;   // 102x15x95 = support, authored CYAN
      if (rs) (rs.material as THREE.MeshStandardMaterial).color.setHex(this.conveyorSupportColor());
      const lamp = g.getObjectByName("Part2") as THREE.Mesh | undefined;  // red indicator strip
      if (lamp) { const mt = lamp.material as THREE.MeshStandardMaterial;
        mt.emissive.copy(mt.color); mt.emissiveIntensity = ghost ? 0.3 : 0.6; if (!ghost) lamp.layers.enable(BLOOM_LAYER); }
      if (!ghost) this.world.bloomActive = true;
    } else {
      // NO BRANCH FOR THIS KIND. This used to be the receptacle's arm, so any kind added without a makeMesh
      // case silently rendered AS A RECEPTACLE — which is exactly what happened to the Obliterator: it was
      // pooled correctly when placed, but every preview, ghost and detail pane showed a receptacle instead.
      // Fail LOUD instead: a magenta box is unmistakable, and it can never be mistaken for a real building.
      const miss = new THREE.Mesh(new THREE.BoxGeometry(F * 0.8, BH, F * 0.8), mat(0xff00ff, 0xff00ff, 0.8));
      miss.position.y = BH / 2; g.add(miss);
      if (!ghost) console.warn(`[factory] makeMesh: no branch for kind "${kind}" — showing the MISSING-MODEL box`);
    }
    g.rotation.y = DIR_YAW[dir];
    return g;
  }

  private beltArrow(ghost: boolean): THREE.Mesh {
    // a flat, low-poly 2D arrow painted on the belt deck, subtly tinted near the belt's own color so it reads
    // as a flow marker, not a floating chevron. Points local +Z (flow); DIR_YAW then aims it along travel.
    const s = new THREE.Shape();
    s.moveTo(-7, -24); s.lineTo(7, -24); s.lineTo(7, 4); s.lineTo(17, 4);
    s.lineTo(0, 24); s.lineTo(-17, 4); s.lineTo(-7, 4); s.closePath();
    const geo = new THREE.ShapeGeometry(s);
    geo.rotateX(-Math.PI / 2); // lay flat on XZ (normal +Y, up)
    geo.rotateY(Math.PI);      // ...tip toward +Z (flow) — rotateX alone points it −Z
    geo.translate(0, 20.6, 0); // just above the 20-stud deck
    const mat = new THREE.MeshStandardMaterial({ color: this.tint(0x2b2b2b), roughness: 0.9, transparent: ghost, opacity: ghost ? 0.55 : 1 });
    return new THREE.Mesh(geo, mat);
  }

  /** The conveyor flow arrow as a shared INSTANCED extra part (baked geometry + one material), built once and cloned
   *  into the conveyor pool. Points local +Z; the per-instance tile transform (DIR_YAW) aims it along travel. */
  private arrowParts(): ExtraPart[] {
    if (!this.arrowExtra) {
      const m = this.beltArrow(false);
      this.arrowExtra = [{ name: "arrow", geometry: m.geometry, material: m.material as THREE.Material }];
    }
    return this.arrowExtra;
  }

  /** The leech's flowing-neon material (shared by its 4 columns): a yellow glow that SCROLLS UP the column's local Y
   *  (via `uFlow`) and fades in/out with ore presence (via `uActive`). Injected onto a MeshStandardMaterial so it
   *  still routes through the selective-bloom pass. Per-leech instance (each gets its own uniforms). */
  private leechNeonMaterial(ghost: boolean, color: number): THREE.MeshStandardMaterial {
    const m = new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 1, roughness: 0.6, transparent: ghost, opacity: ghost ? 0.5 : 1 }); // color READ from the model (Part1Mtl Kd); lit when idle, glows when powered
    const uFlow = { value: 0 }, uActive = { value: ghost ? 0.6 : 0 };
    m.userData.uFlow = uFlow; m.userData.uActive = uActive;
    m.onBeforeCompile = (sh) => {
      sh.uniforms.uFlow = uFlow; sh.uniforms.uActive = uActive;
      sh.vertexShader = "varying float vLeechY;\n" + sh.vertexShader.replace("void main() {", "void main() {\n\tvLeechY = position.y;");
      sh.fragmentShader = "uniform float uFlow;\nuniform float uActive;\nvarying float vLeechY;\n" +
        sh.fragmentShader.replace("#include <emissivemap_fragment>",
          "#include <emissivemap_fragment>\n\tfloat leechBand = 0.5 + 0.5*sin(vLeechY*0.09 - uFlow);\n\ttotalEmissiveRadiance *= uActive * (0.25 + 2.5*pow(leechBand, 3.0));");
    };
    m.customProgramCacheKey = () => "leechNeon";
    return m;
  }

  /** The Waverider's blue neon strip: a bright emission WAVE scrolling along the strip's length (`uFlow`) that swells
   *  while ore is on the belt (`uActive`), over a dim idle base. Injected onto a MeshStandardMaterial for the bloom pass. */
  private waveNeonMaterial(ghost: boolean, color: number): THREE.MeshStandardMaterial {
    const m = new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 1, roughness: 0.5, transparent: ghost, opacity: ghost ? 0.5 : 1 });
    const uFlow = { value: 0 }, uActive = { value: ghost ? 0.6 : 0 };
    m.userData.uFlow = uFlow; m.userData.uActive = uActive;
    m.onBeforeCompile = (sh) => {
      sh.uniforms.uFlow = uFlow; sh.uniforms.uActive = uActive;
      sh.vertexShader = "varying float vWaveP;\n" + sh.vertexShader.replace("void main() {", "void main() {\n\tvWaveP = position.z;");
      sh.fragmentShader = "uniform float uFlow;\nuniform float uActive;\nvarying float vWaveP;\n" +
        sh.fragmentShader.replace("#include <emissivemap_fragment>",
          "#include <emissivemap_fragment>\n\tfloat waveB = 0.5 + 0.5*sin(vWaveP*0.10 - uFlow);\n\ttotalEmissiveRadiance *= (0.18 + uActive*(0.4 + 2.0*pow(waveB, 2.5)));");
    };
    m.customProgramCacheKey = () => "waveNeon";
    return m;
  }

  private arrow(color: number, ghost: boolean): THREE.Group {
    // a BIG, bright chevron riding on top, unmistakably pointing along the flow (local +Z), so each 90°
    // rotation is obvious (a square belt alone reads the same at every quarter-turn).
    const g = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.9, roughness: 0.4, transparent: ghost, opacity: ghost ? 0.85 : 1 });
    const head = new THREE.Mesh(new THREE.ConeGeometry(26, 40, 4), mat);
    head.rotation.x = Math.PI / 2; head.position.set(0, 30, FACTORY.tile * 0.24);
    const shaft = new THREE.Mesh(new THREE.BoxGeometry(16, 10, FACTORY.tile * 0.4), mat);
    shaft.position.set(0, 30, -FACTORY.tile * 0.06);
    g.add(head, shaft);
    return g;
  }

  private tileMarker(color: number): THREE.Mesh {
    const m = new THREE.Mesh(
      new THREE.BoxGeometry(FACTORY.tile * 0.9, 4, FACTORY.tile * 0.9),
      new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.5, transparent: true, opacity: 0.5 }),
    );
    m.position.y = 4;
    return m;
  }

  private clear(): void {
    for (const b of this.buildings.values()) if (b.mesh) { this.root.remove(b.mesh); disposeObj(b.mesh); } // Group-path only
    this.buildings.clear();
    this.walls.clear();  // walls + mods (cryo) are instanced → wiped by instancer.clear()
    this.mods.clear();
    this.instancer.clear();
    this.rebuildWallBoxes();
    this.rampCells.clear();
    this.reserved.clear();
    for (const o of this.items) o.active = false;
    if (this.jolt) { this.jolt.teardown(); this.jolt = null; this.joltBooting = false; } // re-site → drop the world (extent/belts change); it re-boots next flagged frame
    for (const l of this.launches) { l.active = false; l.mesh.visible = false; }
    for (const d of this.discs) d.visible = false;
    for (const w of this.wires) w.visible = false;
    this.aiQueue = [];
    this.partsWasted = 0; this.sinceWaste = 99; this.wasteWarnCd = 0;
    this.activeFloor = 0;
    this.climateDirty = true; this.tpExits = null; this.recList = null; this.hud?.setHasObliterator(this.hasObliterator());
    if (this.buildMode) this.setBuildMode(false);
  }
}

function disposeObj(o: THREE.Object3D): void {
  o.traverse((c) => {
    const m = c as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
    const mat = m.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
    else mat?.dispose();
  });
}
function disposeChildren(g: THREE.Group): void {
  for (const c of [...g.children]) { g.remove(c); disposeObj(c); }
}
